import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
  NO_CURSORS,
  advanceCursors,
  cursorForRun,
  type MigrationCursors,
} from './migrationCursors';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import { CollapsibleSection } from './CollapsibleSection';
import { RepairCalibreImageNamesSection } from './RepairCalibreImageNamesSection';
import { StripReferenceNameMarkupSection } from './StripReferenceNameMarkupSection';
import {
  buildMoveSelectorPayload,
  EMPTY_SELECTOR_FORM,
  FAMILY_FILTER_FIELDS,
  MAX_REFERENCE_IDS,
  moveFingerprint,
  newConfirmationToken,
  parseReferenceIds,
  type SelectorForm,
  type SelectorMode,
} from './moveRefsSelector';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlagsContext } from '../../contexts/FeatureFlagsContext';
import { setFeatureFlagOverride } from '../../services/featureFlags';
import {
  backfillDialColorFromModel,
  backfillReferenceNormalized,
  backfillSubscriptionHistory,
  purgeExtractionCache,
  getAnthropicCreditBalance,
  backfillSwatchMissingReference,
  reclassifyCoDevelopedCalibres,
  deriveManufacturerBrands,
  backfillTierTypesGeneral,
  normalizeCalibreSpelling,
  migrateDialIndexesNumerals,
  type MigrateDialIndexesNumeralsResult,
  migrateCrownTypeCrystal,
  type MigrateCrownTypeCrystalResult,
  migrateCalibreBalanceType,
  type MigrateCalibreBalanceTypeResult,
  backfillCalibreLinks,
  type BackfillCalibreLinksResult,
  pruneOrphanCollectionWatchIds,
  recomputeAllCalibreReferenceCounts,
  recomputeAllWatchBrandReferenceCounts,
  recomputeAllAcquiredByBrandIds,
  recomputeAllManufacturerAncestors,
  moveWatchReferencesBetweenBrands,
  backfillParentOrgIds,
  cleanupAnalogModuleLinks,
  migrateDialFinishToFeatures,
  type MigrateDialFinishResult,
  type CleanupAnalogModuleLinksResult,
  backfillDescriptionSource,
  type BackfillDescriptionSourceResult,
  type AnalogModuleCleanupModuleRow,
  backfillShopifyParentRefsAndVariants,
  backfillIsVariant,
  pushAlgoliaCalibreIndexSettings,
  pushAlgoliaCalibreFamilyIndexSettings,
  pushAlgoliaElectronicModuleIndexSettings,
  backfillElectronicModulesToAlgolia,
  pushAlgoliaWinderIndexSettings,
  backfillWindersToAlgolia,
  recomputeAllElectronicModuleReferenceCounts,
  callBackfillCalibresToAlgolia,
  callBackfillCalibreFamiliesToAlgolia,
  callBackfillManufacturersToAlgolia,
  callBackfillParentOrgsToAlgolia,
  callPushAlgoliaManufacturerIndexSettings,
  callBackfillWatchReferenceFamilyIds,
  callBackfillReferenceImagesFromUserStorage,
  getWatchBrands,
  migrateCustomCalibreSources,
  bulkSetShowProductLinks,
  reseedAcquisitionLookups,
  reseedMovementDecorationLookup,
  reseedCalibreMaterialsLookups,
  reseedDialFeaturesLookup,
  CALIBRE_MATERIALS_LOOKUP_COLLECTIONS,
  reseedWindingArchitectureLookup,
  reseedStorageAndWinderLookups,
  STORAGE_WINDER_LOOKUP_COLLECTIONS,
  seedElectronicModuleLookups,
  seedPocketWatchLookups,
  updateWatchBrand,
  uploadIntroCtaBackground,
  type MigrateCustomSourceResult,
  type BulkSetShowProductLinksResult,
  type BackfillReferenceNormalizedResult,
  type BackfillReferenceNormalizedError,
  type BackfillSubscriptionHistoryResult,
  type PurgeExtractionCacheResult,
  type AnthropicCreditBalanceResult,
  setAnthropicMonthlyBudget,
  subscribeToAnthropicCreditState,
  type AnthropicCreditState,
  type BackfillSwatchMissingReferenceResult,
  type BackfillSwatchMissingReferenceError,
  type BackfillSwatchMissingReferenceSample,
  type ReclassifyCoDevelopedCalibresResult,
  type ReclassifyCoDevelopedCalibresError,
  type ReclassifyCoDevelopedCalibresSample,
  type DeriveManufacturerBrandsResult,
  type DeriveManufacturerBrandsSample,
  type BackfillTierTypesResult,
  type NormalizeCalibreSpellingResult,
  type PruneOrphanCollectionWatchIdsResult,
  type PruneOrphanCollectionWatchIdsError,
  type RecomputeCalibreReferenceCountsResult,
  type RecomputeCalibreReferenceCountsError,
  type RecomputeWatchBrandReferenceCountsResult,
  type RecomputeWatchBrandReferenceCountsError,
  type RecomputeAcquiredByBrandIdsResult,
  type RecomputeAcquiredByBrandIdsError,
  type RecomputeManufacturerAncestorsResult,
  type MoveWatchReferencesBetweenBrandsResult,
  type BackfillParentOrgIdsResult,
  type ParentOrgBackfillRow,
  type BackfillShopifyParentRefsResult,
  type BackfillIsVariantParams,
  type PushAlgoliaCalibreIndexSettingsResult,
  type PushAlgoliaCalibreFamilyIndexSettingsResult,
  type PushAlgoliaElectronicModuleIndexSettingsResult,
  type BackfillElectronicModulesToAlgoliaResult,
  type PushAlgoliaWinderIndexSettingsResult,
  type BackfillWindersToAlgoliaResult,
  type RecomputeElectronicModuleReferenceCountsResult,
  type RecomputeElectronicModuleReferenceCountsError,
  type EntityIndexBackfillResult,
  type EntityIndexSettingsResult,
  type BackfillReferenceImagesFromUserStorageResult,
  type SeedElectronicModuleLookupsResult,
  type SeedPocketWatchLookupsResult,
} from '../../services/firebase';
import type {
  WatchBrand,
  BrandStatus,
  BrandAcquisition,
  AcquisitionType,
  AcquisitionDirectness,
} from '../../types';
import type {
  BackfillDialColorResult,
  BackfillDialColorSample,
  BackfillDialColorAmbiguous,
  BackfillDialColorNoMatch,
  BackfillDialColorError,
} from '../../types/dialColorBackfill';
import {
  DEFAULT_AI_IMAGE_RECOGNITION_CONFIG,
  INTRO_CTA_TEXT_MODES,
  setIntroCtaBackgroundOpacity,
  setIntroCtaBackgroundUrl,
  setIntroCtaTextMode,
  subscribeToAiImageRecognitionConfig,
  type IntroCtaTextMode,
} from '../../services/aiImageRecognitionConfig';
import {
  AI_MAX_RESULTS_DEFAULT,
  AI_MAX_RESULTS_MAX,
  AI_MAX_RESULTS_MIN,
  setAiMaxResults,
  subscribeToAiSearchConfig,
} from '../../services/aiSearchConfig';
import { FEATURE_FLAGS } from '../../services/featureFlagsRegistry';
import {
  DEFAULT_DERIVATION_SETTINGS,
  setDerivationSetting,
  subscribeToDerivationSettings,
} from '../../services/derivationSettings';
import type { DerivationSettings } from '@watchkeeper/shared-types';
import {
  DEFAULT_IMPORT_STOPWORDS,
  normalizeStopwordList,
  stripImportStopwords,
} from '../../services/importStopwords';
import {
  importStopwordsDocExists,
  saveImportStopwords,
  subscribeToImportStopwords,
} from '../../services/importStopwordsConfig';
import type {
  FeatureFlagCategory,
  FeatureFlagDefinition,
  FeatureFlagOverride,
} from '../../types/featureFlags';
import { SchemaConfigPanel } from './SchemaConfigPanel';
import { CONFIG_SCHEMAS, GROUP_ORDER, type ConfigSchema } from './configSchema';

const CATEGORY_ORDER: { id: FeatureFlagCategory; label: string }[] = [
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'destructive', label: 'Destructive' },
  { id: 'experimental', label: 'Experimental' },
  { id: 'other', label: 'Other' },
];

/**
 * Live-Settings group that toggles RN consumer-app features (master on/off
 * switches users feel directly). It lives on the RN App Switches tab; every
 * other Live-Settings group is an admin-tunable RTDB knob and lives on the RTDB
 * Configs tab. Both are RTDB-backed — the split is by audience, not storage.
 */
const RN_SWITCH_GROUP = 'Feature Toggles';

/**
 * Visually-separated footer block for one-off / destructive admin callables
 * (backfills, recomputes, full-rebuild pushes). Sits at the bottom of whichever
 * tab the tools logically belong to, beneath the day-to-day settings.
 */
function ToolsSubsection({ children }: { children: ReactNode }) {
  return (
    <div className="mt-10 pt-6 border-t-2 border-dashed border-gray-200">
      <div className="mb-5">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-700">
          Maintenance &amp; Tools
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          One-off admin operations. Run deliberately — most write to the
          catalogue. Prefer a dry-run first where offered.
        </p>
      </div>
      <div className="space-y-8">{children}</div>
    </div>
  );
}

// `CollapsibleSection` (and its localStorage-backed collapse state) lives in
// its own module so sections large enough to warrant a separate file can use it
// without importing back into this one.

function formatChangedAt(override: FeatureFlagOverride | undefined): string | null {
  if (!override?.lastChangedAt) return null;
  try {
    const date = override.lastChangedAt.toDate();
    return date.toLocaleString();
  } catch {
    return null;
  }
}

interface ToggleRowProps {
  flag: FeatureFlagDefinition;
  override: FeatureFlagOverride | undefined;
  effective: boolean;
  pending: boolean;
  onToggle: (next: boolean) => void;
}

function ToggleRow({ flag, override, effective, pending, onToggle }: ToggleRowProps) {
  const hasOverride = !!override && typeof override.enabled === 'boolean';
  const changedAt = formatChangedAt(override);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-gray-900">{flag.label}</h4>
            <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {flag.id}
            </code>
            {!hasOverride && (
              <Badge variant="default">
                default ({flag.defaultValue ? 'on' : 'off'})
              </Badge>
            )}
            {flag.category === 'destructive' && (
              <Badge variant="danger">destructive</Badge>
            )}
          </div>
          {flag.description && (
            <p className="text-sm text-gray-600 mt-1">{flag.description}</p>
          )}
          {hasOverride && changedAt && (
            <p className="text-xs text-gray-500 mt-2">
              Last changed {changedAt}
              {override?.lastChangedBy ? ` by ${override.lastChangedBy}` : ''}
            </p>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1">
          <button
            type="button"
            role="switch"
            aria-checked={effective}
            disabled={pending}
            onClick={() => onToggle(!effective)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              effective ? 'bg-brand-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                effective ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-xs text-gray-500">
            {effective ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * One-shot maintenance tool: backfill `referenceNormalized` on legacy watch
 * references that were created before the field was persisted (see
 * createWatchReference). Calls the `backfillReferenceNormalized` server callable.
 *
 * Always offers a dry-run (scan & count, no writes) first; the real write run is
 * gated behind a confirm modal. An optional brand filter scopes the run to a
 * single brand (default: all brands) so it can be validated against one brand
 * — e.g. IWC — before running everywhere.
 */
function BackfillReferenceNormalizedSection() {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [brandId, setBrandId] = useState<string>(''); // '' = all brands
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] = useState<
    (BackfillReferenceNormalizedResult & { dryRun: boolean }) | null
  >(null);

  useEffect(() => {
    let active = true;
    getWatchBrands()
      .then((all) => {
        if (!active) return;
        all.sort((a, b) =>
          (a.displayName || a.name).localeCompare(b.displayName || b.name),
        );
        setBrands(all);
      })
      .catch((err) => {
        console.error('Failed to load brands for backfill tool:', err);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedBrandName = brandId
    ? brands.find((b) => b.id === brandId)?.displayName ||
      brands.find((b) => b.id === brandId)?.name ||
      brandId
    : 'all brands';

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await backfillReferenceNormalized({
        dryRun,
        brandId: brandId || undefined,
      });
      setResult({ ...r, dryRun });
      setErrorsExpanded(false);
      const summary =
        `${dryRun ? 'Dry run' : 'Backfill'} complete (${selectedBrandName}): ` +
        `${r.scanned} scanned, ${r.missingField} missing, ` +
        `${dryRun ? 'would update' : 'updated'} ${dryRun ? r.missingField : r.updated}` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillReferenceNormalized failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="backfillReferenceNormalized"
      title="Backfill referenceNormalized"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Scans watch references for docs missing{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            referenceNormalized
          </code>{' '}
          and backfills it (uppercase, alphanumerics only). Run a dry run first to
          see counts without writing, then run for real. Scope to a single brand
          to validate before running across everything.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Brand</label>
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            disabled={running !== null}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.displayName || b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              <span className="text-xs text-gray-500">{selectedBrandName}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat label="Missing field" value={result.missingField} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Skipped" value={result.skipped} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map((e: BackfillReferenceNormalizedError, i) => (
                              <tr key={`${e.brandId}/${e.referenceId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {e.brandId}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {e.referenceId}
                                </td>
                                <td className="px-3 py-1.5 text-red-600 align-top">
                                  {e.error}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Run backfill (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will write{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              referenceNormalized
            </code>{' '}
            to every reference that&apos;s missing it for{' '}
            <span className="font-semibold">{selectedBrandName}</span>. The
            operation is idempotent, but it does modify documents. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * One-off, super-admin-gated migration tool: seed `hasEverBeenPaid` on existing
 * user docs from RevenueCat subscription history (watchlock PR #783). Required
 * once after the Curator trial deploys so the trial-eligibility gate doesn't
 * offer a trial to users who already pay (or have paid).
 *
 * Always offers a dry-run preview first (scan + count `wouldUpdate`, no writes);
 * the real write run is gated behind a confirm modal. The callable is idempotent
 * — it only ever sets the flag TRUE, never false, and skips docs where it's
 * already set — so it's safe to re-run.
 */
function BackfillSubscriptionHistorySection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [result, setResult] = useState<BackfillSubscriptionHistoryResult | null>(
    null,
  );

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await backfillSubscriptionHistory({ dryRun });
      setResult(r);
      const summary = r.dryRun
        ? `Preview: ${r.scanned} scanned, would update ${r.wouldUpdate}, ` +
          `${r.skippedAlreadySet} already set (nothing changed)`
        : `Applied: ${r.scanned} scanned, updated ${r.updated}, ` +
          `${r.skippedAlreadySet} already set`;
      toast.success(summary);
    } catch (err) {
      console.error('backfillSubscriptionHistory failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="backfillSubscriptionHistory"
      title="Backfill Subscription History"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          One-off — populates{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            hasEverBeenPaid
          </code>{' '}
          on all user docs from RevenueCat history. Required after the Curator
          trial deploy to correctly gate trial eligibility. Safe to re-run
          (idempotent — only ever sets the flag TRUE, never false). Run a dry-run
          preview first to see counts without writing, then apply.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run (preview)
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun
                  ? 'Preview only — nothing changed'
                  : `Applied — ${result.updated} user${
                      result.updated === 1 ? '' : 's'
                    } updated`}
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Would update' : 'Updated'}
                value={result.dryRun ? result.wouldUpdate : result.updated}
              />
              <ResultStat
                label="Already set"
                value={result.skippedAlreadySet}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Run backfill (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will set{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              hasEverBeenPaid
            </code>{' '}
            to <span className="font-semibold">true</span> on every user doc that
            shows paid history and doesn&apos;t already have it. The operation is
            idempotent and only ever sets the flag TRUE (never false), but it does
            modify documents. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Configuration → Data Maintenance: purge the extraction cache.
 *
 * `urlToCacheKey` used to truncate its base64 to 128 chars, covering only the
 * first 96 BYTES of `${method}::${url}`. Two long URLs sharing that prefix
 * collided on one doc and served each other's product — hit on Omega product
 * URLs, where the discriminating SKU sits at the very end. The key is now a full
 * sha256, so every doc written under the old scheme is unreachable AND possibly
 * wrong; this clears them instead of waiting out the 30-day TTL.
 *
 * Dry run first (counts + sample URLs, no deletes); the real run is gated behind
 * a confirm modal. Large collections drain over several calls, so the write path
 * loops while the callable reports `hasMore`.
 */
function PurgeExtractionCacheSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [samplesExpanded, setSamplesExpanded] = useState(false);
  const [result, setResult] = useState<PurgeExtractionCacheResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      if (dryRun) {
        const r = await purgeExtractionCache({ dryRun: true });
        setResult(r);
        toast.success(
          `Preview: ${r.scanned} cached extraction${r.scanned === 1 ? '' : 's'} ` +
            `would be deleted (nothing changed)`,
        );
        return;
      }

      // Real run: keep going while the callable reports more to do, so one
      // click drains the collection rather than stopping at the time budget.
      let scanned = 0;
      let deleted = 0;
      let samples: string[] = [];
      let passes = 0;
      for (;;) {
        const r = await purgeExtractionCache({ dryRun: false });
        scanned += r.scanned;
        deleted += r.deleted;
        if (samples.length === 0) samples = r.samples;
        passes += 1;
        setResult({ dryRun: false, scanned, deleted, hasMore: r.hasMore, samples });
        if (!r.hasMore) break;
        // Belt-and-braces stop so a callable that always reports hasMore can't
        // spin forever.
        if (passes >= 20) {
          toast(
            `Stopped after ${passes} passes with more remaining — run again to continue.`,
          );
          break;
        }
      }
      toast.success(
        `Purged ${deleted} cached extraction${deleted === 1 ? '' : 's'}` +
          (passes > 1 ? ` over ${passes} passes` : ''),
      );
    } catch (err) {
      console.error('purgeExtractionCache failed:', err);
      toast.error(
        err instanceof Error
          ? `Purge failed: ${err.message}`
          : 'Purge failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="purgeExtractionCache"
      title="Purge Extraction Cache"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Deletes every doc in{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            extraction_cache
          </code>
          . Run this once after deploying the cache-key fix: the old key
          truncated long URLs, so two different watches could share one cache doc
          and return each other&apos;s data. Those docs are now unreachable but
          would otherwise sit there for 30 days. Purging only costs re-extraction
          on the next import — no catalogue data is touched. Dry run first to see
          the count.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run (preview)
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Purge cache
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun
                  ? 'Preview only — nothing deleted'
                  : `Purged — ${result.deleted} doc${
                      result.deleted === 1 ? '' : 's'
                    } deleted`}
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'deleted'}
              </Badge>
              {result.hasMore && <Badge variant="warning">more remaining</Badge>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Would delete' : 'Deleted'}
                value={result.dryRun ? result.scanned : result.deleted}
              />
            </div>
            {result.samples.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => setSamplesExpanded((v) => !v)}
                >
                  {samplesExpanded ? 'Hide' : 'Show'} {result.samples.length}{' '}
                  sample URL{result.samples.length === 1 ? '' : 's'}
                </button>
                {samplesExpanded && (
                  <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {result.samples.map((u) => (
                      <li
                        key={u}
                        className="text-xs text-gray-600 font-mono truncate"
                        title={u}
                      >
                        {u}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Purge the extraction cache?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This deletes every document in{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              extraction_cache
            </code>
            . No catalogue or reference data is affected — the only cost is that
            the next import of each URL re-extracts instead of reading the cache
            (which means fresh AI calls). Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Purge cache
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * One-shot maintenance tool: backfill the `reference` field on Swatch docs that
 * are missing it. Calls the `backfillSwatchMissingReference` server callable,
 * which is hardcoded to the Swatch brand (no brand filter). The callable prefers
 * each doc's `model` field, falling back to a humanized form of the doc-ID slug.
 *
 * Always offers a dry-run first; the dry run returns a per-source breakdown plus
 * up to 50 sample rows showing exactly what `reference` value would be written
 * per doc — James inspects these before flipping to the real write run, which is
 * gated behind a confirm modal.
 */
function BackfillSwatchMissingReferenceSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [samplesExpanded, setSamplesExpanded] = useState(false);
  const [result, setResult] =
    useState<BackfillSwatchMissingReferenceResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await backfillSwatchMissingReference({ dryRun });
      setResult(r);
      setErrorsExpanded(false);
      setSamplesExpanded(dryRun);
      const summary =
        `${dryRun ? 'Dry run' : 'Backfill'} complete (Swatch): ` +
        `${r.scanned} scanned, ${r.missingReference} missing, ` +
        `${dryRun ? 'would update' : 'updated'} ${dryRun ? r.missingReference : r.updated}` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillSwatchMissingReference failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="backfillSwatchMissingReference"
      title="Backfill Swatch missing reference"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Scans{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchBrands/swatch/references
          </code>{' '}
          for docs missing a{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            reference
          </code>{' '}
          field and backfills it — preferring the doc&apos;s{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">model</code>{' '}
          field, falling back to a humanized form of the doc-ID slug. Run a dry
          run first to inspect the per-source breakdown and sample values, then
          run for real. Swatch only.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              <span className="text-xs text-gray-500">Swatch</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat
                label="Missing reference"
                value={result.missingReference}
              />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Skipped" value={result.skipped} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {/* Sources mini-panel: where each backfilled value came from. */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <ResultStat label="From model field" value={result.bySource.model} />
              <ResultStat
                label="From doc-ID slug"
                value={result.bySource.docIdSlug}
              />
            </div>

            {result.samples.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setSamplesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${samplesExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.samples.length} sample
                  {result.samples.length === 1 ? '' : 's'} — click to{' '}
                  {samplesExpanded ? 'collapse' : 'expand'}
                </button>

                {samplesExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Extracted value
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Source
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.samples.map(
                            (s: BackfillSwatchMissingReferenceSample, i) => (
                              <tr key={`${s.referenceId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {s.referenceId}
                                </td>
                                <td className="px-3 py-1.5 text-gray-900 align-top">
                                  {s.extractedValue}
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                  <Badge
                                    variant={
                                      s.source === 'model' ? 'info' : 'default'
                                    }
                                  >
                                    {s.source}
                                  </Badge>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map(
                              (e: BackfillSwatchMissingReferenceError, i) => (
                                <tr key={`${e.referenceId}/${i}`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {e.referenceId}
                                  </td>
                                  <td className="px-3 py-1.5 text-red-600 align-top">
                                    {e.error}
                                  </td>
                                </tr>
                              ),
                            )}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Run Swatch backfill (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will write a{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              reference
            </code>{' '}
            field to every <span className="font-semibold">Swatch</span> doc
            that&apos;s missing it. The operation is idempotent, but it does
            modify documents. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * One-shot maintenance tool: backfill `dialAndHands.color` on references where
 * it's empty, deriving the value from color tokens in the `model`/`productName`
 * strings (resolved against `lookup_dial_colors` — value/displayName/synonym).
 * Calls the `backfillDialColorFromModel` server callable.
 *
 * Single-token matches are written; refs with multiple candidate tokens are
 * surfaced in the Ambiguous panel ("needs manual attention") and refs with no
 * token at all in the No-match panel. Always offers a dry run first; the real
 * write run is gated behind a confirm modal. An optional brand filter scopes the
 * run to a single brand (default: all brands) for validation before running
 * everywhere.
 */
function BackfillDialColorFromModelSection() {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [brandId, setBrandId] = useState<string>(''); // '' = all brands
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [samplesExpanded, setSamplesExpanded] = useState(false);
  const [ambiguousExpanded, setAmbiguousExpanded] = useState(false);
  const [noMatchExpanded, setNoMatchExpanded] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] = useState<BackfillDialColorResult | null>(null);

  useEffect(() => {
    let active = true;
    getWatchBrands()
      .then((all) => {
        if (!active) return;
        all.sort((a, b) =>
          (a.displayName || a.name).localeCompare(b.displayName || b.name),
        );
        setBrands(all);
      })
      .catch((err) => {
        console.error('Failed to load brands for backfill tool:', err);
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedBrandName = brandId
    ? brands.find((b) => b.id === brandId)?.displayName ||
      brands.find((b) => b.id === brandId)?.name ||
      brandId
    : 'all brands';

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await backfillDialColorFromModel({
        dryRun,
        brandId: brandId || undefined,
      });
      setResult(r);
      setSamplesExpanded(dryRun);
      setAmbiguousExpanded(false);
      setNoMatchExpanded(false);
      setErrorsExpanded(false);
      const matched = r.singleValueMatched + r.multiValueMatched;
      const summary =
        `${dryRun ? 'Dry run' : 'Backfill'} complete (${selectedBrandName}): ` +
        `${r.scanned} scanned, ${r.candidatesWithEmptyColor} empty-color, ` +
        `${matched} matched (${r.singleValueMatched} single, ${r.multiValueMatched} multi), ` +
        `${dryRun ? 'would update' : 'updated'} ${dryRun ? matched : r.updated}` +
        (r.ambiguous.length ? `, ${r.ambiguous.length} ambiguous` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length || r.ambiguous.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillDialColorFromModel failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="backfillDialColorFromModel"
      title="Backfill dialColor from model"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Scans watch references whose{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            dialAndHands.color
          </code>{' '}
          is empty and fills it from color tokens found in the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">model</code>
          /
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            productName
          </code>{' '}
          strings (matched against{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_dial_colors
          </code>{' '}
          by value, display name, or synonym). dialColor is multi-select: when a
          model names several colors they are ALL written (joined, e.g.{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            rose_gold, black
          </code>
          ). A ref only lands in the Ambiguous list when the model carries a
          color-shaped word that isn&apos;t in the lookup at all — a genuine
          unknown to review (usually a missing synonym). Run a dry run first to
          inspect, then run for real. Scope to a single brand to validate before
          running across everything.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Brand</label>
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            disabled={running !== null}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.displayName || b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              <span className="text-xs text-gray-500">{selectedBrandName}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat
                label="Candidates"
                value={result.candidatesWithEmptyColor}
              />
              <ResultStat
                label="Matched (1 color)"
                value={result.singleValueMatched}
              />
              <ResultStat
                label="Matched (multi)"
                value={result.multiValueMatched}
              />
              <ResultStat
                label="Ambiguous"
                value={result.ambiguous.length}
                danger={result.ambiguous.length > 0}
              />
              <ResultStat label="No match" value={result.noMatch.length} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {/* Samples — what was (or would be) written, with match source. */}
            {result.samples.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setSamplesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${samplesExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.samples.length} sample
                  {result.samples.length === 1 ? '' : 's'} — click to{' '}
                  {samplesExpanded ? 'collapse' : 'expand'}
                </button>

                {samplesExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Model
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Matched color(s)
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Source
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.samples
                            .slice(0, 50)
                            .map((s: BackfillDialColorSample, i) => (
                              <tr key={`${s.brandId}/${s.referenceId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {s.brandId}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {s.referenceId}
                                </td>
                                <td className="px-3 py-1.5 text-gray-600 align-top max-w-xs truncate">
                                  {s.model}
                                </td>
                                <td className="px-3 py-1.5 text-gray-900 align-top">
                                  {s.writtenValue}
                                  {s.multiValue && (
                                    <Badge variant="info" className="ml-1.5">
                                      multi
                                    </Badge>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                  <Badge
                                    variant={
                                      s.source === 'value'
                                        ? 'info'
                                        : s.source === 'displayName'
                                          ? 'success'
                                          : 'default'
                                    }
                                  >
                                    {s.source}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {result.samples.length > 50 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 50 of {result.samples.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Ambiguous — multiple candidate colors; needs manual attention. */}
            {result.ambiguous.length > 0 && (
              <div className="mt-3 border border-amber-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setAmbiguousExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${ambiguousExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.ambiguous.length} ambiguous — unrecognized color token
                  in model (review / add synonym) — click to{' '}
                  {ambiguousExpanded ? 'collapse' : 'expand'}
                </button>

                {ambiguousExpanded && (
                  <div className="border-t border-amber-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Model
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Unrecognized token(s)
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Resolved color(s)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.ambiguous
                            .slice(0, 100)
                            .map((a: BackfillDialColorAmbiguous, i) => (
                              <tr key={`${a.brandId}/${a.referenceId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {a.brandId}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {a.referenceId}
                                </td>
                                <td className="px-3 py-1.5 text-gray-600 align-top max-w-xs truncate">
                                  {a.model}
                                </td>
                                <td className="px-3 py-1.5 text-amber-700 font-medium align-top">
                                  {a.unrecognizedTokens.join(', ')}
                                </td>
                                <td className="px-3 py-1.5 text-gray-500 align-top">
                                  {a.resolvedColors.length
                                    ? a.resolvedColors.join(', ')
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {result.ambiguous.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.ambiguous.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* No-match — empty color but no color token found at all. */}
            {result.noMatch.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setNoMatchExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${noMatchExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.noMatch.length} no-match — click to{' '}
                  {noMatchExpanded ? 'collapse' : 'expand'}
                </button>

                {noMatchExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Model
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.noMatch
                            .slice(0, 50)
                            .map((n: BackfillDialColorNoMatch, i) => (
                              <tr key={`${n.brandId}/${n.referenceId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {n.brandId}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {n.referenceId}
                                </td>
                                <td className="px-3 py-1.5 text-gray-600 align-top max-w-md truncate">
                                  {n.model}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {result.noMatch.length > 50 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 50 of {result.noMatch.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Reference
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map((e: BackfillDialColorError, i) => (
                              <tr key={`${e.brandId}/${e.referenceId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {e.brandId}
                                </td>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {e.referenceId}
                                </td>
                                <td className="px-3 py-1.5 text-red-600 align-top">
                                  {e.error}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Run dialColor backfill (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will write{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              dialAndHands.color
            </code>{' '}
            to every reference with an empty color where a single color token was
            matched, for{' '}
            <span className="font-semibold">{selectedBrandName}</span>. Ambiguous
            refs (multiple candidate colors) are left untouched. The operation is
            idempotent, but it does modify documents. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance tool: prune orphaned watchIds from user collections — references
 * that point at watches that no longer exist. Calls the
 * `pruneOrphanCollectionWatchIds` server callable, which only mutates the
 * `watchIds` arrays (safe to run). An optional UID scopes the run to a single
 * user; leaving it empty kicks a full sweep across all users.
 */
function PruneOrphanCollectionWatchIdsSection() {
  const [userId, setUserId] = useState('');
  const [running, setRunning] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] =
    useState<PruneOrphanCollectionWatchIdsResult | null>(null);

  const scopeLabel = userId.trim() ? `user ${userId.trim()}` : 'all users';

  const run = async () => {
    setRunning(true);
    try {
      const r = await pruneOrphanCollectionWatchIds({ userId });
      setResult(r);
      setErrorsExpanded(false);
      const summary =
        `Prune complete (${scopeLabel}): ` +
        `${r.usersScanned} user(s) scanned, ${r.collectionsScanned} collection(s) scanned, ` +
        `${r.collectionsUpdated} updated, ${r.orphansRemoved} orphan(s) removed` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('pruneOrphanCollectionWatchIds failed:', err);
      toast.error(
        err instanceof Error
          ? `Prune failed: ${err.message}`
          : 'Prune failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setErrorsExpanded(false);
  };

  return (
    <CollapsibleSection
      sectionId="pruneOrphanCollectionWatchIds"
      title="Prune Orphan Collection watchIds"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Removes references in user collections that point to watches that no
          longer exist. Safe to run — only modifies{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchIds
          </code>{' '}
          arrays.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm font-medium text-gray-700">User UID</label>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={running}
            placeholder="leave empty for all users"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50 sm:min-w-[22rem]"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => void run()}
          >
            {running ? 'Pruning…' : 'Run'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Prune result
              </span>
              <Badge variant={result.errors.length ? 'warning' : 'success'}>
                done
              </Badge>
              <span className="text-xs text-gray-500">{scopeLabel}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Users scanned" value={result.usersScanned} />
              <ResultStat
                label="Collections scanned"
                value={result.collectionsScanned}
              />
              <ResultStat
                label="Collections updated"
                value={result.collectionsUpdated}
              />
              <ResultStat label="Orphans removed" value={result.orphansRemoved} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              User ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map((e: PruneOrphanCollectionWatchIdsError, i) => (
                              <tr key={`${e.userId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {e.userId}
                                </td>
                                <td className="px-3 py-1.5 text-red-600 align-top">
                                  {e.error}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: recompute the denormalised `referenceCount` on every calibre
 * from current watch-reference state. Useful after a bulk import or if counts
 * drift; each updated calibre re-indexes to Algolia automatically. Gated behind
 * a confirm modal since it sweeps the whole collection.
 */
function RecomputeCalibreReferenceCountsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] =
    useState<RecomputeCalibreReferenceCountsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await recomputeAllCalibreReferenceCounts();
      setResult(r);
      setErrorsExpanded(false);
      const summary =
        `Recompute complete: ${r.scanned} scanned, ` +
        `${r.updated} updated, ${r.reindexed} re-indexed` +
        (r.skipped ? `, ${r.skipped} skipped` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('recomputeAllCalibreReferenceCounts failed:', err);
      toast.error(
        err instanceof Error
          ? `Recompute failed: ${err.message}`
          : 'Recompute failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setErrorsExpanded(false);
  };

  return (
    <CollapsibleSection
      sectionId="recomputeCalibreReferenceCounts"
      title="Recompute Calibre Reference Counts"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Recalculates the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            referenceCount
          </code>{' '}
          field on every calibre based on current watch references. Use this after
          a bulk import, or if counts drift, or if the Algolia index is stale.
          This sweep force-re-indexes every calibre to Algolia — even ones whose
          count is already correct — so it also repairs a stale search index.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Recomputing…' : 'Recompute Counts'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Recompute result
              </span>
              <Badge variant={result.errors.length ? 'warning' : 'success'}>
                done
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Calibres scanned" value={result.scanned} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Re-indexed" value={result.reindexed} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Calibre ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map(
                              (
                                e: RecomputeCalibreReferenceCountsError,
                                i,
                              ) => (
                                <tr key={`${e.calibreId}/${i}`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {e.calibreId}
                                  </td>
                                  <td className="px-3 py-1.5 text-red-600 align-top">
                                    {e.error}
                                  </td>
                                </tr>
                              ),
                            )}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Recompute calibre reference counts?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will scan all watch references and update reference counts on
            every calibre. May take a minute. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Recompute
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: recompute the denormalised `parentReferenceCount` on every brand
 * from its current listable (parent) references. Seeds brands that predate the
 * counter and repairs drift; the reference trigger keeps them fresh thereafter.
 * Idempotent — a second run reports everything unchanged. Gated behind a confirm
 * modal since it sweeps the whole collection.
 */
function RecomputeWatchBrandReferenceCountsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] =
    useState<RecomputeWatchBrandReferenceCountsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await recomputeAllWatchBrandReferenceCounts();
      setResult(r);
      setErrorsExpanded(false);
      const summary =
        `Recompute complete: ${r.scanned} scanned, ` +
        `${r.updated} updated, ${r.unchanged} unchanged` +
        (r.skipped ? `, ${r.skipped} skipped` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('recomputeAllWatchBrandReferenceCounts failed:', err);
      toast.error(
        err instanceof Error
          ? `Recompute failed: ${err.message}`
          : 'Recompute failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setErrorsExpanded(false);
  };

  return (
    <CollapsibleSection
      sectionId="recomputeWatchBrandReferenceCounts"
      title="Recompute Brand Reference Counts"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Recalculates the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            parentReferenceCount
          </code>{' '}
          field on every brand from its current listable (parent) references. Run
          this once to seed brands that predate the counter, or any time counts
          drift — the reference trigger keeps them fresh from then on. Safe to
          re-run: brands whose count is already correct are reported unchanged
          and not written.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Recomputing…' : 'Recompute Counts'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Recompute result
              </span>
              <Badge variant={result.errors.length ? 'warning' : 'success'}>
                done
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Brands scanned" value={result.scanned} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Unchanged" value={result.unchanged} />
              <ResultStat label="Skipped" value={result.skipped} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors.map(
                            (e: RecomputeWatchBrandReferenceCountsError, i) => (
                              <tr key={`${e.brandId}/${i}`}>
                                <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                  {e.brandId}
                                </td>
                                <td className="px-3 py-1.5 text-red-600 align-top">
                                  {e.error}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                      The server reports at most the first 100 failures; the
                      skipped count is the true total.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Recompute brand reference counts?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will count the listable references of every brand and update
            those whose stored count is wrong. May take a minute. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Recompute
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: recompute the inverse `acquiredByBrandIds` field on every brand
 * by scanning all `acquisitions` arrays. Useful after a bulk acquisition import
 * or if the inverse field drifts; each updated brand re-indexes to Algolia
 * automatically via the existing brand trigger. Gated behind a confirm modal
 * since it sweeps the whole collection.
 */
function RecomputeAcquiredByBrandIdsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] =
    useState<RecomputeAcquiredByBrandIdsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await recomputeAllAcquiredByBrandIds();
      setResult(r);
      setErrorsExpanded(false);
      const summary =
        `Recompute complete: ${r.scanned} scanned, ` +
        `${r.updated} updated, ${r.cleared} cleared` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('recomputeAllAcquiredByBrandIds failed:', err);
      toast.error(
        err instanceof Error
          ? `Recompute failed: ${err.message}`
          : 'Recompute failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setErrorsExpanded(false);
  };

  return (
    <CollapsibleSection
      sectionId="recomputeAcquiredByBrandIds"
      title="Recompute Acquired-By Brand IDs"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Recalculates the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            acquiredByBrandIds
          </code>{' '}
          inverse field on every brand by scanning all{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            acquisitions
          </code>{' '}
          arrays. Use this after a bulk acquisition import or if the inverse field
          drifts. Each updated brand re-indexes to Algolia automatically via the
          existing brand trigger.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Recomputing…' : 'Recompute Inverse'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Recompute result
              </span>
              <Badge variant={result.errors.length ? 'warning' : 'success'}>
                done
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Brands scanned" value={result.scanned} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Cleared" value={result.cleared} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map(
                              (e: RecomputeAcquiredByBrandIdsError, i) => (
                                <tr key={`${e.brandId}/${i}`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {e.brandId}
                                  </td>
                                  <td className="px-3 py-1.5 text-red-600 align-top">
                                    {e.error}
                                  </td>
                                </tr>
                              ),
                            )}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Recompute acquired-by brand IDs?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will scan all brand{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              acquisitions
            </code>{' '}
            arrays and rebuild the inverse{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              acquiredByBrandIds
            </code>{' '}
            on every brand. May take ~30s. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Recompute
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Rescue tool: move watch references (and related entities — calibres, etc.)
 * from a source brand onto a target brand. Used to reconcile a duplicate/mistaken
 * brand doc holding stray refs (e.g. an orphan `iwc-schaffhausen` ref that belongs
 * on `iwc`). The source brand doc is always left in place.
 *
 * Moves everything by default, or — with a selector (watchlock #1078) — only the
 * refs matching a pattern, an id list, or a field filter. A selection is widened
 * server-side to whole parent/variant groups, because moving part of a group
 * loses data.
 *
 * THE DRY-RUN GATE. A selective wet run needs a `confirmationToken`, but the
 * server only checks it is non-empty; it is not bound to a preview, so it proves
 * nothing. The real safeguard is here: a selective move is unlocked only by a
 * successful dry run of the SAME brands and selector (`moveFingerprint`), and
 * the confirm modal shows that preview's numbers. That is also the only moment
 * the refs themselves can be seen — `sampleRefsMoved` exists on dry runs only.
 */
export function MoveRefsBetweenBrandsSection() {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [sourceBrandId, setSourceBrandId] = useState<string>('');
  const [targetBrandId, setTargetBrandId] = useState<string>('');
  const [dryRun, setDryRun] = useState(true);
  /**
   * Set when the curator unticks Dry run WHILE a selector is active — a
   * deliberate choice for filtered moves, which switching between filters then
   * respects. Unticking it for a move-all does not count.
   */
  const [selectiveWetChosen, setSelectiveWetChosen] = useState(false);
  const [selector, setSelector] = useState<SelectorForm>(EMPTY_SELECTOR_FORM);
  const [running, setRunning] = useState(false);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [result, setResult] =
    useState<MoveWatchReferencesBetweenBrandsResult | null>(null);
  /** The move a result belongs to, so a stale one is never shown against a changed form. */
  const [resultFingerprint, setResultFingerprint] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getWatchBrands()
      .then((all) => {
        if (!active) return;
        all.sort((a, b) =>
          (a.displayName || a.name).localeCompare(b.displayName || b.name),
        );
        setBrands(all);
      })
      .catch((err) => {
        console.error('Failed to load brands for move-refs tool:', err);
      });
    return () => {
      active = false;
    };
  }, []);

  const brandLabel = (id: string): string => {
    const b = brands.find((x) => x.id === id);
    return b ? b.displayName || b.name : id;
  };

  const built = buildMoveSelectorPayload(selector);
  const selective = selector.mode !== 'all';
  const fingerprint = built.payload
    ? moveFingerprint(sourceBrandId, targetBrandId, built.payload)
    : null;
  // A result is only shown while the form still describes the move it came from.
  const currentResult = result && resultFingerprint === fingerprint ? result : null;
  const previewMatches = !!currentResult && currentResult.dryRun;

  const bothChosen = sourceBrandId !== '' && targetBrandId !== '';
  const sameBrand = bothChosen && sourceBrandId === targetBrandId;
  const formValid = bothChosen && !sameBrand && built.error === null;
  const needsPreview = selective && !dryRun && !previewMatches;
  const canRun = formValid && !running && !needsPreview;

  const updateSelector = (patch: Partial<SelectorForm>) => {
    // Soft default: switching INTO a filter ticks Dry run — a selective move
    // needs a preview anyway — unless the curator has already unticked it for a
    // filtered move. Computed from current state, outside the updater.
    if (patch.mode && patch.mode !== selector.mode && patch.mode !== 'all' && !selectiveWetChosen) {
      setDryRun(true);
    }
    setSelector((prev) => ({ ...prev, ...patch }));
  };

  // Show a validation message once there is something to validate. A blank
  // input still blocks the run; it just does not greet a fresh choice in red.
  const activeInput =
    selector.mode === 'pattern'
      ? selector.pattern
      : selector.mode === 'ids'
        ? selector.idsText
        : selector.mode === 'field'
          ? selector.fieldValue
          : '';
  const showSelectorError = selective && built.error !== null && activeInput.trim() !== '';

  const run = async () => {
    if (!built.payload) return;
    const wet = !dryRun;
    // Belt and braces: the server rejects this too, but say so plainly first.
    if (wet && selective && !previewMatches) {
      toast.error('Run a dry run of this exact selection before moving.');
      return;
    }
    setRunning(true);
    try {
      const r = await moveWatchReferencesBetweenBrands({
        sourceBrandId,
        targetBrandId,
        dryRun,
        ...built.payload,
        ...(wet && selective ? { confirmationToken: newConfirmationToken() } : {}),
      });
      setResult(r);
      setResultFingerprint(fingerprint);
      const other = Object.values(r.otherEntitiesMoved).reduce(
        (sum, n) => sum + n,
        0,
      );
      const summary =
        `${r.dryRun ? 'Dry run' : 'Move'} complete: ` +
        `${r.dryRun ? 'would move' : 'moved'} ${r.refsMoved} ref(s), ` +
        `${r.calibresMoved} calibre(s)` +
        (other ? `, ${other} other entit${other === 1 ? 'y' : 'ies'}` : '');
      toast.success(summary);
    } catch (err) {
      console.error('moveWatchReferencesBetweenBrands failed:', err);
      toast.error(
        err instanceof Error
          ? `Move failed: ${err.message}`
          : 'Move failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const otherEntries = currentResult
    ? Object.entries(currentResult.otherEntitiesMoved)
    : [];
  const idCount = parseReferenceIds(selector.idsText).length;

  const selectorSummary = (): string => {
    switch (selector.mode) {
      case 'all':
        return 'ALL watch references';
      case 'pattern':
        return `refs whose reference matches /${selector.pattern}/`;
      case 'ids':
        return `${idCount} listed ref id${idCount === 1 ? '' : 's'}`;
      case 'field':
        return `refs whose ${selector.field} ${
          selector.fieldOp === 'equals'
            ? `equals “${selector.fieldValue.trim()}”`
            : `matches /${selector.fieldValue}/`
        }`;
    }
  };

  const modeOption = (mode: SelectorMode, label: string) => (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="radio"
        name="moveRefsSelector"
        checked={selector.mode === mode}
        onChange={() => updateSelector({ mode })}
        disabled={running}
        className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
      />
      {label}
    </label>
  );

  const inputClass =
    'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50';

  return (
    <CollapsibleSection
      sectionId="moveRefsBetweenBrands"
      title="Move Refs Between Brands"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Rescue tool: moves watch references (and related entities — calibres,
          etc.) from a <strong>source</strong> brand onto a{' '}
          <strong>target</strong> brand — all of them, or only those matching a
          filter. Use to reconcile a duplicate or mistaken brand doc holding stray
          refs (e.g. an orphan{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            iwc-schaffhausen
          </code>{' '}
          ref that belongs on{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">iwc</code>).
          Run a dry run first, then uncheck <em>Dry run</em> to move for real. The
          source brand doc is <strong>not</strong> deleted.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Source brand (refs move FROM)
            </label>
            <select
              value={sourceBrandId}
              onChange={(e) => setSourceBrandId(e.target.value)}
              disabled={running}
              className={inputClass}
            >
              <option value="">Select source…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.displayName || b.name} ({b.id})
                </option>
              ))}
            </select>
          </div>

          <div className="text-gray-400 pb-2 hidden sm:block">→</div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Target brand (refs move TO)
            </label>
            <select
              value={targetBrandId}
              onChange={(e) => setTargetBrandId(e.target.value)}
              disabled={running}
              className={inputClass}
            >
              <option value="">Select target…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.displayName || b.name} ({b.id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {sameBrand && (
          <p className="text-sm text-red-600">
            Source and target must be different brands.
          </p>
        )}

        <fieldset className="rounded-lg border border-gray-200 p-3 space-y-2">
          <legend className="px-1 text-sm font-medium text-gray-700">Refs to move</legend>
          {modeOption('all', 'Move all refs')}

          {modeOption('pattern', 'Filter by pattern')}
          {selector.mode === 'pattern' && (
            <div className="ml-6 space-y-1">
              <input
                type="text"
                value={selector.pattern}
                onChange={(e) => updateSelector({ pattern: e.target.value })}
                disabled={running}
                aria-label="Reference pattern"
                placeholder="IW50\d+"
                className={`${inputClass} w-full font-mono`}
              />
              <p className="text-xs text-gray-500">
                Regular expression on the <code>reference</code> field. Must match the
                whole value, and is case-sensitive.
              </p>
            </div>
          )}

          {modeOption('ids', 'Filter by ID list')}
          {selector.mode === 'ids' && (
            <div className="ml-6 space-y-1">
              <textarea
                value={selector.idsText}
                onChange={(e) => updateSelector({ idsText: e.target.value })}
                disabled={running}
                aria-label="Reference ids"
                rows={5}
                placeholder="One ref doc id per line"
                className={`${inputClass} w-full font-mono`}
              />
              <p
                className={`text-xs ${idCount > MAX_REFERENCE_IDS ? 'font-medium text-amber-700' : 'text-gray-500'}`}
              >
                {idCount} unique id{idCount === 1 ? '' : 's'}
                {idCount > MAX_REFERENCE_IDS &&
                  ` — over the ${MAX_REFERENCE_IDS} cap; split the list into separate moves`}
              </p>
            </div>
          )}

          {modeOption('field', 'Filter by field')}
          {selector.mode === 'field' && (
            <div className="ml-6 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selector.field}
                  onChange={(e) =>
                    updateSelector({ field: e.target.value as SelectorForm['field'] })
                  }
                  disabled={running}
                  aria-label="Field"
                  className={inputClass}
                >
                  {FAMILY_FILTER_FIELDS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                {(['equals', 'matches'] as const).map((op) => (
                  <label key={op} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="moveRefsFieldOp"
                      checked={selector.fieldOp === op}
                      onChange={() => updateSelector({ fieldOp: op })}
                      disabled={running}
                      className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    {op}
                  </label>
                ))}
              </div>
              <input
                type="text"
                value={selector.fieldValue}
                onChange={(e) => updateSelector({ fieldValue: e.target.value })}
                disabled={running}
                aria-label="Field value"
                placeholder={selector.fieldOp === 'equals' ? 'Portugieser' : 'Pilot.*'}
                className={`${inputClass} w-full ${selector.fieldOp === 'matches' ? 'font-mono' : ''}`}
              />
              <p className="text-xs text-gray-500">
                {selector.fieldOp === 'equals'
                  ? 'Exact value — ignores case and surrounding spaces.'
                  : 'Regular expression — must match the whole value, and is case-sensitive.'}
              </p>
            </div>
          )}

          {selective && (
            <p className="text-xs text-amber-700">
              Refs that share a parent/variant family with a match move too — splitting a family
              loses data. The dry run shows how many.
            </p>
          )}
          {showSelectorError && (
            <p className="text-sm text-red-600">{built.error}</p>
          )}
        </fieldset>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => {
              setDryRun(e.target.checked);
              if (selective) setSelectiveWetChosen(!e.target.checked);
            }}
            disabled={running}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Dry run (preview counts, no writes)
        </label>

        {needsPreview && formValid && (
          <p className="text-sm text-amber-700">
            Run a dry run of this exact selection first — the move unlocks once it has succeeded.
          </p>
        )}

        <div className="flex gap-2">
          <Button
            variant={dryRun ? 'secondary' : 'primary'}
            loading={running}
            disabled={!canRun}
            onClick={() => {
              if (dryRun) {
                void run();
              } else {
                setConfirmWrite(true);
              }
            }}
          >
            {running
              ? dryRun
                ? 'Previewing…'
                : 'Moving…'
              : dryRun
                ? 'Preview'
                : 'Move refs'}
          </Button>
          {currentResult && (
            <Button
              variant="ghost"
              disabled={running}
              onClick={() => {
                setResult(null);
                setResultFingerprint(null);
              }}
            >
              Reset
            </Button>
          )}
        </div>

        {currentResult && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {currentResult.dryRun ? 'Dry run' : 'Move'} result
              </span>
              <Badge variant={currentResult.dryRun ? 'info' : 'success'}>
                {currentResult.dryRun ? 'no writes' : 'written'}
              </Badge>
              <span className="text-xs text-gray-500">
                {brandLabel(sourceBrandId)} → {brandLabel(targetBrandId)}
              </span>
            </div>

            {currentResult.referenceIdsNotFound && currentResult.referenceIdsNotFound.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-medium">
                  {currentResult.referenceIdsNotFound.length} id
                  {currentResult.referenceIdsNotFound.length === 1 ? '' : 's'} not found under{' '}
                  {brandLabel(sourceBrandId)}
                </p>
                <p className="mt-1 break-all font-mono text-xs">
                  {currentResult.referenceIdsNotFound.join(', ')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat
                label={currentResult.dryRun ? 'Refs to move' : 'Refs moved'}
                value={currentResult.refsMoved}
              />
              <ResultStat
                label={currentResult.dryRun ? 'Calibres to move' : 'Calibres moved'}
                value={currentResult.calibresMoved}
              />
              {otherEntries.map(([key, count]) => (
                <ResultStat key={key} label={key} value={count} />
              ))}
            </div>

            {typeof currentResult.refsAddedByFamilyClosure === 'number' &&
              currentResult.refsAddedByFamilyClosure > 0 && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-800">
                  {currentResult.refsAddedByFamilyClosure} ref
                  {currentResult.refsAddedByFamilyClosure === 1 ? '' : 's'} pulled in with their
                  parent/variant families
                  {currentResult.dryRun ? ' — included in the count above.' : '.'}
                </p>
              )}

            {currentResult.sampleRefsMoved && currentResult.sampleRefsMoved.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-600">
                  {currentResult.refsMoved > currentResult.sampleRefsMoved.length
                    ? `First ${currentResult.sampleRefsMoved.length} of ${currentResult.refsMoved} refs`
                    : 'Refs'}
                </p>
                <ul className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white px-2 py-1 font-mono text-xs text-gray-700">
                  {/* Index keys: two refs can share a reference string. */}
                  {currentResult.sampleRefsMoved.map((ref, i) => (
                    <li key={i}>{ref}</li>
                  ))}
                </ul>
              </div>
            )}

            {currentResult.auditLogId && (
              <p className="text-xs text-gray-500">
                Audit log:{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                  {currentResult.auditLogId}
                </code>
              </p>
            )}

            {!currentResult.dryRun && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Source brand{' '}
                <code className="bg-amber-100 px-1.5 py-0.5 rounded">
                  {brandLabel(sourceBrandId)}
                </code>{' '}
                {selective
                  ? 'still exists and keeps the refs this filter did not select.'
                  : 'still exists and is now empty of refs. Delete it manually via the brand list.'}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Move refs between brands?"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            This will move {selectorSummary()} and related entities from{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              {brandLabel(sourceBrandId)}
            </code>{' '}
            onto{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              {brandLabel(targetBrandId)}
            </code>
            . The source brand doc is not deleted.
          </p>
          {selective && previewMatches && currentResult && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <p className="font-medium">From the dry run of this selection:</p>
              <ul className="mt-1 list-disc pl-5">
                <li>{currentResult.refsMoved} refs, {currentResult.calibresMoved} calibres</li>
                {(currentResult.refsAddedByFamilyClosure ?? 0) > 0 && (
                  <li className="text-amber-700">
                    {currentResult.refsAddedByFamilyClosure} of those pulled in with their families
                  </li>
                )}
                {(currentResult.referenceIdsNotFound?.length ?? 0) > 0 && (
                  <li className="text-amber-700">
                    {currentResult.referenceIdsNotFound?.length} requested ids not found
                  </li>
                )}
              </ul>
            </div>
          )}
          <p className="text-sm text-gray-700">Continue?</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run();
              }}
            >
              Move refs
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Phase 3 of the parent-org migration: set `parentOrgId` on every brand and
 * manufacturer whose legacy `parentOrg` string resolves to exactly one
 * organization. Ambiguous and unmatched strings are never written — they are
 * listed, each with a jump to its editor, for a curator to fix by hand.
 *
 * Same shape as Move Refs: the write is unlocked by a dry run in this session and
 * confirmed against that dry run's numbers. The callable writes `parentOrgId`
 * alone and skips linked documents, so a second run writes nothing.
 */
export function BackfillParentOrgIdsSection({
  onOpenBrand,
  onOpenManufacturer,
}: {
  onOpenBrand?: (brandId: string) => void;
  onOpenManufacturer?: (manufacturerId: string) => void;
} = {}) {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<BackfillParentOrgIdsResult | null>(null);
  /** The most recent dry run — what unlocks, and what the confirm modal quotes. */
  const [preview, setPreview] = useState<BackfillParentOrgIdsResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(true);
    try {
      const r = await backfillParentOrgIds({
        dryRun,
        ...(dryRun ? {} : { confirmationToken: newConfirmationToken() }),
      });
      setResult(r);
      // A wet run consumes the preview: the data it described has changed, so the
      // next write needs a fresh look.
      setPreview(dryRun ? r : null);
      toast.success(
        dryRun
          ? `Dry run: ${r.counts.resolved} would be linked, ${r.counts.ambiguous + r.counts.unmatched} need a curator`
          : `Linked ${r.counts.written} document${r.counts.written === 1 ? '' : 's'}`,
      );
    } catch (err) {
      console.error('backfillParentOrgIds failed:', err);
      toast.error(err instanceof Error ? `Backfill failed: ${err.message}` : 'Backfill failed — see console');
    } finally {
      setRunning(false);
    }
  };

  const canWrite = !!preview && preview.counts.resolved > 0 && !running;

  const entityName = (row: ParentOrgBackfillRow) => {
    const open = row.collection === 'watchBrands' ? onOpenBrand : onOpenManufacturer;
    return open ? (
      <button
        type="button"
        onClick={() => open(row.id)}
        className="text-left text-brand-700 hover:underline"
        title={`Open ${row.id} in its editor`}
      >
        {row.name}
      </button>
    ) : (
      <span>{row.name}</span>
    );
  };

  const typeLabel = (row: ParentOrgBackfillRow) =>
    row.collection === 'watchBrands' ? 'Brand' : 'Manufacturer';

  const table = (
    rows: ParentOrgBackfillRow[],
    columns: Array<{ header: string; cell: (row: ParentOrgBackfillRow) => ReactNode }>,
  ) => (
    <div className="max-h-80 overflow-auto rounded border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="px-2 py-1.5 font-medium">Type</th>
            <th className="px-2 py-1.5 font-medium">Name</th>
            {columns.map((c) => (
              <th key={c.header} className="px-2 py-1.5 font-medium">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={`${row.collection}/${row.id}`}>
              <td className="px-2 py-1.5 text-xs text-gray-500">{typeLabel(row)}</td>
              <td className="px-2 py-1.5">
                {entityName(row)}
                <div className="font-mono text-[10px] text-gray-400">{row.id}</div>
              </td>
              {columns.map((c) => (
                <td key={c.header} className="px-2 py-1.5">
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const code = (text: string) => (
    <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{text}</code>
  );

  return (
    <CollapsibleSection sectionId="backfillParentOrgIds" title="Backfill Parent-Org FKs">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-600">
          Sets {code('parentOrgId')} on brands and manufacturers whose legacy {code('parentOrg')}{' '}
          string matches exactly one parent organization — by id, name or alternative name. Strings
          matching several organizations, or none, are <strong>never</strong> written; they are
          listed below to fix by hand. Writes only {code('parentOrgId')} and skips anything already
          linked, so re-running is safe.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={running} disabled={running} onClick={() => void run(true)}>
            Dry run
          </Button>
          <Button
            variant="primary"
            disabled={!canWrite}
            onClick={() => setConfirmOpen(true)}
            title={
              preview
                ? preview.counts.resolved === 0
                  ? 'Nothing to link'
                  : undefined
                : 'Run a dry run first'
            }
          >
            Backfill
          </Button>
          {!preview && !running && (
            <span className="self-center text-xs text-gray-500">Run a dry run to unlock the backfill.</span>
          )}
        </div>

        {result && (
          <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <ResultStat
                label={result.dryRun ? 'Would link' : 'Linked'}
                value={result.dryRun ? result.counts.resolved : result.counts.written}
              />
              <ResultStat label="Ambiguous" value={result.counts.ambiguous} />
              <ResultStat label="Unmatched" value={result.counts.unmatched} />
              <ResultStat label="Already linked" value={result.counts.alreadyLinked} />
              <ResultStat label="Linked, flagged" value={result.counts.flaggedLinked} />
              <ResultStat label="No parent org" value={result.counts.noString} />
              <ResultStat label="Scanned" value={result.counts.scanned} />
            </div>

            {result.ambiguous.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-amber-800">
                  Ambiguous — matches more than one organization ({result.ambiguous.length})
                </p>
                {table(result.ambiguous, [
                  { header: 'Stored string', cell: (r) => code(r.parentOrg) },
                  {
                    header: 'Matches',
                    cell: (r) => (
                      <span className="font-mono text-xs text-gray-600">
                        {(r.candidateOrgIds ?? []).join(', ')}
                      </span>
                    ),
                  },
                ])}
              </div>
            )}

            {result.unmatched.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-amber-800">
                  Unmatched — no organization answers to this string ({result.unmatched.length})
                </p>
                {table(result.unmatched, [{ header: 'Stored string', cell: (r) => code(r.parentOrg) }])}
                <p className="mt-1 text-xs text-gray-500">
                  Fix by picking an organization in the editor, or by adding the string as an
                  alternative name on the right organization and re-running.
                </p>
              </div>
            )}

            {result.flaggedLinked.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-amber-800">
                  Already linked, but worth a look ({result.flaggedLinked.length})
                </p>
                {table(result.flaggedLinked, [
                  { header: 'Stored FK', cell: (r) => code(r.parentOrgId ?? '') },
                  {
                    header: 'Issue',
                    cell: (r) =>
                      r.flag === 'fk-missing-org' ? (
                        <span className="text-xs text-red-700">Organization no longer exists</span>
                      ) : (
                        <span className="text-xs text-amber-800">
                          String {code(r.parentOrg)} names {code((r.candidateOrgIds ?? []).join(', '))}
                        </span>
                      ),
                  },
                ])}
              </div>
            )}

            {result.resolvedSample.length > 0 && (
              <details>
                <summary className="cursor-pointer text-sm text-gray-700">
                  {result.dryRun ? 'Would link' : 'Linked'}
                  {result.counts.resolved > result.resolvedSample.length
                    ? ` — first ${result.resolvedSample.length} of ${result.counts.resolved}`
                    : ` (${result.counts.resolved})`}
                </summary>
                <div className="mt-2">
                  {table(result.resolvedSample, [
                    { header: 'Stored string', cell: (r) => code(r.parentOrg) },
                    {
                      header: 'Organization',
                      cell: (r) => code((r.candidateOrgIds ?? []).join(', ')),
                    },
                  ])}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title="Backfill parent-org FKs?">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            This writes {code('parentOrgId')} on{' '}
            <strong>{preview?.counts.resolved ?? 0}</strong> document
            {preview?.counts.resolved === 1 ? '' : 's'}, from the last dry run. Nothing else on those
            documents changes, and{' '}
            {(preview?.counts.ambiguous ?? 0) + (preview?.counts.unmatched ?? 0)} ambiguous or
            unmatched documents are left alone. Continue?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run(false);
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Tag legacy descriptions 'imported' where the ref's own `sourceType` says bulk
 * import created it. Provenance hygiene: it changes no pre-tick or rewrite
 * decision (untagged and 'imported' are treated alike), and each write is one
 * Algolia re-index — so the write runs in slices, like the calibre-links backfill.
 */
export function BackfillDescriptionSourceSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<BackfillDescriptionSourceResult | null>(null);
  const [preview, setPreview] = useState<BackfillDescriptionSourceResult | null>(null);
  /** Cursor from the last wet slice — Continue resumes from it. */
  const [cursor, setCursor] = useState<string | null>(null);
  const [writtenTotal, setWrittenTotal] = useState(0);

  const run = async (dryRun: boolean, resume = false) => {
    setRunning(true);
    try {
      const r = await backfillDescriptionSource({
        dryRun,
        ...(dryRun ? {} : { confirmationToken: newConfirmationToken() }),
        ...(!dryRun && resume && cursor ? { startAfterPath: cursor } : {}),
      });
      setResult(r);
      if (dryRun) {
        setPreview(r);
        setCursor(null);
        setWrittenTotal(0);
      } else {
        setCursor(r.nextCursor ?? null);
        setWrittenTotal((t) => (resume ? t : 0) + r.written);
      }
      toast.success(
        dryRun
          ? `Dry run: ${r.imported} descriptions would be tagged 'imported'`
          : `Tagged ${r.written}${r.nextCursor ? ' — Continue for the next slice' : ' — done'}`,
      );
    } catch (err) {
      console.error('backfillDescriptionSource failed:', err);
      toast.error(err instanceof Error ? `Backfill failed: ${err.message}` : 'Backfill failed — see console');
    } finally {
      setRunning(false);
    }
  };

  const code = (text: string) => <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{text}</code>;
  const dryIncomplete = !!preview?.nextCursor;
  const canWrite = !!preview && preview.imported > 0 && !running;
  const counts = (m: Record<string, number>) =>
    Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .map(([k, n]) => `${k} ${n.toLocaleString()}`)
      .join(' · ');

  return (
    <CollapsibleSection sectionId="backfillDescriptionSource" title="Backfill description source">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-600">
          Stamps {code("descriptionSource: 'imported'")} on descriptions that carry no source, where the
          ref&apos;s own {code('sourceType')} says bulk import created it ({code('shopify')}, {code('generic')},
          or {code('manual')} — a file upload). Refs with no {code('sourceType')} are left alone: nothing
          separates an old import from a hand-made ref. Writes only {code('descriptionSource')}.
        </p>
        <p className="rounded bg-amber-50 p-2 text-xs text-amber-900">
          This changes no decision today — the review pre-tick and the Description Rewrite tool treat untagged
          and {code("'imported'")} alike. Each write re-indexes the ref in Algolia, so the backfill runs{' '}
          {DEFAULT_DESCRIPTION_BACKFILL_SLICE.toLocaleString()} at a time.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={running} disabled={running} onClick={() => void run(true)}>
            Dry run
          </Button>
          <Button
            variant="primary"
            disabled={!canWrite || !!cursor}
            onClick={() => setConfirmOpen(true)}
            title={preview ? undefined : 'Run a dry run first'}
          >
            Backfill
          </Button>
          {cursor && (
            <Button variant="primary" loading={running} disabled={running} onClick={() => void run(false, true)}>
              Continue
            </Button>
          )}
        </div>

        {result && (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{result.dryRun ? 'Dry run' : 'Backfill slice'}</span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>{result.dryRun ? 'no writes' : 'written'}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat label={result.dryRun ? "Would tag 'imported'" : 'Tagged this slice'} value={result.dryRun ? result.imported : result.written} />
              <ResultStat label="Already tagged" value={result.tagged} />
              <ResultStat label="No sourceType (left alone)" value={result.unknownSource} />
              <ResultStat label="No description" value={result.noDescription} />
              <ResultStat label="Other source (left alone)" value={result.otherSource} />
              {!result.dryRun && <ResultStat label="Tagged, all slices" value={writtenTotal} />}
            </div>
            {Object.keys(result.importedBySourceType).length > 0 && (
              <p className="text-xs text-gray-600">By source: {counts(result.importedBySourceType)}</p>
            )}
            {Object.keys(result.otherSourceTypes).length > 0 && (
              <p className="text-xs text-gray-600">Left alone, other sources: {counts(result.otherSourceTypes)}</p>
            )}
            {result.dryRun && dryIncomplete && (
              <p className="text-xs text-amber-800">
                The dry run hit its time limit before the end of the catalogue — the counts cover what it scanned.
              </p>
            )}
            {!result.dryRun && result.nextCursor && (
              <p className="text-xs text-gray-600">Slice complete. Continue runs the next {result.maxWrites.toLocaleString()}.</p>
            )}
            {!result.dryRun && !result.nextCursor && <p className="text-xs text-emerald-700">Done — nothing left to tag.</p>}
          </div>
        )}
      </div>

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title="Backfill description source?">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            From the last dry run, <strong>{preview?.imported.toLocaleString() ?? 0}</strong> descriptions would be
            tagged {code("'imported'")}. This writes the first{' '}
            <strong>{DEFAULT_DESCRIPTION_BACKFILL_SLICE.toLocaleString()}</strong>; Continue runs each further slice.
            Every write re-indexes that ref in Algolia. Continue?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run(false);
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/** Slice size the description-source backfill writes per click (the callable's default). */
const DEFAULT_DESCRIPTION_BACKFILL_SLICE = 5000;

/**
 * Undo what bulk import did to analog Casio refs before #602: unlink the
 * electronic module it gave them, link (or seed) their calibre, and delete module
 * docs that have become calibres. Same shape as the parent-org backfill: the
 * write is unlocked by a dry run in this session and confirmed against it.
 */
export function CleanupAnalogModuleLinksSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<CleanupAnalogModuleLinksResult | null>(null);
  const [preview, setPreview] = useState<CleanupAnalogModuleLinksResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(true);
    try {
      const r = await cleanupAnalogModuleLinks({
        dryRun,
        ...(dryRun ? {} : { confirmationToken: newConfirmationToken() }),
      });
      setResult(r);
      setPreview(dryRun ? r : null);
      toast.success(
        dryRun
          ? `Dry run: ${r.counts.refsToUnlink} refs to unlink, ${r.counts.modulesToDelete} modules to delete`
          : `Unlinked ${r.counts.refsWritten} refs, deleted ${r.counts.modulesDeleted} modules`,
      );
    } catch (err) {
      console.error('cleanupAnalogModuleLinks failed:', err);
      toast.error(err instanceof Error ? `Cleanup failed: ${err.message}` : 'Cleanup failed — see console');
    } finally {
      setRunning(false);
    }
  };

  const canWrite =
    !!preview && (preview.counts.refsToUnlink > 0 || preview.counts.modulesToDelete > 0) && !running;
  const code = (text: string) => <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{text}</code>;
  const refName = (path: string) => path.split('/').slice(-3).join('/');

  const moduleTable = (rows: AnalogModuleCleanupModuleRow[], withCalibre: boolean) => (
    <div className="max-h-60 overflow-auto rounded border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="px-2 py-1.5 font-medium">Module</th>
            <th className="px-2 py-1.5 font-medium">Number</th>
            <th className="px-2 py-1.5 font-medium">Refs unlinked here</th>
            {withCalibre && <th className="px-2 py-1.5 font-medium">Replaced by calibre</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((m) => (
            <tr key={m.moduleId}>
              <td className="px-2 py-1.5 font-mono text-xs">{m.moduleId}</td>
              <td className="px-2 py-1.5">{m.number ?? '—'}</td>
              <td className="px-2 py-1.5">{m.unlinkedRefs}</td>
              {withCalibre && <td className="px-2 py-1.5 font-mono text-xs">{m.calibreId}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <CollapsibleSection sectionId="cleanupAnalogModuleLinks" title="Clean up analog module links">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-600">
          Before the import fix (#602), bulk import linked analog Casio refs to an{' '}
          <em>electronic</em> module — Casio numbers its analog quartz movements as modules too. For every
          ref whose watch type has no {code('showModule')} flag this removes {code('moduleId')}, and if the
          ref has no calibre link, links the calibre that carries the module&apos;s number — or seeds the
          calibre string ({code('Casio Module 5625')}) where no such calibre exists yet. A module doc is
          deleted only once a calibre carries its number and nothing links it. Refs of an unresolved type
          are left alone; re-running is safe.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={running} disabled={running} onClick={() => void run(true)}>
            Dry run
          </Button>
          <Button
            variant="primary"
            disabled={!canWrite}
            onClick={() => setConfirmOpen(true)}
            title={preview ? (canWrite ? undefined : 'Nothing to clean up') : 'Run a dry run first'}
          >
            Clean up
          </Button>
          {!preview && !running && (
            <span className="self-center text-xs text-gray-500">Run a dry run to unlock the clean-up.</span>
          )}
        </div>

        {result && (
          <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Clean-up'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>{result.dryRun ? 'no writes' : 'written'}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <ResultStat
                label={result.dryRun ? 'Refs to unlink' : 'Refs unlinked'}
                value={result.dryRun ? result.counts.refsToUnlink : result.counts.refsWritten}
              />
              <ResultStat label="Calibre linked" value={result.counts.calibresLinked} />
              <ResultStat label="Calibre seeded" value={result.counts.calibresSeeded} />
              <ResultStat label="Already had a calibre" value={result.counts.alreadyLinked} />
              <ResultStat
                label={result.dryRun ? 'Modules to delete' : 'Modules deleted'}
                value={result.dryRun ? result.counts.modulesToDelete : result.counts.modulesDeleted}
              />
              <ResultStat label="Modules awaiting a calibre" value={result.counts.modulesAwaitingCalibre} />
              <ResultStat label="Links to deleted modules" value={result.counts.danglingModules} />
              <ResultStat label="Unresolved type (skipped)" value={result.skippedUnresolvedType} />
            </div>

            {result.sharedModules.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-amber-800">
                  Shared modules — kept ({result.sharedModules.length})
                </p>
                <p className="mb-1 text-xs text-gray-600">
                  Also linked by digital / analog-digital refs, so they are real electronic modules. The{' '}
                  {result.counts.sharedModuleRefs} analog ref(s) on them are still unlinked by this run — if
                  one is really an analog-digital watch, fix its watch type instead.
                </p>
                <ul className="list-inside list-disc text-xs text-gray-700">
                  {result.sharedModules.map((m) => (
                    <li key={m.moduleId}>
                      {code(m.moduleId)} — {m.analogRefs} analog, {m.otherRefs} other
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.modulesToDelete.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-gray-800">
                  {result.dryRun ? 'Module docs to delete' : 'Module docs deleted'} ({result.modulesToDelete.length})
                </p>
                {moduleTable(result.modulesToDelete, true)}
              </div>
            )}

            {result.modulesAwaitingCalibre.length > 0 && (
              <div>
                <p className="mb-1 text-sm font-medium text-amber-800">
                  Awaiting a calibre — kept ({result.modulesAwaitingCalibre.length})
                </p>
                <p className="mb-1 text-xs text-gray-600">
                  No calibre carries these numbers yet. Their refs get the calibre string seeded; create the
                  calibre, then re-run to link the refs and delete the module doc.
                </p>
                {moduleTable(result.modulesAwaitingCalibre, false)}
              </div>
            )}

            {result.ambiguousNumbers.length > 0 && (
              <p className="text-xs text-amber-800">
                Numbers carried by more than one calibre (left unlinked): {result.ambiguousNumbers.join(', ')}
              </p>
            )}

            {result.refs.length > 0 && (
              <details>
                <summary className="cursor-pointer text-sm text-gray-700">
                  Every ref ({result.refs.length})
                </summary>
                <div className="mt-2 max-h-80 overflow-auto rounded border border-gray-200 bg-white">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">Ref</th>
                        <th className="px-2 py-1.5 font-medium">Module</th>
                        <th className="px-2 py-1.5 font-medium">Calibre</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {result.refs.map((r) => (
                        <tr key={r.path}>
                          <td className="px-2 py-1 font-mono">{refName(r.path)}</td>
                          <td className="px-2 py-1 font-mono">
                            {r.moduleId}
                            {r.danglingModule && <span className="ml-1 text-gray-400">(deleted)</span>}
                            {r.sharedModule && <span className="ml-1 text-amber-700">(shared)</span>}
                          </td>
                          <td className="px-2 py-1">
                            {r.calibreOutcome === 'alreadyLinked' && <span className="text-gray-500">already linked</span>}
                            {r.calibreOutcome === 'linked' && <>link {code(r.write.calibreId ?? '')}</>}
                            {r.calibreOutcome === 'seeded' && <>seed {code(r.write.calibre ?? '')}</>}
                            {r.calibreOutcome === 'noNumber' && <span className="text-amber-700">no module number</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title="Clean up analog module links?">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            From the last dry run: removes {code('moduleId')} from <strong>{preview?.counts.refsToUnlink ?? 0}</strong>{' '}
            refs, links a calibre on <strong>{preview?.counts.calibresLinked ?? 0}</strong>, seeds the calibre
            string on <strong>{preview?.counts.calibresSeeded ?? 0}</strong>, and deletes{' '}
            <strong>{preview?.counts.modulesToDelete ?? 0}</strong> module doc
            {preview?.counts.modulesToDelete === 1 ? '' : 's'}. Module deletions cannot be undone. Continue?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run(false);
              }}
            >
              Clean up
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}


/**
 * Maintenance: move the structural values out of `dialAndHands.finish` into
 * `dialAndHands.dialFeatures`, the field #618 split off for them, and clear the
 * finish. Same dry-run-then-confirm shape as the analog module clean-up, plus a
 * RESUME cursor — there is no index for "refs with a structural finish" (it is
 * a map subfield), so the run reads references brand by brand and cannot always
 * finish in one invocation.
 */
export function MigrateDialFinishSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<MigrateDialFinishResult | null>(null);
  const [preview, setPreview] = useState<MigrateDialFinishResult | null>(null);
  /**
   * Resume cursors, ONE PER MODE. They must not be shared: a dry run that stops
   * at the soft deadline has only PREVIEWED the brands it covered, so handing
   * its cursor to the write would start the write after them and skip every ref
   * it just reported. The write's own cursor advances only when the write runs.
   */
  const [cursors, setCursors] = useState<MigrationCursors>(NO_CURSORS);
  const dryCursor = cursors.dry;
  const wetCursor = cursors.wet;

  const run = async (dryRun: boolean) => {
    setRunning(true);
    try {
      const cursor = cursorForRun(cursors, dryRun);
      const r = await migrateDialFinishToFeatures({
        dryRun,
        ...(dryRun ? {} : { confirmationToken: newConfirmationToken() }),
        ...(cursor ? { startAfterBrandId: cursor } : {}),
      });
      setResult(r);
      setCursors((prev) =>
        advanceCursors(prev, { dryRun, complete: r.complete, lastBrandId: r.lastBrandId }),
      );
      // Keep the write unlocked while there is more to write; clear the preview
      // only once the write has actually finished the corpus.
      if (dryRun) setPreview(r);
      else if (r.complete) setPreview(null);
      const tail = r.complete ? '' : ' — more to do, press Continue';
      toast.success(
        dryRun
          ? `Dry run: ${r.counts.toMigrate} refs to migrate${tail}`
          : `Migrated ${r.counts.refsWritten} refs${tail}`,
      );
    } catch (err) {
      console.error('migrateDialFinishToFeatures failed:', err);
      toast.error(
        err instanceof Error ? `Migration failed: ${err.message}` : 'Migration failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  // Unlocked by a dry run in this session, and STAYS unlocked mid-write so a
  // multi-pass migration can be carried to completion without re-previewing.
  const canWrite = ((!!preview && preview.counts.toMigrate > 0) || !!wetCursor) && !running;
  const code = (text: string) => <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{text}</code>;
  const refName = (path: string) => path.split('/').slice(-3).join('/');

  return (
    <CollapsibleSection sectionId="migrateDialFinish" title="Migrate dial finish → dial features">
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-600">
          {code('dialFinish')} used to answer three questions at once. #618 gave the structural half its own
          field, so {code('skeletonized')}, {code('sandwich')} and {code('three_dimensional')} move to{' '}
          {code('dialAndHands.dialFeatures')} ({code('skeletonized')}, {code('sandwich_dial')},{' '}
          {code('multi_level')}) and the finish is cleared. Existing features are kept and the new one is
          appended, never duplicated. {code('marquetry')} and {code('full_lume')} stay put deliberately.
          Re-running is safe: a migrated ref has no structural finish left.
        </p>
        <p className="text-xs text-gray-500">
          Reads every reference brand by brand, so a run can stop at its time limit and hand back a cursor —
          press <strong>Continue</strong> until it reports complete.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={running} disabled={running} onClick={() => void run(true)}>
            {dryCursor ? 'Dry run (continue)' : 'Dry run'}
          </Button>
          <Button
            variant="primary"
            disabled={!canWrite}
            onClick={() => setConfirmOpen(true)}
            title={preview ? (canWrite ? undefined : 'Nothing to migrate') : 'Run a dry run first'}
          >
            {wetCursor ? 'Continue migration' : 'Migrate'}
          </Button>
          {!preview && !running && (
            <span className="self-center text-xs text-gray-500">Run a dry run to unlock the migration.</span>
          )}
        </div>

        {result && (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Migration'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              <Badge variant={result.complete ? 'success' : 'warning'}>
                {result.complete ? 'complete' : 'more to do'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <ResultStat label="Brands scanned" value={result.counts.brandsScanned} />
              <ResultStat label="Refs scanned" value={result.counts.refsScanned} />
              <ResultStat label="Refs with a finish" value={result.counts.refsWithFinish} />
              <ResultStat
                label={result.dryRun ? 'To migrate' : 'Migrated'}
                value={result.dryRun ? result.counts.toMigrate : result.counts.refsWritten}
              />
              <ResultStat label="Feature already present" value={result.counts.featureAlreadyPresent} />
            </div>

            {Object.keys(result.counts.byFinish).length > 0 && (
              <p className="text-xs text-gray-600">
                By finish:{' '}
                {Object.entries(result.counts.byFinish)
                  .map(([k, n]) => `${k} ${n}`)
                  .join(' · ')}
              </p>
            )}

            {!result.complete && result.lastBrandId && (
              <p className="text-xs text-amber-800">
                Stopped at its time limit after brand {code(result.lastBrandId)}. Press the same button again
                to carry on from there.
              </p>
            )}

            {result.sample.length > 0 && (
              <details>
                <summary className="cursor-pointer text-sm text-gray-700">
                  Sample ({result.sample.length} of {result.counts.toMigrate + result.counts.featureAlreadyPresent})
                </summary>
                <div className="mt-2 max-h-80 overflow-auto rounded border border-gray-200 bg-white">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">Ref</th>
                        <th className="px-2 py-1.5 font-medium">Finish</th>
                        <th className="px-2 py-1.5 font-medium">Features after</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {result.sample.map((a) => (
                        <tr key={a.path}>
                          <td className="px-2 py-1 font-mono">{refName(a.path)}</td>
                          <td className="px-2 py-1">{a.finish}</td>
                          <td className="px-2 py-1">{a.dialFeatures}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title="Migrate dial finishes?">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            From the last dry run: moves <strong>{preview?.counts.toMigrate ?? 0}</strong> ref
            {preview?.counts.toMigrate === 1 ? '' : 's'} to {code('dialFeatures')} and clears their{' '}
            {code('finish')}. Each write re-indexes the ref. Re-running is safe. Continue?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run(false);
              }}
            >
              Migrate
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: walk each manufacturer's `parentManufacturerId` chain and write
 * the resolved `manufacturerIdAncestors` array. Run once after deploying the
 * trigger, or re-run after a mid-chain re-parent if descendants' ancestor arrays
 * are stale. Idempotent and safe to re-run. Gated behind a confirm modal since
 * it sweeps the whole collection.
 */
function RecomputeManufacturerAncestorsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] =
    useState<RecomputeManufacturerAncestorsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await recomputeAllManufacturerAncestors();
      setResult(r);
      const errorCount = r.errors ?? 0;
      const summary =
        `Recompute complete: ${r.scanned} scanned, ` +
        `${r.updated} updated, ${r.skipped} skipped` +
        (errorCount ? `, ${errorCount} error(s)` : '');
      if (errorCount) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('recomputeAllManufacturerAncestors failed:', err);
      toast.error(
        err instanceof Error
          ? `Recompute failed: ${err.message}`
          : 'Recompute failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="recomputeManufacturerAncestors"
      title="Recompute Manufacturer Ancestors"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Walks each manufacturer's{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            parentManufacturerId
          </code>{' '}
          chain and writes the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            manufacturerIdAncestors
          </code>{' '}
          array. Run once after deploying the trigger, OR re-run after a mid-chain
          re-parent if descendants' ancestor arrays are stale.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Recomputing…' : 'Recompute Ancestors'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Recompute result
              </span>
              <Badge variant={result.errors ? 'warning' : 'success'}>
                done
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Manufacturers scanned" value={result.scanned} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Skipped" value={result.skipped} />
              <ResultStat
                label="Errors"
                value={result.errors ?? 0}
                danger={(result.errors ?? 0) > 0}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Recompute manufacturer ancestors?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will walk every manufacturer's{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              parentManufacturerId
            </code>{' '}
            chain and rebuild the{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              manufacturerIdAncestors
            </code>{' '}
            array on every manufacturer. Idempotent and safe to re-run. May take
            ~30s. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Recompute
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: re-derive each Shopify parent's display `reference` from its lead
 * variant SKU and (re)write the parent's `variantReferences` array (the
 * searchable union of all variant SKUs indexed on the parent's Algolia record).
 * Run once after deploying the lead-variant importer change + Algolia settings;
 * the importer maintains both on subsequent imports. Idempotent and safe to
 * re-run. Gated behind a confirm modal since it sweeps every brand's parents.
 */
function BackfillShopifyParentRefsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<BackfillShopifyParentRefsResult | null>(
    null,
  );

  const run = async () => {
    setRunning(true);
    try {
      const r = await backfillShopifyParentRefsAndVariants();
      setResult(r);
      const errorCount = r.errors?.length ?? 0;
      const summary =
        `Backfill complete: ${r.scanned} scanned, ` +
        `${r.updated} updated, ${r.skipped} skipped` +
        (errorCount ? `, ${errorCount} error(s)` : '');
      if (errorCount) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillShopifyParentRefsAndVariants failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);
  const errorCount = result?.errors?.length ?? 0;

  return (
    <CollapsibleSection
      sectionId="backfillShopifyParentRefs"
      title="Backfill Shopify Parent Refs"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Re-derives each Shopify parent's display{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            reference
          </code>{' '}
          from its lead variant SKU, and (re)writes the parent's{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            variantReferences
          </code>{' '}
          array (the searchable union of all variant SKUs). Run once after
          deploying the lead-variant importer change + pushing the Algolia
          settings. Idempotent and safe to re-run.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill Parent Refs'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant={errorCount ? 'warning' : 'success'}>done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Parents scanned" value={result.scanned} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat label="Skipped" value={result.skipped} />
              <ResultStat
                label="Errors"
                value={errorCount}
                danger={errorCount > 0}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill Shopify parent refs?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This sweeps every brand's Shopify parent references, re-deriving each
            parent's{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              reference
            </code>{' '}
            from its lead variant SKU and rewriting its{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              variantReferences
            </code>{' '}
            array. A parent's displayed ref may change (e.g. a handle-slug
            fallback becomes a real SKU). Idempotent and safe to re-run. May take
            a while on large catalogs. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Runaway backstop on the isVariant auto-resume loop. Each pass is a full 540s
 * of server work, so 100 passes is ~15 hours — never a real limit, purely a
 * guard against chaining forever if the server keeps reporting incomplete.
 */
const IS_VARIANT_MAX_PASSES = 100;

/** Cap on error strings retained across passes so a pathological run can't grow unbounded. */
const IS_VARIANT_MAX_ERRORS = 200;

/** Resume point returned by an incomplete pass — both fields go back verbatim. */
interface IsVariantCursor {
  cursorBrandId: string;
  cursorAfterReferenceId: string;
}

/** Running totals accumulated across the chained passes of one run. */
interface IsVariantTotals {
  passes: number;
  /**
   * Sum of each pass's `brandsScanned`. Not a distinct brand count: a brand
   * whose references straddle a pass boundary is counted once per pass it
   * appears in, hence "brand passes".
   */
  brandPasses: number;
  scanned: number;
  updated: number;
  skipped: number;
  errors: string[];
  /** Errors dropped once {@link IS_VARIANT_MAX_ERRORS} was reached. */
  errorsTruncated: number;
}

const EMPTY_IS_VARIANT_TOTALS: IsVariantTotals = {
  passes: 0,
  brandPasses: 0,
  scanned: 0,
  updated: 0,
  skipped: 0,
  errors: [],
  errorsTruncated: 0,
};

/** How a run ended. `capped` means the pass backstop tripped, not that work finished. */
type IsVariantRunStatus = 'complete' | 'stopped' | 'capped' | 'failed';

/**
 * One-shot maintenance tool: set the `isVariant` boolean on every existing watch
 * reference, derived from whether it carries a `parentReferenceId` (watchlock
 * PR #823). Idempotent and safe to re-run.
 *
 * Unlike the other backfills here, the callable is cursor-paged: it runs for up
 * to 540s and returns `complete: false` with a cursor when it runs out of
 * budget. This section chains those passes automatically — the whole point,
 * since ~110k references need several invocations and hand-chaining cursors
 * through the Firebase console is exactly the pain this replaces. Totals
 * accumulate live across passes; Stop halts cleanly after the in-flight pass and
 * keeps the cursor so Resume can pick up from the same point.
 */
function BackfillIsVariantSection() {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [brandId, setBrandId] = useState<string>(''); // '' = all brands
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [totals, setTotals] = useState<IsVariantTotals | null>(null);
  const [status, setStatus] = useState<IsVariantRunStatus | null>(null);
  const [lastDryRun, setLastDryRun] = useState(false);
  const [cursor, setCursor] = useState<IsVariantCursor | null>(null);
  // Ref drives the loop (read between passes, never stale); state drives the UI.
  const stopRef = useRef(false);
  const [stopRequested, setStopRequested] = useState(false);

  useEffect(() => {
    let active = true;
    getWatchBrands()
      .then((all) => {
        if (!active) return;
        all.sort((a, b) =>
          (a.displayName || a.name).localeCompare(b.displayName || b.name),
        );
        setBrands(all);
      })
      .catch((err) => {
        console.error('Failed to load brands for isVariant backfill tool:', err);
      });
    return () => {
      active = false;
    };
  }, []);

  // The pass chain lives in this tab — closing it mid-run abandons the sweep
  // (the cursor is only in component state), so warn before unload.
  useEffect(() => {
    if (!running) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [running]);

  const selectedBrandName = brandId
    ? brands.find((b) => b.id === brandId)?.displayName ||
      brands.find((b) => b.id === brandId)?.name ||
      brandId
    : 'all brands';

  const run = async (dryRun: boolean, resumeFrom: IsVariantCursor | null) => {
    setRunning(dryRun ? 'dry' : 'write');
    setLastDryRun(dryRun);
    setStatus(null);
    setErrorsExpanded(false);
    stopRef.current = false;
    setStopRequested(false);

    // Resuming keeps the totals already on screen so the numbers stay cumulative
    // across the interruption; a fresh run starts from zero.
    let acc: IsVariantTotals =
      resumeFrom && totals ? totals : { ...EMPTY_IS_VARIANT_TOTALS, errors: [] };
    let next: IsVariantCursor | null = resumeFrom;
    if (!resumeFrom) {
      setTotals(acc);
      setCursor(null);
    }
    let outcome: IsVariantRunStatus = 'complete';

    try {
      for (let pass = 1; pass <= IS_VARIANT_MAX_PASSES; pass++) {
        const params: BackfillIsVariantParams = {
          dryRun,
          ...(brandId ? { brandId } : {}),
          ...(next ?? {}),
        };
        console.info('[backfillIsVariant] pass', pass, 'request', params);
        const r = await backfillIsVariant(params);
        console.info('[backfillIsVariant] pass', pass, 'result', r);

        const incoming = r.errors ?? [];
        const room = Math.max(0, IS_VARIANT_MAX_ERRORS - acc.errors.length);
        acc = {
          passes: acc.passes + 1,
          brandPasses: acc.brandPasses + (r.brandsScanned ?? 0),
          scanned: acc.scanned + (r.scanned ?? 0),
          updated: acc.updated + (r.updated ?? 0),
          skipped: acc.skipped + (r.skipped ?? 0),
          errors: [...acc.errors, ...incoming.slice(0, room)],
          errorsTruncated:
            acc.errorsTruncated + Math.max(0, incoming.length - room),
        };
        setTotals(acc);

        if (r.complete) {
          next = null;
          setCursor(null);
          outcome = 'complete';
          break;
        }

        // Refuse to chain unless the server handed back a cursor that actually
        // moved — otherwise an incomplete-with-no-progress reply loops forever.
        const nextBrandId = r.cursorBrandId;
        const nextAfterId = r.cursorAfterReferenceId;
        if (
          !nextBrandId ||
          !nextAfterId ||
          (nextBrandId === next?.cursorBrandId &&
            nextAfterId === next?.cursorAfterReferenceId)
        ) {
          throw new Error(
            'Run reported incomplete but returned no forward cursor — stopping ' +
              'rather than re-running the same page.',
          );
        }
        next = {
          cursorBrandId: nextBrandId,
          cursorAfterReferenceId: nextAfterId,
        };
        setCursor(next);

        if (stopRef.current) {
          outcome = 'stopped';
          break;
        }
        if (pass === IS_VARIANT_MAX_PASSES) outcome = 'capped';
      }

      setStatus(outcome);
      const verb = dryRun ? 'Dry run' : 'Backfill';
      const ending =
        outcome === 'complete'
          ? 'complete'
          : outcome === 'stopped'
            ? 'stopped (cursor kept)'
            : `stopped at the ${IS_VARIANT_MAX_PASSES}-pass cap`;
      const summary =
        `${verb} ${ending} (${selectedBrandName}): ${acc.scanned} scanned, ` +
        `${dryRun ? 'would update' : 'updated'} ${acc.updated}, ` +
        `${acc.skipped} skipped` +
        (acc.errors.length ? `, ${acc.errors.length} error(s)` : '');
      if (outcome !== 'complete' || acc.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillIsVariant failed:', err);
      setStatus('failed');
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
      stopRef.current = false;
      setStopRequested(false);
    }
  };

  const requestStop = () => {
    stopRef.current = true;
    setStopRequested(true);
  };

  const reset = () => {
    setTotals(null);
    setStatus(null);
    setCursor(null);
    setErrorsExpanded(false);
  };

  const canResume = !running && !!cursor && status !== 'complete';
  const errorCount = totals?.errors.length ?? 0;
  const statusBadge: Record<
    IsVariantRunStatus,
    { label: string; variant: 'success' | 'warning' | 'danger' }
  > = {
    complete: { label: 'complete', variant: 'success' },
    stopped: { label: 'stopped', variant: 'warning' },
    capped: { label: 'pass cap hit', variant: 'warning' },
    failed: { label: 'failed', variant: 'danger' },
  };

  return (
    <CollapsibleSection sectionId="backfillIsVariant" title="Backfill isVariant">
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Sets the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            isVariant
          </code>{' '}
          boolean on every existing reference, derived from whether it has a{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            parentReferenceId
          </code>
          . One-time migration for references written before the field existed —
          the importer maintains it from here on. Idempotent and safe to re-run.
        </p>
        <p className="text-sm text-gray-600">
          The callable runs in 540s passes and hands back a cursor when it runs
          out of budget; this tool chains those passes automatically until the
          sweep completes. Keep this tab open while it runs — the chain lives
          here, not on the server. Scope to a single brand to validate before
          sweeping everything.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Brand</label>
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            disabled={running !== null}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.displayName || b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => void run(true, null)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
          {running !== null && (
            <Button
              variant="danger"
              disabled={stopRequested}
              onClick={requestStop}
            >
              {stopRequested ? 'Stopping…' : 'Stop'}
            </Button>
          )}
          {canResume && cursor && (
            <Button
              variant="primary"
              onClick={() => void run(lastDryRun, cursor)}
            >
              Resume from cursor
            </Button>
          )}
          {totals && running === null && (
            <Button variant="ghost" onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {running !== null && totals && (
          <div className="text-sm text-gray-700 border border-blue-200 bg-blue-50 rounded-lg px-3 py-2">
            Pass {totals.passes + 1} · {totals.scanned.toLocaleString()} scanned
            · {totals.updated.toLocaleString()}{' '}
            {lastDryRun ? 'would update' : 'updated'} —{' '}
            {stopRequested ? 'stopping after this pass…' : 'running…'}
          </div>
        )}

        {totals && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900">
                {lastDryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={lastDryRun ? 'info' : 'success'}>
                {lastDryRun ? 'no writes' : 'written'}
              </Badge>
              {status && (
                <Badge variant={statusBadge[status].variant}>
                  {statusBadge[status].label}
                </Badge>
              )}
              <span className="text-xs text-gray-500">{selectedBrandName}</span>
              <span className="text-xs text-gray-500">
                {totals.passes} pass{totals.passes === 1 ? '' : 'es'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Brand passes" value={totals.brandPasses} />
              <ResultStat label="Scanned" value={totals.scanned} />
              <ResultStat
                label={lastDryRun ? 'Would update' : 'Updated'}
                value={totals.updated}
              />
              <ResultStat label="Skipped" value={totals.skipped} />
              <ResultStat
                label="Errors"
                value={errorCount + totals.errorsTruncated}
                danger={errorCount + totals.errorsTruncated > 0}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              &ldquo;Brand passes&rdquo; sums each pass&apos;s brand count, so a
              brand whose references span a pass boundary is counted once per
              pass — it is not a distinct brand total. Scanned / updated /
              skipped are per-document and do not overlap.
            </p>

            {status !== 'complete' && cursor && (
              <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-800 font-medium">
                  Sweep did not finish. Resume picks up from this cursor:
                </p>
                <div className="mt-1 space-y-0.5">
                  <code className="block text-xs font-mono text-gray-700 break-all">
                    cursorBrandId: {cursor.cursorBrandId}
                  </code>
                  <code className="block text-xs font-mono text-gray-700 break-all">
                    cursorAfterReferenceId: {cursor.cursorAfterReferenceId}
                  </code>
                </div>
              </div>
            )}

            {errorCount > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errorCount} error{errorCount === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <ul className="divide-y divide-gray-100">
                        {totals.errors.slice(0, 100).map((e, i) => (
                          <li
                            key={`${i}-${e}`}
                            className="px-3 py-1.5 text-xs text-red-600 break-all"
                          >
                            {e}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {(errorCount > 100 || totals.errorsTruncated > 0) && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first {Math.min(errorCount, 100)} of{' '}
                        {errorCount}
                        {totals.errorsTruncated > 0
                          ? ` retained (${totals.errorsTruncated} more dropped past the ${IS_VARIANT_MAX_ERRORS}-error cap — see console for the full log)`
                          : ''}
                        .
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Run isVariant backfill (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This writes{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              isVariant
            </code>{' '}
            to every reference for{' '}
            <span className="font-semibold">{selectedBrandName}</span>, chaining
            540s passes until the sweep completes. Each write touches{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              updatedAt
            </code>
            , which re-fires the Algolia reindex trigger. Idempotent and safe to
            re-run. Leave this tab open — you can Stop between passes. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false, null);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push the `watchkeeper_calibres` Algolia index settings and
 * (re)create its sort replicas. Run after the callable's settings change or to
 * repair a misconfigured index. Gated behind a confirm modal since it overwrites
 * live index settings.
 */
function PushCalibreIndexSettingsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] =
    useState<PushAlgoliaCalibreIndexSettingsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await pushAlgoliaCalibreIndexSettings();
      setResult(r);
      toast.success(
        `Index settings pushed: ${r.replicasAdded.length} replica(s) added, ` +
          `${r.replicas.length} total`,
      );
    } catch (err) {
      console.error('pushAlgoliaCalibreIndexSettings failed:', err);
      toast.error(
        err instanceof Error
          ? `Push failed: ${err.message}`
          : 'Push failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="pushCalibreIndexSettings"
      title="Push Calibre Index Settings"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_calibres
          </code>{' '}
          index settings + sort replicas (
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            _name_asc
          </code>
          ,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            _name_desc
          </code>
          ,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            _refCount_desc
          </code>
          ) to Algolia. Run after the callable's settings change or to repair a
          misconfigured index.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Pushing…' : 'Push Settings'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Push result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Replicas added" value={result.replicasAdded.length} />
              <ResultStat label="Replicas total" value={result.replicas.length} />
              <ResultStat label="Updated" value={result.updated ? 'yes' : 'no'} />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Index:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.index}
              </code>
            </div>
            {result.replicas.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">Replicas: </span>
                {result.replicas.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Push calibre index settings?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will overwrite the{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              watchkeeper_calibres
            </code>{' '}
            Algolia index settings and (re)create its sort replicas. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Push Settings
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push the `watchkeeper_calibre_families` Algolia index settings
 * and (re)create its sort replicas. Run after the callable's settings change or
 * to repair a misconfigured index. Idempotent — no confirm modal required.
 * Calibre Families Phase 2.
 */
function PushCalibreFamilyIndexSettingsSection() {
  const [running, setRunning] = useState(false);
  const [result, setResult] =
    useState<PushAlgoliaCalibreFamilyIndexSettingsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await pushAlgoliaCalibreFamilyIndexSettings();
      setResult(r);
      toast.success(
        r.updated
          ? `Index settings pushed (${r.index})`
          : `Index settings unchanged (${r.index})`,
      );
    } catch (err) {
      console.error('pushAlgoliaCalibreFamilyIndexSettings failed:', err);
      toast.error(
        err instanceof Error
          ? `Push failed: ${err.message}`
          : 'Push failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="pushCalibreFamilyIndexSettings"
      title="Push Calibre Family Index Settings"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_calibre_families
          </code>{' '}
          index settings (searchable attributes, filter facets, custom ranking) to
          Algolia. Idempotent. Run after the callable&apos;s settings change or to
          repair a misconfigured index.
        </p>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => void run()}
          >
            {running ? 'Pushing…' : 'Push Settings'}
          </Button>
          {result && (
            <Button
              type="button"
              variant="ghost"
              disabled={running}
              onClick={reset}
            >
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Push result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            {/*
              The callable returns only `{ index, updated }` — the family index
              declares no sort replicas (unlike watchkeeper_calibres). Read
              defensively with `?.`/`??` so a future server shape change shows a
              wrong value instead of throwing mid-render and blanking the page
              (there is no error boundary above this tree).
            */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <ResultStat
                label="Updated"
                value={result?.updated ? 'yes' : 'no'}
              />
              <ResultStat label="Index" value={result?.index ?? '—'} />
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push the `watchkeeper_manufacturers` Algolia index settings.
 * Run after the callable's settings change or to repair a misconfigured index.
 * Idempotent — no confirm modal required, same as the calibre-family push.
 * Manufacturers → Algolia Phase 4 (watchlock #866).
 */
function PushManufacturerIndexSettingsSection() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<EntityIndexSettingsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await callPushAlgoliaManufacturerIndexSettings();
      setResult(r);
      toast.success(
        r.updated
          ? `Index settings pushed (${r.index})`
          : `Index settings unchanged (${r.index})`,
      );
    } catch (err) {
      console.error('pushAlgoliaManufacturerIndexSettings failed:', err);
      toast.error(
        err instanceof Error
          ? `Push failed: ${err.message}`
          : 'Push failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="pushManufacturerIndexSettings"
      title="Push Manufacturer Index Settings"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_manufacturers
          </code>{' '}
          index settings (searchable attributes, filter facets, custom ranking)
          to Algolia. Idempotent. Run after the callable&apos;s settings change or
          to repair a misconfigured index.
        </p>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => void run()}
          >
            {running ? 'Pushing…' : 'Push Settings'}
          </Button>
          {result && (
            <Button
              type="button"
              variant="ghost"
              disabled={running}
              onClick={reset}
            >
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Push result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            {/*
              The callable returns only `{ index, updated }` — the manufacturer
              index declares no sort replicas (unlike watchkeeper_calibres). Read
              defensively with `?.`/`??` so a future server shape change shows a
              wrong value instead of throwing mid-render and blanking the page
              (there is no error boundary above this tree).
            */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <ResultStat
                label="Updated"
                value={result?.updated ? 'yes' : 'no'}
              />
              <ResultStat label="Index" value={result?.index ?? '—'} />
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push every `movement_manufacturers` doc to the
 * `watchkeeper_manufacturers` Algolia index. Use after a field-shape change to
 * refresh existing records. Long-running, so gated behind a confirm modal.
 * Manufacturers → Algolia Phase 4 (watchlock #866).
 */
function BackfillManufacturersToAlgoliaSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] = useState<EntityIndexBackfillResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await callBackfillManufacturersToAlgolia({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r.scanned} scanned, ${r.indexed} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillManufacturersToAlgolia failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillManufacturersToAlgolia"
      title="Backfill Manufacturers to Algolia"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes every{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            movement_manufacturers
          </code>{' '}
          doc to the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_manufacturers
          </code>{' '}
          Algolia index. Use after a field shape change to refresh existing
          records (e.g. when new indexed fields are added).
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Manufacturers scanned" value={result.scanned} />
              <ResultStat label="Indexed" value={result.indexed} />
              <ResultStat
                label="Full rebuild"
                value={result.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Index:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.index}
              </code>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill manufacturers to Algolia?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every manufacturer and push it to Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push every `parent_organizations` doc to the
 * `watchkeeper_parent_organizations` Algolia index (watchlock #1086).
 *
 * Twenty-seven documents, so this finishes in seconds — but it is still gated
 * behind the same confirm modal as its siblings, because `fullRebuild` swaps
 * the index atomically and that is not a click to make by accident.
 *
 * Run `pushAlgoliaParentOrgIndexSettings` BEFORE the first backfill, or the
 * records land in an index with no facets declared and every filter comes back
 * empty. That callable has no button here (neither do the calibre-family,
 * winder or electronic-module settings pushes) — it is a
 * `firebase functions:shell` one-liner, run once at deploy time.
 */
function BackfillParentOrgsToAlgoliaSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] = useState<EntityIndexBackfillResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await callBackfillParentOrgsToAlgolia({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r.scanned} scanned, ${r.indexed} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillParentOrgsToAlgolia failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillParentOrgsToAlgolia"
      title="Backfill Parent Organizations to Algolia"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes every{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            parent_organizations
          </code>{' '}
          doc to the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_parent_organizations
          </code>{' '}
          Algolia index. Use after a field shape change to refresh existing
          records (e.g. when new indexed fields are added).
        </p>

        <p className="text-xs text-gray-500">
          The index record carries the organization&rsquo;s own scalars only —
          history, ownership events and description are stripped to stay inside
          Algolia&rsquo;s 10KB record limit. Editors must load the Firestore
          document, never seed a form from a search hit.
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Organizations scanned" value={result.scanned} />
              <ResultStat label="Indexed" value={result.indexed} />
              <ResultStat
                label="Full rebuild"
                value={result.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Index:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.index}
              </code>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill parent organizations to Algolia?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every parent organization and push it to Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push every `custom_calibres` doc to the `watchkeeper_calibres`
 * Algolia index. Use after a field-shape change to refresh existing records.
 * Long-running, so gated behind a confirm modal.
 */
function BackfillCalibresToAlgoliaSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] = useState<EntityIndexBackfillResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await callBackfillCalibresToAlgolia({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r.scanned} scanned, ${r.indexed} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillCalibresToAlgolia failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillCalibresToAlgolia"
      title="Backfill Calibres to Algolia"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes every{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            custom_calibres
          </code>{' '}
          doc to the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_calibres
          </code>{' '}
          Algolia index. Use after a field shape change to refresh existing
          records (e.g. when new indexed fields are added).
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Calibres scanned" value={result.scanned} />
              <ResultStat label="Indexed" value={result.indexed} />
              <ResultStat
                label="Full rebuild"
                value={result.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Index:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.index}
              </code>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill calibres to Algolia?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every calibre and push it to Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push every calibre-family doc to the
 * `watchkeeper_calibre_families` Algolia index. Use after a field-shape change
 * to refresh existing records. Long-running, so gated behind a confirm modal.
 * Calibre Families Phase 2.
 */
function BackfillCalibreFamiliesToAlgoliaSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] = useState<EntityIndexBackfillResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await callBackfillCalibreFamiliesToAlgolia({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r.scanned} scanned, ${r.indexed} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillCalibreFamiliesToAlgolia failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillCalibreFamiliesToAlgolia"
      title="Backfill Calibre Families to Algolia"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes every calibre-family doc to the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_calibre_families
          </code>{' '}
          Algolia index. Use after a field shape change to refresh existing
          records (e.g. when new indexed fields are added).
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Families scanned" value={result.scanned} />
              <ResultStat label="Indexed" value={result.indexed} />
              <ResultStat
                label="Full rebuild"
                value={result.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Index:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.index}
              </code>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill calibre families to Algolia?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every calibre family and push it to Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push the `watchkeeper_electronic_modules` Algolia index settings
 * (searchable attributes, filter facets, custom ranking, any sort replicas) to
 * Algolia. Idempotent. Run after the callable's settings change or to repair a
 * misconfigured index. Gated behind a confirm modal since it overwrites live
 * index settings. Electronic Modules Phase 2.
 */
function PushElectronicModuleIndexSettingsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] =
    useState<PushAlgoliaElectronicModuleIndexSettingsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await pushAlgoliaElectronicModuleIndexSettings();
      setResult(r);
      toast.success(
        r?.updated
          ? `Index settings pushed${r.index ? ` (${r.index})` : ''}`
          : `Index settings unchanged${r?.index ? ` (${r.index})` : ''}`,
      );
    } catch (err) {
      console.error('pushAlgoliaElectronicModuleIndexSettings failed:', err);
      toast.error(
        err instanceof Error
          ? `Push failed: ${err.message}`
          : 'Push failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="pushElectronicModuleIndexSettings"
      title="Push Electronic Module Index Settings"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Declares the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_electronic_modules
          </code>{' '}
          index settings (searchable attributes, filter facets, custom ranking)
          on Algolia. Idempotent. Run after the callable&apos;s settings change
          or to repair a misconfigured index.
        </p>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Pushing…' : 'Push Settings'}
          </Button>
          {result && (
            <Button
              type="button"
              variant="ghost"
              disabled={running}
              onClick={reset}
            >
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Push result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            {/*
              Read defensively: the watchlock callable's exact return shape is
              not pinned in this repo. `replicas` may be absent (the index may
              declare no sort replicas), so only render that row when present.
            */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Updated" value={result?.updated ? 'yes' : 'no'} />
              <ResultStat label="Index" value={result?.index ?? '—'} />
              {Array.isArray(result?.replicas) && (
                <ResultStat
                  label="Replicas total"
                  value={result.replicas.length}
                />
              )}
            </div>
            {Array.isArray(result?.replicas) && result.replicas.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">Replicas: </span>
                {result.replicas.join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Push electronic module index settings?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will overwrite the{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              watchkeeper_electronic_modules
            </code>{' '}
            Algolia index settings. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Push Settings
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push every electronic-module doc to the
 * `watchkeeper_electronic_modules` Algolia index. Use after a field-shape change
 * to refresh existing records. Long-running, so gated behind a confirm modal.
 * Electronic Modules Phase 2.
 */
function BackfillElectronicModulesToAlgoliaSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] =
    useState<BackfillElectronicModulesToAlgoliaResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await backfillElectronicModulesToAlgolia({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r?.scanned ?? 0} scanned, ${r?.indexed ?? 0} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillElectronicModulesToAlgolia failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillElectronicModulesToAlgolia"
      title="Backfill Electronic Modules to Algolia"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes every electronic-module doc to the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_electronic_modules
          </code>{' '}
          Algolia index. Use after a field shape change to refresh existing
          records (e.g. when new indexed fields are added).
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        {fullRebuild && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-semibold">Full rebuild is destructive: </span>
            the index is atomically swapped and Algolia records for docs no
            longer in Firestore are <span className="font-medium">removed</span>.
            Only run this after a deliberate schema change.
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Modules scanned" value={result?.scanned ?? 0} />
              <ResultStat label="Indexed" value={result?.indexed ?? 0} />
              <ResultStat
                label="Full rebuild"
                value={result?.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            {result?.index && (
              <div className="mt-2 text-xs text-gray-500">
                Index:{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                  {result.index}
                </code>
              </div>
            )}
            {Array.isArray(result?.sampleIds) && result.sampleIds.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">Sample IDs: </span>
                <span className="font-mono">
                  {result.sampleIds.slice(0, 20).join(', ')}
                </span>
                {result.sampleIds.length > 20 &&
                  ` … (+${result.sampleIds.length - 20} more)`}
              </div>
            )}
            {result?.auditLogId && (
              <div className="mt-2 text-xs text-gray-500">
                Audit log:{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                  {result.auditLogId}
                </code>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill electronic modules to Algolia?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every electronic module and push it to Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push the `watchkeeper_winders` Algolia index settings (searchable
 * attributes, filter facets, custom ranking, `paginationLimitedTo`). Idempotent.
 * Gated behind a confirm modal since it overwrites live index settings.
 *
 * This is the FIRST thing to run on a new deploy — before the backfill. Pushing
 * settings after records are indexed makes Algolia rebuild the index to apply
 * them; pushing first means the backfill lands into an index already configured.
 */
function PushWinderIndexSettingsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] =
    useState<PushAlgoliaWinderIndexSettingsResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await pushAlgoliaWinderIndexSettings();
      setResult(r);
      toast.success(
        r?.updated
          ? `Index settings pushed${r.index ? ` (${r.index})` : ''}`
          : `Index settings unchanged${r?.index ? ` (${r.index})` : ''}`,
      );
    } catch (err) {
      console.error('pushAlgoliaWinderIndexSettings failed:', err);
      toast.error(
        err instanceof Error
          ? `Push failed: ${err.message}`
          : 'Push failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="pushWinderIndexSettings"
      title="Push Winder Index Settings"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Declares the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_winders
          </code>{' '}
          index settings (searchable attributes, filter facets, custom ranking,
          pagination ceiling) on Algolia. Idempotent. Run this{' '}
          <span className="font-medium">before</span> the backfill below on a
          fresh index, and again after the callable&apos;s settings change.
        </p>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Pushing…' : 'Push Settings'}
          </Button>
          {result && (
            <Button
              type="button"
              variant="ghost"
              disabled={running}
              onClick={reset}
            >
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Push result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            {/*
              No replicas row, unlike the module/calibre pushes: the winders
              index is deliberately replica-free — the Curated Winders listing
              sorts client-side, and its sort-worthy fields (model, make) are
              strings, which Algolia's customRanking cannot order.
            */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <ResultStat label="Updated" value={result?.updated ? 'yes' : 'no'} />
              <ResultStat label="Index" value={result?.index ?? '—'} />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Push winder index settings?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This overwrites the live{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              watchkeeper_winders
            </code>{' '}
            index settings with the declaration in the callable. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Push Settings
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: push every winder doc to the `watchkeeper_winders` Algolia index.
 * Use to backfill a fresh index, or after a field-shape change to refresh
 * existing records. Long-running, so gated behind a confirm modal.
 *
 * ONE invocation is the whole backfill. The callable keyset-paginates the
 * `winders` collection to completion server-side and returns final totals, so
 * there is no client-driven "call again from the last id" loop here — the
 * pagination that would need driving does not cross the wire.
 */
function BackfillWindersToAlgoliaSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] = useState<BackfillWindersToAlgoliaResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await backfillWindersToAlgolia({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r?.scanned ?? 0} scanned, ${r?.indexed ?? 0} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillWindersToAlgolia failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillWindersToAlgolia"
      title="Backfill Winders to Algolia"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Pushes every winder doc to the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watchkeeper_winders
          </code>{' '}
          Algolia index — the source the Curated Winders tab reads. Run once
          after the index is created (existing winders pre-date the sync
          trigger), and again after a field-shape change. New and edited winders
          index themselves via{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            syncWinderToAlgolia
          </code>
          , so this is not needed for day-to-day edits.
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        {fullRebuild && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span className="font-semibold">Full rebuild is destructive: </span>
            the index is atomically swapped and Algolia records for docs no
            longer in Firestore are <span className="font-medium">removed</span>.
            Only run this after a deliberate schema change.
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Winders scanned" value={result?.scanned ?? 0} />
              <ResultStat label="Indexed" value={result?.indexed ?? 0} />
              <ResultStat
                label="Full rebuild"
                value={result?.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            {result?.index && (
              <div className="mt-2 text-xs text-gray-500">
                Index:{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                  {result.index}
                </code>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill winders to Algolia?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every winder and push it to Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: recompute the denormalised `referenceCount` on every
 * electronic-module doc from the live `collectionGroup('references')` count.
 * Useful after a bulk import or if counts drift; each updated module re-indexes
 * to Algolia automatically. Gated behind a confirm modal since it sweeps the
 * whole collection. Electronic Modules Phase 2.
 */
function RecomputeElectronicModuleReferenceCountsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] =
    useState<RecomputeElectronicModuleReferenceCountsResult | null>(null);

  const errors = result?.errors ?? [];

  const run = async () => {
    setRunning(true);
    try {
      const r = await recomputeAllElectronicModuleReferenceCounts();
      setResult(r);
      setErrorsExpanded(false);
      const errCount = r?.errors?.length ?? 0;
      const summary =
        `Recompute complete: ${r?.scanned ?? 0} scanned, ` +
        `${r?.updated ?? 0} updated, ${r?.reindexed ?? 0} re-indexed` +
        (r?.skipped ? `, ${r.skipped} skipped` : '') +
        (errCount ? `, ${errCount} error(s)` : '');
      if (errCount) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('recomputeAllElectronicModuleReferenceCounts failed:', err);
      toast.error(
        err instanceof Error
          ? `Recompute failed: ${err.message}`
          : 'Recompute failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setResult(null);
    setErrorsExpanded(false);
  };

  return (
    <CollapsibleSection
      sectionId="recomputeElectronicModuleReferenceCounts"
      title="Recompute Electronic Module Reference Counts"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Recalculates the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            referenceCount
          </code>{' '}
          field on every electronic module from the live reference
          collection-group count. Use this after a bulk import, or if counts
          drift. Each updated module re-indexes to Algolia automatically.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Recomputing…' : 'Recompute Counts'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Recompute result
              </span>
              <Badge variant={errors.length ? 'warning' : 'success'}>done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Modules scanned" value={result?.scanned ?? 0} />
              <ResultStat label="Updated" value={result?.updated ?? 0} />
              <ResultStat label="Re-indexed" value={result?.reindexed ?? 0} />
              <ResultStat
                label="Errors"
                value={errors.length}
                danger={errors.length > 0}
              />
            </div>

            {Array.isArray(result?.sampleIds) && result.sampleIds.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">Sample IDs: </span>
                <span className="font-mono">
                  {result.sampleIds.slice(0, 20).join(', ')}
                </span>
                {result.sampleIds.length > 20 &&
                  ` … (+${result.sampleIds.length - 20} more)`}
              </div>
            )}
            {result?.auditLogId && (
              <div className="mt-2 text-xs text-gray-500">
                Audit log:{' '}
                <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                  {result.auditLogId}
                </code>
              </div>
            )}

            {errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.length} error{errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Module ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {errors
                            .slice(0, 100)
                            .map(
                              (
                                e: RecomputeElectronicModuleReferenceCountsError,
                                i,
                              ) => (
                                <tr key={`${e.moduleId}/${i}`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {e.moduleId}
                                  </td>
                                  <td className="px-3 py-1.5 text-red-600 align-top">
                                    {e.error}
                                  </td>
                                </tr>
                              ),
                            )}
                        </tbody>
                      </table>
                    </div>
                    {errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Recompute electronic module reference counts?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will scan the reference collection group and update reference
            counts on every electronic module. May take a minute. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Recompute
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

function BackfillWatchReferenceFamilyIdsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fullRebuild, setFullRebuild] = useState(false);
  const [result, setResult] = useState<EntityIndexBackfillResult | null>(null);

  const run = async () => {
    setRunning(true);
    try {
      const r = await callBackfillWatchReferenceFamilyIds({ fullRebuild });
      setResult(r);
      toast.success(
        `Backfill complete: ${r.scanned} scanned, ${r.indexed} indexed` +
          (fullRebuild ? ' (full rebuild — atomic swap + orphan cleanup)' : ''),
      );
    } catch (err) {
      console.error('backfillWatchReferenceFamilyIds failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const reset = () => setResult(null);

  return (
    <CollapsibleSection
      sectionId="backfillWatchReferenceFamilyIds"
      title="Backfill Watch Reference Family IDs"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Backfills the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">familyId</code>{' '}
          attribute onto every watch-reference Algolia record so the
          family-filter on refs works. Use after the family-filter Phase 1
          functions deploy, or whenever the family wiring changes.
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={fullRebuild}
            disabled={running}
            onChange={(e) => setFullRebuild(e.target.checked)}
          />
          <span>
            <span className="font-medium">
              Full rebuild (atomic swap + orphan cleanup)
            </span>
            <span className="block text-xs text-gray-500">
              Use after a major schema change. Atomically swaps the index;
              preserves settings/synonyms; removes Algolia records for docs no
              longer in Firestore.
            </span>
          </span>
        </label>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmOpen(true)}
          >
            {running ? 'Backfilling…' : 'Backfill'}
          </Button>
          {result && (
            <Button variant="ghost" disabled={running} onClick={reset}>
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Backfill result
              </span>
              <Badge variant="success">done</Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="References scanned" value={result.scanned} />
              <ResultStat label="Indexed" value={result.indexed} />
              <ResultStat
                label="Full rebuild"
                value={result.fullRebuild ? 'yes' : 'no'}
              />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Index:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.index}
              </code>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Backfill watch reference family IDs?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will iterate every watch reference and push its familyId to
            Algolia.
            {fullRebuild
              ? ' Running in full-rebuild mode: the index is atomically swapped and Algolia records for docs no longer in Firestore are removed (settings/synonyms preserved).'
              : ' Running incrementally: existing records are upserted by objectID.'}{' '}
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Backfill
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Maintenance: scan watch references for image URLs still pointing at per-user
 * Storage paths and copy those files into ref-owned storage, rewriting the
 * reference docs. Dry-run (default) reports counts only; the real write run is
 * gated behind a confirm modal. Each run writes an audit log to
 * `image_backfill_runs/{autoId}`. Counterpart to the approval-time fix in
 * watchlock PR #725 — patches legacy refs merged before that landed.
 */
function BackfillReferenceImagesFromUserStorageSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [brandId, setBrandId] = useState('');
  const [limit, setLimit] = useState('');
  const [result, setResult] =
    useState<BackfillReferenceImagesFromUserStorageResult | null>(null);

  const run = async (asDryRun: boolean) => {
    setRunning(asDryRun ? 'dry' : 'write');
    try {
      const parsedLimit = limit.trim() ? Number(limit.trim()) : undefined;
      const r = await callBackfillReferenceImagesFromUserStorage({
        dryRun: asDryRun,
        brandId: brandId.trim() || undefined,
        limit:
          parsedLimit != null && Number.isFinite(parsedLimit)
            ? parsedLimit
            : undefined,
      });
      setResult(r);
      const summary =
        `${r.dryRun ? 'Dry run' : 'Backfill'} complete: ` +
        `${r.refsScanned} scanned, ${r.refsAffected} affected, ` +
        `${r.dryRun ? 'would copy' : 'copied'} ${r.imagesCopied} image(s)` +
        (r.imagesOrphaned ? `, ${r.imagesOrphaned} orphaned` : '');
      if (r.imagesOrphaned) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillReferenceImagesFromUserStorage failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  const reset = () => {
    setResult(null);
    setDryRun(true);
    setBrandId('');
    setLimit('');
  };

  return (
    <CollapsibleSection
      sectionId="backfillReferenceImagesFromUserStorage"
      title="Backfill Reference Images from User Storage"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Scans watch references for image URLs still pointing at per-user
          Storage paths and copies those files into ref-owned storage, rewriting
          the reference docs to the new URLs. Run a dry run first to see counts
          without writing. Each run writes an audit log to{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            image_backfill_runs
          </code>
          .
        </p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={dryRun}
            disabled={running !== null}
            onChange={(e) => setDryRun(e.target.checked)}
          />
          <span>
            <span className="font-medium">Dry run</span>
            <span className="block text-xs text-gray-500">
              Scan &amp; report only — no Storage copies or document writes.
            </span>
          </span>
        </label>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm font-medium text-gray-700 sm:w-20">
            Brand ID
          </label>
          <input
            type="text"
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            disabled={running !== null}
            placeholder="Leave empty for all brands"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <label className="text-sm font-medium text-gray-700 sm:w-20">
            Limit
          </label>
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            disabled={running !== null}
            placeholder="Leave empty for unlimited"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:opacity-50"
          />
        </div>

        {!dryRun && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Without dry-run, this will copy files in Cloud Storage and update
            reference documents. Run dry-run first to see what would change.
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running !== null}
            disabled={running !== null}
            onClick={() => {
              if (dryRun) {
                void run(true);
              } else {
                setConfirmWrite(true);
              }
            }}
          >
            {running !== null ? 'Running…' : 'Run'}
          </Button>
          <Button
            variant="ghost"
            disabled={running !== null}
            onClick={reset}
          >
            Reset
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Refs scanned" value={result.refsScanned} />
              <ResultStat label="Refs affected" value={result.refsAffected} />
              <ResultStat label="Images affected" value={result.imagesAffected} />
              <ResultStat label="Images copied" value={result.imagesCopied} />
              <ResultStat
                label="Images orphaned"
                value={result.imagesOrphaned}
                danger={result.imagesOrphaned > 0}
              />
              <ResultStat label="Refs updated" value={result.refsUpdated} />
            </div>

            {result.sampleRefIds.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  Sample ref IDs (first 5)
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {result.sampleRefIds.slice(0, 5).map((id) => (
                    <li key={id} className="text-xs text-gray-700">
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                        {id}
                      </code>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.sampleOrphanedPaths.length > 0 && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  Sample orphaned paths (first 5)
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {result.sampleOrphanedPaths.slice(0, 5).map((p) => (
                    <li key={p} className="text-xs text-gray-700 break-all">
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                        {p}
                      </code>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 text-xs text-gray-500">
              Log ID:{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">
                {result.logId}
              </code>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Backfill reference images (write)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This will copy files in Cloud Storage and update reference documents
            {brandId.trim() ? ` for brand ${brandId.trim()}` : ' across all brands'}
            {limit.trim() ? ` (limit ${limit.trim()})` : ''}. This is not a dry
            run. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * One-shot migration: reassign every custom calibre (and calibre image) whose
 * `source` is the legacy `'custom'` value — which the RN app renders no
 * attribution for — to `'brand'` (in-house movement) or `'manufacturer'`
 * (third-party), per the brandId/manufacturerId rule. Offers a dry-run (scan &
 * count, no writes); the real write run is gated behind a confirm modal.
 */
function MigrateCustomCalibreSourceSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<
    (MigrateCustomSourceResult & { dryRun: boolean }) | null
  >(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await migrateCustomCalibreSources({ dryRun });
      setResult({ ...r, dryRun });
      const summary =
        `${dryRun ? 'Dry run' : 'Migration'} complete: ` +
        `${r.calibresScanned} scanned, ` +
        `${r.calibresWithCustomSource} calibre + ${r.imagesWithCustomSource} image 'custom' source(s), ` +
        `${dryRun ? 'would update' : 'updated'} ${r.calibresUpdated} doc(s)` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('migrateCustomCalibreSources failed:', err);
      toast.error(
        err instanceof Error
          ? `Migration failed: ${err.message}`
          : 'Migration failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="migrateCustomCalibreSource"
      title="Migrate 'custom' calibre source"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Finds calibres (and calibre images) whose{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">source</code>{' '}
          is the legacy{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            'custom'
          </code>{' '}
          value — which the RN app renders no attribution for — and reassigns each
          to{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">brand</code>{' '}
          (in-house movement, where brandId === manufacturerId) or{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            manufacturer
          </code>{' '}
          (third-party). Other provenance values are untouched. Run the dry-run
          first to see the count.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={!!running}
            onClick={() => void run(true)}
          >
            {running === 'dry' ? 'Scanning…' : 'Dry run'}
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={!!running}
            onClick={() => setConfirmOpen(true)}
          >
            {running === 'write' ? 'Migrating…' : 'Run & write'}
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run result' : 'Migration result'}
              </span>
              <Badge variant={result.errors.length ? 'warning' : 'success'}>
                {result.dryRun ? 'preview' : 'done'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Calibres scanned" value={result.calibresScanned} />
              <ResultStat
                label="Calibre 'custom'"
                value={result.calibresWithCustomSource}
              />
              <ResultStat
                label="Image 'custom'"
                value={result.imagesWithCustomSource}
              />
              <ResultStat
                label={result.dryRun ? 'Would update' : 'Docs updated'}
                value={result.calibresUpdated}
              />
              <ResultStat label="→ brand" value={result.toBrand} />
              <ResultStat label="→ manufacturer" value={result.toManufacturer} />
              <ResultStat label="Images updated" value={result.imagesUpdated} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Migrate 'custom' calibre source?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This rewrites the{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">source</code>{' '}
            field on every calibre and calibre image currently set to{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              'custom'
            </code>
            , reassigning each to <span className="font-medium">brand</span> or{' '}
            <span className="font-medium">manufacturer</span>. Run the dry-run
            first if you haven&apos;t — it reports exactly how many will change.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run(false);
              }}
            >
              Migrate
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * One-shot global brand operation: flip `showProductLinks` on/off for every
 * brand at once. Each direction is gated behind a confirm modal that shows the
 * brand count. New brands now default to `showProductLinks: false` (opt-in); use
 * this to flip existing brands retroactively.
 */
function ShowProductLinksBulkSection() {
  const [brandCount, setBrandCount] = useState<number | null>(null);
  const [running, setRunning] = useState<boolean>(false);
  const [confirmValue, setConfirmValue] = useState<boolean | null>(null);
  const [result, setResult] = useState<
    (BulkSetShowProductLinksResult & { value: boolean }) | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const brands = await getWatchBrands();
        if (!cancelled) setBrandCount(brands.length);
      } catch (err) {
        console.error('Failed to load brand count:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const run = async (value: boolean) => {
    setRunning(true);
    try {
      const r = await bulkSetShowProductLinks(value);
      setResult({ ...r, value });
      const summary =
        `Product links ${value ? 'enabled' : 'disabled'} on ` +
        `${r.updated}/${r.brandsScanned} brand(s)` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('bulkSetShowProductLinks failed:', err);
      toast.error(
        err instanceof Error
          ? `Bulk update failed: ${err.message}`
          : 'Bulk update failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  const countLabel = brandCount == null ? 'all' : String(brandCount);

  return (
    <CollapsibleSection
      sectionId="bulkShowProductLinks"
      title="Bulk: brand 'Show Product Links'"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Sets{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            showProductLinks
          </code>{' '}
          on <span className="font-medium">all {countLabel}</span> brand(s) at
          once — the explore &ldquo;View product page&rdquo; gate. New brands
          default to off; use this to flip existing brands.
        </p>

        <div className="flex gap-2">
          <Button
            variant="primary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmValue(true)}
          >
            Enable on ALL brands
          </Button>
          <Button
            variant="secondary"
            loading={running}
            disabled={running}
            onClick={() => setConfirmValue(false)}
          >
            Disable on ALL brands
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                Bulk update result
              </span>
              <Badge variant={result.errors.length ? 'warning' : 'success'}>
                {result.value ? 'enabled' : 'disabled'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Brands scanned" value={result.brandsScanned} />
              <ResultStat label="Updated" value={result.updated} />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmValue !== null}
        onClose={() => setConfirmValue(null)}
        title={`${confirmValue ? 'Enable' : 'Disable'} product links on all brands?`}
      >
        <div>
          <p className="text-sm text-gray-700">
            This will update{' '}
            <span className="font-semibold">{countLabel}</span> brand(s) to{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              showProductLinks: {String(confirmValue)}
            </code>
            . Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmValue(null)}>
              Cancel
            </Button>
            <Button
              variant={confirmValue ? 'primary' : 'danger'}
              onClick={() => {
                const value = confirmValue;
                setConfirmValue(null);
                if (value !== null) void run(value);
              }}
            >
              {confirmValue ? 'Enable all' : 'Disable all'}
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

export function ResultStat({
  label,
  value,
  danger,
}: {
  label: string;
  value: number | string;
  danger?: boolean;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={`text-lg font-semibold ${danger ? 'text-red-600' : 'text-gray-900'}`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

/**
 * One-shot admin tool: wipe + re-seed the two acquisition lookup collections
 * (`lookup_acquisition_types` + `lookup_acquisition_directness`) from the
 * current seed values. Needed after the acquisition enums were swapped to the
 * new 8-value / 6-value sets — the collections were already seeded with the old
 * values, and since no data is imported yet, the simplest path is wipe + reseed.
 * Idempotent; gated behind a confirm modal.
 */
function ReseedAcquisitionLookupsSection() {
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      await reseedAcquisitionLookups();
      toast.success(
        'Acquisition lookups re-seeded (types + directness) with the new values.',
      );
    } catch (err) {
      console.error('reseedAcquisitionLookups failed:', err);
      toast.error(
        err instanceof Error
          ? `Reseed failed: ${err.message}`
          : 'Reseed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="reseedAcquisitionLookups"
      title="Reseed Acquisition Lookups"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Wipes and re-seeds{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_acquisition_types
          </code>{' '}
          and{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_acquisition_directness
          </code>{' '}
          from the current seed values (the new 8-value type / 6-value directness
          sets). Deletes all existing docs in both collections first, then writes
          the canonical seeds. Idempotent — safe to run repeatedly. Run this once
          after the acquisition enums changed (no acquisition data imported yet).
        </p>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => setConfirmOpen(true)}
        >
          Reseed Acquisition Lookups
        </Button>
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Reseed acquisition lookups?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This deletes every doc in{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              lookup_acquisition_types
            </code>{' '}
            and{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              lookup_acquisition_directness
            </code>
            , then writes the new seed values. Any manual edits to these two
            collections will be lost. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void run();
              }}
            >
              Reseed
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Seeds the curated `lookup_movement_decoration` vocab (perlage, Côtes de
 * Genève, anglage, …) used by the calibre editor's Decorations picker and the
 * AI calibre enrichment. Non-destructive MERGE — admin-added values are
 * preserved, so it's safe to run repeatedly (e.g. after James adds more terms
 * to the seed list). No confirm modal needed since nothing is deleted.
 */
function SeedMovementDecorationLookupSection() {
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      await reseedMovementDecorationLookup();
      toast.success('Movement decoration lookup seeded (existing values preserved).');
    } catch (err) {
      console.error('reseedMovementDecorationLookup failed:', err);
      toast.error(
        err instanceof Error
          ? `Seed failed: ${err.message}`
          : 'Seed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="seedMovementDecorationLookup"
      title="Seed Movement Decoration Lookup"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Merges the curated decoration vocab into{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_movement_decoration
          </code>{' '}
          (perlage, Côtes de Genève, anglage/bevelling, blued screws, engraving,
          guilloché, sunburst, snailing, black polishing, frosting). Powers the
          calibre editor's Decorations picker and the AI calibre enrichment.{' '}
          <strong>Non-destructive</strong> — any values you've added manually are
          preserved. Safe to run repeatedly.
        </p>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => void run()}
        >
          Seed Movement Decoration Lookup
        </Button>
      </div>
    </CollapsibleSection>
  );
}

/**
 * Seeds the curated `lookup_winding_architecture` vocab (full rotor,
 * micro-rotor, peripheral, bumper, dual rotor, oscillating weight) used by the
 * calibre editor's Auto Winding Architecture field and the AI calibre
 * enrichment prompt. The editor also auto-seeds on first read, but that only
 * fires when an automatic calibre is opened — this button re-seeds on demand
 * after the seed list is extended. Non-destructive MERGE — admin-added values
 * are preserved, so it's safe to run repeatedly. No confirm modal needed since
 * nothing is deleted.
 */
function SeedWindingArchitectureLookupSection() {
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      await reseedWindingArchitectureLookup();
      toast.success('Auto winding architecture lookup seeded (existing values preserved).');
    } catch (err) {
      console.error('reseedWindingArchitectureLookup failed:', err);
      toast.error(
        err instanceof Error
          ? `Seed failed: ${err.message}`
          : 'Seed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="seedWindingArchitectureLookup"
      title="Seed Auto Winding Architecture Lookup"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Merges the curated self-winding topology vocab into{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_winding_architecture
          </code>{' '}
          (full rotor, micro-rotor, peripheral rotor, bumper — with "hammer" as
          a synonym — dual rotor, generic oscillating weight). Powers the
          calibre editor's Auto Winding Architecture field (shown only when the
          movement type carries the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">isAuto</code>{' '}
          property) and the AI calibre enrichment.{' '}
          <strong>Non-destructive</strong> — any values you've added manually are
          preserved. Safe to run repeatedly.
        </p>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => void run()}
        >
          Seed Auto Winding Architecture Lookup
        </Button>
      </div>
    </CollapsibleSection>
  );
}

/**
 * Seeds the three calibre materials/finishing lookups added with shared-types
 * v1.92.0 in one pass: `lookup_rotor_material`, `lookup_bridge_material` and
 * `lookup_plate_finishing`.
 *
 * Each auto-seeds the first time something reads it, so opening a calibre
 * editor is enough to create it. The reason to press this instead is the AI
 * side: the `enrichCalibre` prompt renders all three as {{LOOKUP_*}} tokens
 * server-side, and a collection nobody has opened yet expands to an EMPTY slug
 * list rather than failing loudly — the model is then asked to pick from
 * nothing and returns null every time. Run this once before the first
 * enrichment, and again after extending a seed list.
 *
 * The fourth v1.92.0 field, Case Materials Available, is deliberately absent:
 * it reuses the long-standing `lookup_case_materials` collection rather than
 * introducing one of its own. Non-destructive MERGE — admin-added values are
 * preserved, so no confirm modal.
 */
/**
 * Seed `lookup_dial_features` — the vocabulary behind the ref editor's Dial
 * Features picker and the `{{LOOKUP_DIAL_FEATURES}}` token in enrichReference.
 *
 * A lookup with no values is not a loud failure: the picker renders an empty
 * list and the prompt token expands to an empty slug list, so the AI is asked
 * to choose from nothing and returns nothing. Press this once before the first
 * enrichment, and again after the seed list grows.
 *
 * Non-destructive MERGE — admin-added values and edited display names are
 * preserved — so there is no confirm modal and pressing it twice is harmless.
 */
function SeedDialFeaturesLookupSection() {
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      await reseedDialFeaturesLookup();
      toast.success('Dial Features lookup seeded (existing values preserved).');
    } catch (err) {
      console.error('reseedDialFeaturesLookup failed:', err);
      toast.error(
        err instanceof Error ? `Seed failed: ${err.message}` : 'Seed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection sectionId="seedDialFeaturesLookup" title="Seed Dial Features Lookup">
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Merges the fifteen starter values into{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">lookup_dial_features</code> —
          skeletonisation, apertures, inlays, gem setting, engraved and enamelled work. These back
          the reference editor&apos;s <strong>Dial Features</strong> picker and the{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{'{{LOOKUP_DIAL_FEATURES}}'}</code>{' '}
          token in the enrichment prompt.
        </p>
        <p className="text-xs text-gray-500">
          Existing values are preserved, so pressing it again after extending the seed list only
          adds what is missing. If the picker still shows nothing afterwards, the Firestore rule for
          this collection has not been deployed — the read is denied and the list reads as empty.
        </p>
        <Button variant="secondary" loading={running} disabled={running} onClick={() => void run()}>
          Seed dial features
        </Button>
      </div>
    </CollapsibleSection>
  );
}

function SeedCalibreMaterialsLookupsSection() {
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      await reseedCalibreMaterialsLookups();
      toast.success('Calibre materials lookups seeded (existing values preserved).');
    } catch (err) {
      console.error('reseedCalibreMaterialsLookups failed:', err);
      toast.error(
        err instanceof Error
          ? `Seed failed: ${err.message}`
          : 'Seed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="seedCalibreMaterialsLookups"
      title="Seed Calibre Materials Lookups"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Merges starter vocabularies into the three v1.92.0 materials lookups:{' '}
          {CALIBRE_MATERIALS_LOOKUP_COLLECTIONS.map((name, i) => (
            <span key={name}>
              {i > 0 && ', '}
              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{name}</code>
            </span>
          ))}
          . These back the calibre editor's Rotor Material, Bridge Material and
          Plate Finishing fields, their tier overrides, and the matching{' '}
          {'{{LOOKUP_*}}'} tokens in the AI calibre enrichment prompt — which
          render an empty list until the collections exist, so run this before
          the first enrichment. Case Materials Available reuses the existing{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_case_materials
          </code>{' '}
          and needs no seeding. The starter lists are deliberately short —
          extend them in the Lookup Manager.{' '}
          <strong>Non-destructive</strong> — any values you've added manually are
          preserved. Safe to run repeatedly.
        </p>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => void run()}
        >
          Seed Calibre Materials Lookups
        </Button>
      </div>
    </CollapsibleSection>
  );
}

/**
 * Seeds the seven Task #30 storage + winder lookup collections in one pass:
 * `lookup_storage_locations` (the 8 StorageLocationCategory slugs the RN storage
 * picker writes) plus the six `lookup_winder_*` vocabularies behind the winder
 * catalog editor's pickers and the `enrichWinder` prompt's {{LOOKUP_*}} tokens.
 *
 * Each collection also auto-seeds the first time something reads it — opening
 * the Curated Winders tab is enough. This button exists so the six winder
 * collections can be created BEFORE the first enrichment run (an unseeded
 * collection leaves its token unexpanded in the prompt rather than failing
 * loudly), and so James can re-seed after extending a seed list.
 *
 * Non-destructive MERGE — admin-added values are preserved, so it's safe to run
 * repeatedly. No confirm modal needed since nothing is deleted.
 */
function SeedStorageWinderLookupsSection() {
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      await reseedStorageAndWinderLookups();
      toast.success(
        `Seeded ${STORAGE_WINDER_LOOKUP_COLLECTIONS.length} storage + winder lookups (existing values preserved).`,
      );
    } catch (err) {
      console.error('reseedStorageAndWinderLookups failed:', err);
      toast.error(
        err instanceof Error ? `Seed failed: ${err.message}` : 'Seed failed \u2014 see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="seedStorageWinderLookups"
      title="Seed Storage & Winder Lookups"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Merges the curated storage + winder vocabularies into their seven
          collections:{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_storage_locations
          </code>{' '}
          (the 7 categories behind the app\u2019s storage picker) and{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_winder_*
          </code>{' '}
          (manufacturers, rotation directions, programs, power sources, materials,
          price tiers). The winder collections back the Curated Winders editor and
          the <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">enrichWinder</code>{' '}
          prompt \u2014 each collection\u2019s multi-select flag is what tells the AI to
          return one slug or an array.{' '}
          <strong>Non-destructive</strong> \u2014 any values you\u2019ve added manually are
          preserved. Safe to run repeatedly.
        </p>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => void run()}
        >
          Seed Storage &amp; Winder Lookups
        </Button>
      </div>
    </CollapsibleSection>
  );
}

function SeedElectronicModuleLookupsSection() {
  const [running, setRunning] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState<SeedElectronicModuleLookupsResult | null>(
    null,
  );

  const run = async () => {
    setRunning(true);
    try {
      const r = await seedElectronicModuleLookups(overwrite);
      setResult(r);
      toast.success(
        `Seeded ElectronicModule lookups: ${r.totalAdded} added, ${r.totalUpdated} updated.`,
      );
    } catch (err) {
      console.error('seedElectronicModuleLookups failed:', err);
      toast.error(
        err instanceof Error
          ? `Seed failed: ${err.message}`
          : 'Seed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="seedElectronicModuleLookups"
      title="Seed ElectronicModule Lookups"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Seeds the ElectronicModules display + battery lookups —{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_display_type
          </code>{' '}
          and{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_battery_type
          </code>{' '}
          — with a starter vocabulary. Default is non-destructive: only missing
          slugs are added; existing/curated values are untouched. Safe to run
          repeatedly; curate further from the lookup manager afterwards.
        </p>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            disabled={running}
          />
          Overwrite — re-apply seed display names &amp; sort order to existing seed
          slugs (curated non-seed values are still preserved; nothing is deleted)
        </label>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => void run()}
        >
          {running ? 'Seeding…' : 'Seed ElectronicModule Lookups'}
        </Button>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                Seed result
              </span>
              <Badge variant="success">done</Badge>
              {result.overwrite && <Badge variant="warning">overwrite</Badge>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Total added" value={result.totalAdded} />
              <ResultStat label="Total updated" value={result.totalUpdated} />
            </div>
            <div className="space-y-2">
              {result.collections.map((c) => (
                <div
                  key={c.collection}
                  className="border border-gray-200 rounded-md p-2 bg-white"
                >
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    {c.collection}
                  </code>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mt-2">
                    <ResultStat label="Added" value={c.added} />
                    <ResultStat label="Updated" value={c.updated} />
                    <ResultStat label="Skipped" value={c.skipped} />
                    <ResultStat label="Total" value={c.total} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

function SeedPocketWatchLookupsSection() {
  const [running, setRunning] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState<SeedPocketWatchLookupsResult | null>(
    null,
  );

  const run = async () => {
    setRunning(true);
    try {
      const r = await seedPocketWatchLookups(overwrite);
      setResult(r);
      toast.success(
        `Seeded pocket-watch lookups: ${r.totalAdded} added, ${r.totalUpdated} updated.`,
      );
    } catch (err) {
      console.error('seedPocketWatchLookups failed:', err);
      toast.error(
        err instanceof Error
          ? `Seed failed: ${err.message}`
          : 'Seed failed — see console',
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <CollapsibleSection
      sectionId="seedPocketWatchLookups"
      title="Seed Pocket Watch Lookups"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Seeds the pocket-watch lookups —{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_pocket_watch_case_style
          </code>
          ,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_pocket_watch_escapement
          </code>
          ,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_pocket_watch_jewel_bearing
          </code>
          ,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_pocket_watch_movement_size
          </code>{' '}
          and{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_pocket_watch_adjustments
          </code>{' '}
          — with a starter vocabulary. Default is non-destructive: only missing
          slugs are added; existing/curated values are untouched. Safe to run
          repeatedly; curate further from the lookup manager afterwards.
        </p>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            disabled={running}
          />
          Overwrite — re-apply seed display names &amp; sort order to existing seed
          slugs (curated non-seed values are still preserved; nothing is deleted)
        </label>

        <Button
          variant="primary"
          loading={running}
          disabled={running}
          onClick={() => void run()}
        >
          {running ? 'Seeding…' : 'Seed Pocket Watch Lookups'}
        </Button>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">
                Seed result
              </span>
              <Badge variant="success">done</Badge>
              {result.overwrite && <Badge variant="warning">overwrite</Badge>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <ResultStat label="Total added" value={result.totalAdded} />
              <ResultStat label="Total updated" value={result.totalUpdated} />
            </div>
            <div className="space-y-2">
              {result.collections.map((c) => (
                <div
                  key={c.collection}
                  className="border border-gray-200 rounded-md p-2 bg-white"
                >
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    {c.collection}
                  </code>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mt-2">
                    <ResultStat label="Added" value={c.added} />
                    <ResultStat label="Updated" value={c.updated} />
                    <ResultStat label="Skipped" value={c.skipped} />
                    <ResultStat label="Total" value={c.total} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

// ============================================================================
// Brand Audit + Acquisitions importers (two sibling AdminConfig sections that
// apply James's deeper-pass AI research files to the brand catalogue).
// ============================================================================

const VALID_BRAND_STATUSES: BrandStatus[] = [
  'active',
  'dormant',
  'defunct',
  'revived',
  'absorbed',
];
const VALID_ACQUISITION_TYPES: AcquisitionType[] = [
  'full_brand_merger',
  'full_brand_company_acquisition',
  'brand_rights_acquisition',
  'manufacture_integration',
  'brand_acquisition_for_revival',
  'rights_acquisition_for_revival',
  'successor_renaming_absorption',
  'brand_and_product_takeover',
];
const VALID_DIRECTNESS: AcquisitionDirectness[] = [
  'direct_watch_brand_to_watch_brand',
  'watch_brand_company_to_watch_brand',
  'assigned_to_watch_brand_after_group_acquisition',
  'brand_ecosystem_not_pure_direct',
  'successor_brand_lineage',
  'same_brand_revival',
];
/** Statuses for which a `yearCeased` value is meaningful. */
const CEASED_STATUSES: BrandStatus[] = ['defunct', 'dormant', 'absorbed'];

/** Lowercase + collapse internal whitespace, for case/space-insensitive matching. */
function normalizeBrandKey(s: string | undefined | null): string {
  return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Build a name → brand index for matching imported rows against the catalogue.
 * `displayName` keys win over `name` keys on collision (we match displayName
 * first per the spec). Returns a lookup fn.
 */
function buildBrandIndex(brands: WatchBrand[]): (rawName: string) => WatchBrand | undefined {
  const byName = new Map<string, WatchBrand>();
  // Seed `name` first so `displayName` overwrites on collision.
  for (const b of brands) {
    const k = normalizeBrandKey(b.name);
    if (k) byName.set(k, b);
  }
  for (const b of brands) {
    const k = normalizeBrandKey(b.displayName);
    if (k) byName.set(k, b);
  }
  return (rawName: string) => byName.get(normalizeBrandKey(rawName));
}

/** Parse a year-ish string to a 4-digit number, or undefined if not sensible. */
function parseYear(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const m = String(raw).match(/\d{4}/);
  if (!m) return undefined;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : undefined;
}

/** Trigger a client-side CSV download. */
function downloadCsv(filename: string, rows: string[][]): void {
  const escape = (cell: string) => `"${String(cell ?? '').replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(escape).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Minimal RFC-4180-ish CSV parser: handles quoted fields, embedded commas /
 * newlines, and "" escapes. Returns an array of rows (array of cell strings).
 * Good enough for the well-formed exports James's research pipeline produces.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  // Strip a leading BOM if present.
  const t = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (inQuotes) {
      if (ch === '"') {
        if (t[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      // Handle CRLF: skip the \n after a \r.
      if (ch === '\r' && t[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      rows.push(row);
      row = [];
    } else {
      cell += ch;
    }
  }
  // Flush trailing cell/row (file may not end with a newline).
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Drop fully-empty trailing rows.
  return rows.filter((r) => r.length > 1 || (r.length === 1 && r[0].trim() !== ''));
}

// ---------------------------------------------------------------------------
// Section 1: Import Brand Audit
// ---------------------------------------------------------------------------

/** The shape of one brand entry in the audit JSON (only fields we read). */
interface AuditEntry {
  'Brand Name': string;
  Country?: string;
  'Year Founded'?: string;
  'Year Ceased'?: string;
  'Parent Org'?: string;
  'Audited Status'?: string;
  'Status Reason'?: string;
  'Needs Manual Review'?: string;
  'Audit Notes'?: string;
}

type AuditFieldKey =
  | 'status'
  | 'founded'
  | 'yearCeased'
  | 'country'
  | 'parentOrg'
  | 'needsReview'
  | 'auditNotes';

const AUDIT_FIELDS: { key: AuditFieldKey; label: string }[] = [
  { key: 'status', label: 'status' },
  { key: 'founded', label: 'founded' },
  { key: 'yearCeased', label: 'yearCeased' },
  { key: 'country', label: 'country' },
  { key: 'parentOrg', label: 'parentOrg' },
  { key: 'needsReview', label: 'needsReview' },
  { key: 'auditNotes', label: 'auditNotes' },
];

interface ProposedChange {
  field: AuditFieldKey;
  from: string;
  to: string;
  /** Whether this change is gated by the per-field overwrite toggle (current value non-empty). */
  overwrite: boolean;
}

interface AuditPreviewRow {
  brandName: string;
  brand?: WatchBrand;
  /** Audited status was unknown / unmappable (e.g. typo in the enum). */
  invalidStatus?: string;
  changes: ProposedChange[];
}

const display = (v: unknown): string =>
  v === undefined || v === null || v === '' ? '—' : String(v);

/**
 * One-shot admin tool: apply a deeper-pass status-audit JSON
 * (`watch_brands_annotated_researched_*.json`) to the brand catalogue. Matches
 * each entry's "Brand Name" against the catalogue (case/space-insensitive,
 * displayName first), previews per-field changes, and writes only matched
 * brands. Fill-empty-only by default; a per-field overwrite toggle in the
 * preview header lets the admin force-overwrite a field for the whole run.
 */
function ImportBrandAuditSection() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [overwrite, setOverwrite] = useState<Record<AuditFieldKey, boolean>>({
    status: false,
    founded: false,
    yearCeased: false,
    country: false,
    parentOrg: false,
    needsReview: false,
    auditNotes: false,
  });
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applied, setApplied] = useState<{
    updated: number;
    skipped: number;
    errors: { brand: string; error: string }[];
  } | null>(null);

  useEffect(() => {
    let active = true;
    getWatchBrands()
      .then((all) => {
        if (active) setBrands(all);
      })
      .catch((err) => console.error('Failed to load brands for audit import:', err));
    return () => {
      active = false;
    };
  }, []);

  const lookup = useMemo(() => buildBrandIndex(brands), [brands]);

  /** Compute the proposed (post-overwrite-toggle) changes for one entry. */
  const computeRow = (entry: AuditEntry): AuditPreviewRow => {
    const brandName = (entry['Brand Name'] || '').trim();
    const brand = lookup(brandName);
    const row: AuditPreviewRow = { brandName, brand, changes: [] };
    if (!brand) return row;

    const auditedRaw = (entry['Audited Status'] || '').trim();
    const inactiveCandidate = auditedRaw === 'inactive_candidate';
    let proposedStatus: BrandStatus | undefined;
    if (inactiveCandidate) {
      proposedStatus = 'active';
    } else if (auditedRaw && VALID_BRAND_STATUSES.includes(auditedRaw as BrandStatus)) {
      proposedStatus = auditedRaw as BrandStatus;
    } else if (auditedRaw) {
      row.invalidStatus = auditedRaw;
    }

    const needsReview =
      (entry['Needs Manual Review'] || '').trim().toUpperCase() === 'TRUE' ||
      inactiveCandidate;
    const founded = parseYear(entry['Year Founded']);
    const yearCeased = parseYear(entry['Year Ceased']);
    const country = (entry.Country || '').trim();
    const parentOrg = (entry['Parent Org'] || '').trim();
    const auditNotes = [entry['Status Reason'], entry['Audit Notes']]
      .map((s) => (s || '').trim())
      .filter(Boolean)
      .join('\n\n');

    // Helper: register a change respecting fill-empty-only + per-field overwrite.
    const consider = (
      field: AuditFieldKey,
      proposed: string | number | boolean | undefined,
      currentRaw: unknown,
      currentEmpty: boolean,
    ) => {
      if (proposed === undefined) return;
      const current = currentRaw;
      // No-op if already equal.
      if (String(current ?? '') === String(proposed)) return;
      const needsOverwrite = !currentEmpty;
      if (needsOverwrite && !overwrite[field]) return;
      row.changes.push({
        field,
        from: display(current),
        to: String(proposed),
        overwrite: needsOverwrite,
      });
    };

    // status — undefined status is treated as 'active' for emptiness/equality.
    if (proposedStatus !== undefined) {
      const effectiveCurrent = brand.status ?? 'active';
      if (effectiveCurrent !== proposedStatus) {
        const needsOverwrite = brand.status !== undefined;
        if (!needsOverwrite || overwrite.status) {
          row.changes.push({
            field: 'status',
            from: display(brand.status),
            to: proposedStatus,
            overwrite: needsOverwrite,
          });
        }
      }
    }

    // founded — fill if currently empty (legacy `founded` field).
    if (founded !== undefined) {
      consider('founded', founded, brand.founded, brand.founded == null);
    }

    // yearCeased — only when the proposed status implies a ceased date.
    if (
      yearCeased !== undefined &&
      proposedStatus !== undefined &&
      CEASED_STATUSES.includes(proposedStatus)
    ) {
      consider('yearCeased', yearCeased, brand.yearCeased, brand.yearCeased == null);
    }

    if (country) {
      consider('country', country, brand.country, !(brand.country || '').trim());
    }
    if (parentOrg) {
      consider('parentOrg', parentOrg, brand.parentOrg, !(brand.parentOrg || '').trim());
    }

    // needsReview — absence/false counts as "empty" (we only raise the flag for free).
    consider('needsReview', needsReview, brand.needsReview ?? false, brand.needsReview !== true);

    if (auditNotes) {
      consider('auditNotes', auditNotes, brand.auditNotes, !(brand.auditNotes || '').trim());
    }

    return row;
  };

  const rows = useMemo(
    () => (entries ? entries.map(computeRow) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, lookup, overwrite],
  );

  const matched = rows.filter((r) => r.brand);
  const missing = rows.filter((r) => !r.brand);
  const withChanges = matched.filter((r) => r.changes.length > 0);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setApplied(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const list: AuditEntry[] = Array.isArray(json) ? json : json.brands;
      if (!Array.isArray(list)) {
        throw new Error('Expected a top-level `brands` array');
      }
      setEntries(list);
      setFileName(file.name);
      toast.success(`Parsed ${list.length} audit entries from ${file.name}`);
    } catch (err) {
      setEntries(null);
      setFileName('');
      toast.error(
        err instanceof Error ? `Parse failed: ${err.message}` : 'Failed to parse JSON',
      );
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const apply = async () => {
    setApplying(true);
    const result = { updated: 0, skipped: 0, errors: [] as { brand: string; error: string }[] };
    for (const r of withChanges) {
      if (!r.brand) continue;
      const updates: Partial<WatchBrand> = {};
      for (const c of r.changes) {
        switch (c.field) {
          case 'status':
            updates.status = c.to as BrandStatus;
            break;
          case 'founded':
            updates.founded = Number(c.to);
            break;
          case 'yearCeased':
            updates.yearCeased = Number(c.to);
            break;
          case 'country':
            updates.country = c.to;
            break;
          case 'parentOrg':
            updates.parentOrg = c.to;
            break;
          case 'needsReview':
            updates.needsReview = c.to === 'true';
            break;
          case 'auditNotes':
            updates.auditNotes = c.to;
            break;
        }
      }
      try {
        await updateWatchBrand(r.brand.id, updates);
        result.updated += 1;
      } catch (err) {
        result.errors.push({
          brand: r.brandName,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    result.skipped = missing.length;
    setApplied(result);
    setApplying(false);
    // Refresh brands so a second preview reflects the writes.
    try {
      setBrands(await getWatchBrands());
    } catch {
      /* non-fatal */
    }
    if (result.errors.length) {
      toast(`Applied with ${result.errors.length} error(s)`, { icon: '⚠️' });
    } else {
      toast.success(`Brand audit applied: ${result.updated} brand(s) updated`);
    }
  };

  const downloadMissing = () => {
    downloadCsv('brand-audit-missing.csv', [
      ['Brand Name'],
      ...missing.map((r) => [r.brandName]),
    ]);
  };

  return (
    <CollapsibleSection sectionId="importBrandAudit" title="Import Brand Audit">
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Upload a deeper-pass status-audit JSON (
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watch_brands_annotated_researched_*.json
          </code>
          ). Each entry's <span className="font-medium">Brand Name</span> is matched
          against the catalogue (case/space-insensitive, display name first).
          Changes are <span className="font-medium">fill-empty-only</span> by default
          — flip a field's overwrite toggle below to force it for this run.{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            inactive_candidate
          </code>{' '}
          maps to <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">active</code>{' '}
          + needsReview. Unmatched brands are skipped (not created).
        </p>

        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleFile}
          />
          <Button
            variant="secondary"
            disabled={applying}
            onClick={() => fileRef.current?.click()}
          >
            {entries ? 'Choose different file' : 'Choose JSON file'}
          </Button>
          {fileName && (
            <span className="text-xs text-gray-500 truncate">{fileName}</span>
          )}
        </div>

        {entries && (
          <>
            <div className="flex flex-wrap gap-2">
              <ResultStat label="Entries" value={rows.length} />
              <ResultStat label="Matched" value={matched.length} />
              <ResultStat
                label="Not found"
                value={missing.length}
                danger={missing.length > 0}
              />
              <ResultStat label="With changes" value={withChanges.length} />
            </div>

            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Overwrite non-empty values (per field, this run)
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {AUDIT_FIELDS.map((f) => (
                  <label
                    key={f.key}
                    className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={overwrite[f.key]}
                      onChange={(e) =>
                        setOverwrite((prev) => ({ ...prev, [f.key]: e.target.checked }))
                      }
                      className="accent-brand-600"
                    />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            {missing.length > 0 && (
              <button
                type="button"
                onClick={downloadMissing}
                className="text-sm text-brand-600 hover:text-brand-700 underline"
              >
                Download {missing.length} not-found brand(s) as CSV
              </button>
            )}

            <div className="max-h-96 overflow-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-3 py-2">Brand</th>
                    <th className="px-3 py-2">Match</th>
                    <th className="px-3 py-2">Proposed changes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                        {r.brandName}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.brand ? (
                          <span className="text-green-700">✅ matched</span>
                        ) : (
                          <span className="text-amber-600">⚠ not found</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {r.invalidStatus && (
                          <div className="text-amber-600 text-xs mb-1">
                            unknown audited status “{r.invalidStatus}” — status skipped
                          </div>
                        )}
                        {!r.brand ? (
                          <span className="text-gray-400">—</span>
                        ) : r.changes.length === 0 ? (
                          <span className="text-gray-400">no changes</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {r.changes.map((c, j) => (
                              <li key={j} className="text-xs">
                                <code className="bg-gray-100 px-1 rounded">{c.field}</code>{' '}
                                <span className="text-gray-400">{c.from}</span>
                                {' → '}
                                <span className="text-gray-900">{c.to}</span>
                                {c.overwrite && (
                                  <span className="ml-1 text-amber-600">(overwrite)</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                loading={applying}
                disabled={applying || withChanges.length === 0}
                onClick={() => setConfirmOpen(true)}
              >
                Apply to {withChanges.length} brand(s)
              </Button>
            </div>

            {applied && (
              <div className="flex flex-wrap gap-2">
                <ResultStat label="Updated" value={applied.updated} />
                <ResultStat label="Skipped (not found)" value={applied.skipped} />
                <ResultStat
                  label="Errors"
                  value={applied.errors.length}
                  danger={applied.errors.length > 0}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Apply brand audit?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This writes the proposed changes to{' '}
            <span className="font-semibold">{withChanges.length}</span> matched
            brand(s). Fields with an active overwrite toggle will replace existing
            non-empty values. {missing.length} unmatched brand(s) will be skipped.
            Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void apply();
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

// ---------------------------------------------------------------------------
// Section 2: Import Acquisitions
// ---------------------------------------------------------------------------

interface AcqPreviewRow {
  rowNum: number;
  acquirerName: string;
  acquiredName: string;
  acquirer?: WatchBrand;
  acquired?: WatchBrand;
  /** acquisition_type / directness not in the enum. */
  invalid?: string;
  /** Already present on the acquirer (dedup by acquiredBrandId). */
  duplicate?: boolean;
  proposed?: BrandAcquisition;
}

/**
 * One-shot admin tool: apply a deeper-pass acquisitions CSV
 * (`watch_brand_acquisitions_researched_*.csv`) to the brand catalogue. Each row
 * names an acquirer and an acquired brand; both are matched against the
 * catalogue. The proposed {@link BrandAcquisition} is appended to the acquirer's
 * `acquisitions` array, deduped by `acquiredBrandId` (existing links untouched).
 * Rows missing either brand are skipped (not created) and downloadable as CSV.
 */
function ImportAcquisitionsSection() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<AcqPreviewRow[] | null>(null);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applied, setApplied] = useState<{
    added: number;
    acquirersUpdated: number;
    errors: { brand: string; error: string }[];
  } | null>(null);

  const loadBrands = async () => {
    const all = await getWatchBrands();
    setBrands(all);
    return all;
  };

  useEffect(() => {
    let active = true;
    loadBrands().catch((err) =>
      active ? console.error('Failed to load brands for acquisitions import:', err) : null,
    );
    return () => {
      active = false;
    };
  }, []);

  const buildRows = (text: string, brandList: WatchBrand[]): AcqPreviewRow[] => {
    const lookup = buildBrandIndex(brandList);
    const parsed = parseCsv(text);
    if (parsed.length < 2) return [];
    const header = parsed[0].map((h) => h.trim());
    const idx = (name: string) => header.indexOf(name);
    const cAcquirer = idx('acquirer_brand');
    const cAcquired = idx('acquired_brand');
    const cType = idx('acquisition_type');
    const cYear = idx('year');
    const cDirect = idx('directness');
    const cNotes = idx('notes');
    const cSources = idx('sources');

    return parsed.slice(1).map((cells, i): AcqPreviewRow => {
      const acquirerName = (cells[cAcquirer] || '').trim();
      const acquiredName = (cells[cAcquired] || '').trim();
      const acquirer = acquirerName ? lookup(acquirerName) : undefined;
      const acquired = acquiredName ? lookup(acquiredName) : undefined;
      const row: AcqPreviewRow = {
        rowNum: i + 2,
        acquirerName,
        acquiredName,
        acquirer,
        acquired,
      };
      if (!acquirer || !acquired) return row;

      const typeRaw = (cells[cType] || '').trim();
      const directRaw = (cells[cDirect] || '').trim();
      if (!VALID_ACQUISITION_TYPES.includes(typeRaw as AcquisitionType)) {
        row.invalid = `acquisition_type “${typeRaw}”`;
        return row;
      }
      if (!VALID_DIRECTNESS.includes(directRaw as AcquisitionDirectness)) {
        row.invalid = `directness “${directRaw}”`;
        return row;
      }

      row.duplicate = (acquirer.acquisitions || []).some(
        (a) => a.acquiredBrandId === acquired.id,
      );

      const year = parseYear(cells[cYear]);
      const notes = (cells[cNotes] || '').trim();
      const sourceUrl = (cells[cSources] || '')
        .split('|')[0]
        .trim();
      row.proposed = {
        acquiredBrandId: acquired.id,
        acquisitionType: typeRaw as AcquisitionType,
        directness: directRaw as AcquisitionDirectness,
        ...(year !== undefined ? { year } : {}),
        ...(notes ? { notes } : {}),
        ...(sourceUrl ? { sourceUrl } : {}),
      };
      return row;
    });
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setApplied(null);
    try {
      const text = await file.text();
      const built = buildRows(text, brands);
      if (built.length === 0) throw new Error('No data rows found');
      setRows(built);
      setFileName(file.name);
      toast.success(`Parsed ${built.length} acquisition row(s) from ${file.name}`);
    } catch (err) {
      setRows(null);
      setFileName('');
      toast.error(
        err instanceof Error ? `Parse failed: ${err.message}` : 'Failed to parse CSV',
      );
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const all = rows || [];
  const importable = all.filter(
    (r) => r.acquirer && r.acquired && r.proposed && !r.duplicate && !r.invalid,
  );
  const missing = all.filter((r) => !r.acquirer || !r.acquired);
  const dupes = all.filter((r) => r.duplicate);

  const apply = async () => {
    setApplying(true);
    const errors: { brand: string; error: string }[] = [];
    // Group importable rows by acquirer so each acquirer is written once.
    const byAcquirer = new Map<string, { brand: WatchBrand; adds: BrandAcquisition[] }>();
    for (const r of importable) {
      if (!r.acquirer || !r.proposed) continue;
      const g = byAcquirer.get(r.acquirer.id) || { brand: r.acquirer, adds: [] };
      // Dedup within this run too (same target listed twice for one acquirer).
      if (
        !g.adds.some((a) => a.acquiredBrandId === r.proposed!.acquiredBrandId) &&
        !(g.brand.acquisitions || []).some(
          (a) => a.acquiredBrandId === r.proposed!.acquiredBrandId,
        )
      ) {
        g.adds.push(r.proposed);
      }
      byAcquirer.set(r.acquirer.id, g);
    }
    let added = 0;
    let acquirersUpdated = 0;
    for (const { brand, adds } of byAcquirer.values()) {
      if (adds.length === 0) continue;
      const next = [...(brand.acquisitions || []), ...adds];
      try {
        await updateWatchBrand(brand.id, { acquisitions: next });
        added += adds.length;
        acquirersUpdated += 1;
      } catch (err) {
        errors.push({
          brand: brand.displayName || brand.name,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    setApplied({ added, acquirersUpdated, errors });
    setApplying(false);
    try {
      const refreshed = await loadBrands();
      // Re-derive preview against fresh data so dedup reflects the writes.
      if (rows) {
        const lookup = buildBrandIndex(refreshed);
        setRows((prev) =>
          (prev || []).map((r) => {
            const acquirer = lookup(r.acquirerName);
            const acquired = lookup(r.acquiredName);
            const duplicate =
              acquirer && acquired
                ? (acquirer.acquisitions || []).some((a) => a.acquiredBrandId === acquired.id)
                : r.duplicate;
            return { ...r, acquirer, acquired, duplicate };
          }),
        );
      }
    } catch {
      /* non-fatal */
    }
    if (errors.length) {
      toast(`Applied with ${errors.length} error(s)`, { icon: '⚠️' });
    } else {
      toast.success(`Acquisitions applied: ${added} link(s) across ${acquirersUpdated} acquirer(s)`);
    }
  };

  const downloadMissing = () => {
    downloadCsv('acquisitions-missing.csv', [
      ['row', 'acquirer_brand', 'acquired_brand', 'reason'],
      ...missing.map((r) => [
        String(r.rowNum),
        r.acquirerName,
        r.acquiredName,
        !r.acquirer && !r.acquired
          ? 'both missing'
          : !r.acquirer
            ? 'acquirer missing'
            : 'acquired missing',
      ]),
    ]);
  };

  const matchCell = (r: AcqPreviewRow) => {
    if (r.acquirer && r.acquired) return <span className="text-green-700">✅ both</span>;
    if (!r.acquirer && !r.acquired)
      return <span className="text-red-600">❌ both missing</span>;
    if (!r.acquirer) return <span className="text-amber-600">⚠ acquirer missing</span>;
    return <span className="text-amber-600">⚠ acquired missing</span>;
  };

  return (
    <CollapsibleSection sectionId="importAcquisitions" title="Import Acquisitions">
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Upload a deeper-pass acquisitions CSV (
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            watch_brand_acquisitions_researched_*.csv
          </code>
          ). Both <span className="font-medium">acquirer_brand</span> and{' '}
          <span className="font-medium">acquired_brand</span> are matched against the
          catalogue. The acquisition is appended to the acquirer's{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">acquisitions</code>{' '}
          array, deduped by acquired brand (existing links left alone). Rows missing
          either brand are skipped (not created).
        </p>

        <div className="flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="text/csv,.csv"
            className="hidden"
            onChange={handleFile}
          />
          <Button
            variant="secondary"
            disabled={applying}
            onClick={() => fileRef.current?.click()}
          >
            {rows ? 'Choose different file' : 'Choose CSV file'}
          </Button>
          {fileName && (
            <span className="text-xs text-gray-500 truncate">{fileName}</span>
          )}
        </div>

        {rows && (
          <>
            <div className="flex flex-wrap gap-2">
              <ResultStat label="Rows" value={all.length} />
              <ResultStat label="Importable" value={importable.length} />
              <ResultStat label="Duplicates" value={dupes.length} />
              <ResultStat
                label="Missing brand(s)"
                value={missing.length}
                danger={missing.length > 0}
              />
            </div>

            {missing.length > 0 && (
              <button
                type="button"
                onClick={downloadMissing}
                className="text-sm text-brand-600 hover:text-brand-700 underline"
              >
                Download {missing.length} skipped row(s) as CSV
              </button>
            )}

            <div className="max-h-96 overflow-auto border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-xs text-gray-500">
                    <th className="px-3 py-2">Acquirer → Acquired</th>
                    <th className="px-3 py-2">Match</th>
                    <th className="px-3 py-2">Proposed acquisition</th>
                  </tr>
                </thead>
                <tbody>
                  {all.map((r, i) => (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="px-3 py-2 text-gray-900 whitespace-nowrap">
                        <span className="font-medium">{r.acquirerName}</span>
                        <span className="text-gray-400"> → </span>
                        {r.acquiredName}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{matchCell(r)}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">
                        {r.invalid ? (
                          <span className="text-amber-600">invalid {r.invalid} — skipped</span>
                        ) : r.duplicate ? (
                          <span className="text-gray-400">already linked — skipped</span>
                        ) : r.proposed ? (
                          <span>
                            <code className="bg-gray-100 px-1 rounded">
                              {r.proposed.acquisitionType}
                            </code>
                            {r.proposed.year ? ` · ${r.proposed.year}` : ''} ·{' '}
                            {r.proposed.directness}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              variant="primary"
              loading={applying}
              disabled={applying || importable.length === 0}
              onClick={() => setConfirmOpen(true)}
            >
              Apply {importable.length} acquisition(s)
            </Button>

            {applied && (
              <div className="flex flex-wrap gap-2">
                <ResultStat label="Links added" value={applied.added} />
                <ResultStat label="Acquirers updated" value={applied.acquirersUpdated} />
                <ResultStat
                  label="Errors"
                  value={applied.errors.length}
                  danger={applied.errors.length > 0}
                />
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Apply acquisitions?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This appends <span className="font-semibold">{importable.length}</span>{' '}
            acquisition link(s) to their acquirer brands. Existing links (deduped by
            acquired brand) are left alone. {missing.length} row(s) missing a brand
            will be skipped. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmOpen(false);
                void apply();
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Configuration → RN App Switches.
 *
 * RN consumer-app feature master switches (the `Feature Toggles` group) plus
 * the Identify CTA background image. One-off: the bulk brand "Show Product
 * Links" writer in the Maintenance & Tools footer.
 */
export function RnAppSwitchesTab() {
  return (
    <div className="space-y-8">
      <LiveSettingsSection
        title="RN Feature Toggles"
        sectionId="liveSettings:rnSwitches"
        filterGroups={(g) => g === RN_SWITCH_GROUP}
      />
      <IdentifyCtaImageSection />

      <ToolsSubsection>
        <ShowProductLinksBulkSection />
      </ToolsSubsection>
    </div>
  );
}

/**
 * Configuration → RTDB Configs.
 *
 * Admin-tunable RTDB knobs that aren't RN feature switches — thresholds,
 * currency, search/content, shipping, external links. Same RTDB storage as the
 * RN switches; split by audience.
 */
export function RtdbConfigsTab() {
  return (
    <div className="space-y-8">
      <LiveSettingsSection
        title="RTDB Settings"
        sectionId="liveSettings:rtdbConfigs"
        filterGroups={(g) => g !== RN_SWITCH_GROUP}
      />
    </div>
  );
}

/**
 * Configuration → Feature Flags.
 *
 * Admin-UI gating flags (Firestore overrides over the code registry). Carries
 * its own toggle/persist state and the destructive-enable confirm modal.
 */
export function FeatureFlagsTab() {
  const { user } = useAuth();
  const { overrides, loaded } = useFeatureFlagsContext();
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [confirmFlag, setConfirmFlag] = useState<FeatureFlagDefinition | null>(
    null,
  );

  const flagsByCategory = useMemo(() => {
    const grouped: Record<FeatureFlagCategory, FeatureFlagDefinition[]> = {
      maintenance: [],
      destructive: [],
      experimental: [],
      other: [],
    };
    for (const f of FEATURE_FLAGS) {
      grouped[f.category].push(f);
    }
    return grouped;
  }, []);

  const handleToggle = async (flag: FeatureFlagDefinition, next: boolean) => {
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    if (flag.category === 'destructive' && next === true) {
      setConfirmFlag(flag);
      return;
    }
    await persistToggle(flag, next);
  };

  const persistToggle = async (flag: FeatureFlagDefinition, next: boolean) => {
    if (!user?.uid) return;
    setPending((p) => ({ ...p, [flag.id]: true }));
    try {
      await setFeatureFlagOverride(flag.id, next, user.uid);
      toast.success(`${next ? 'Enabled' : 'Disabled'} ${flag.label}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update flag',
      );
    } finally {
      setPending((p) => {
        const copy = { ...p };
        delete copy[flag.id];
        return copy;
      });
    }
  };

  const getEffective = (flag: FeatureFlagDefinition): boolean => {
    const override = overrides[flag.id];
    if (override && typeof override.enabled === 'boolean') {
      return override.enabled;
    }
    return flag.defaultValue;
  };

  return (
    <div>
      {!loaded && (
        <p className="text-xs text-gray-400 mb-4">Loading overrides…</p>
      )}

      <div className="space-y-8">
        {CATEGORY_ORDER.map(({ id, label }) => {
          const flags = flagsByCategory[id];
          if (!flags || flags.length === 0) return null;
          return (
            <CollapsibleSection key={id} sectionId={`featureFlags:${id}`} title={label}>
              {flags.map((flag) => (
                <ToggleRow
                  key={flag.id}
                  flag={flag}
                  override={overrides[flag.id]}
                  effective={getEffective(flag)}
                  pending={!!pending[flag.id]}
                  onToggle={(next) => handleToggle(flag, next)}
                />
              ))}
            </CollapsibleSection>
          );
        })}
      </div>

      <Modal
        isOpen={!!confirmFlag}
        onClose={() => setConfirmFlag(null)}
        title="Enable destructive flag?"
      >
        {confirmFlag && (
          <div>
            <p className="text-sm text-gray-700">
              You&apos;re about to enable{' '}
              <span className="font-semibold">{confirmFlag.label}</span>. This
              flag is marked <span className="text-red-600 font-medium">destructive</span>
              {confirmFlag.description ? ` — ${confirmFlag.description}` : ''}.
            </p>
            <p className="text-sm text-gray-700 mt-3">
              Anyone with admin access will immediately see the gated UI. Continue?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmFlag(null)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  const flag = confirmFlag;
                  setConfirmFlag(null);
                  await persistToggle(flag, true);
                }}
              >
                Enable
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/**
 * Configuration → Catalog Indexes & Recompute.
 *
 * Derivation Settings on top; the Algolia pushes and derived-field recomputes
 * (calibre reference counts, acquired-by brand IDs, manufacturer ancestors) sit
 * in the Maintenance & Tools footer.
 */
export function CatalogIndexesTab() {
  return (
    <div className="space-y-8">
      <DerivationSettingsSection />
      <ReferenceStopwordsSection />

      <ToolsSubsection>
        <PushCalibreIndexSettingsSection />
        <PushCalibreFamilyIndexSettingsSection />
        <PushElectronicModuleIndexSettingsSection />
        <PushManufacturerIndexSettingsSection />
        <PushWinderIndexSettingsSection />
        <BackfillCalibresToAlgoliaSection />
        <BackfillCalibreFamiliesToAlgoliaSection />
        <BackfillElectronicModulesToAlgoliaSection />
        <BackfillManufacturersToAlgoliaSection />
        <BackfillParentOrgsToAlgoliaSection />
        <BackfillWindersToAlgoliaSection />
        <BackfillWatchReferenceFamilyIdsSection />
        <BackfillReferenceImagesFromUserStorageSection />
        <RecomputeCalibreReferenceCountsSection />
        <RecomputeWatchBrandReferenceCountsSection />
        <RecomputeElectronicModuleReferenceCountsSection />
        <RecomputeAcquiredByBrandIdsSection />
        <RecomputeManufacturerAncestorsSection />
      </ToolsSubsection>
    </div>
  );
}

/**
 * Backfill calibre tier types → stamp `tierType: 'general'` on every existing
 * tier that lacks one. Phase 1 of the tier-types feature: all tiers created
 * before the field existed are manufacturer-defined grades (Standard/Elaboré/
 * Top/…), i.e. general. Fill-empty only (never touches a hand-set type),
 * idempotent. Run dry first, then write. Writing re-indexes each touched calibre
 * to Algolia via watchlock's onCustomCalibreWrite trigger.
 */
function BackfillTierTypesSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [result, setResult] = useState<BackfillTierTypesResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await backfillTierTypesGeneral(dryRun);
      setResult(r);
      setErrorsExpanded(false);
      const summary =
        `${dryRun ? 'Dry run' : 'Backfill'} complete: ` +
        `${r.scanned} scanned, ` +
        `${dryRun ? 'would stamp' : 'stamped'} ${r.tiersStamped} tier(s) ` +
        `across ${r.calibresUpdated} calibre(s)` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillTierTypesGeneral failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="backfillTierTypesGeneral"
      title="Backfill calibre tier types (general)"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Stamps{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            tierType: &apos;general&apos;
          </code>{' '}
          on every existing calibre tier that lacks one. Tiers created before the
          tier-types feature are all manufacturer-defined grades (Standard /
          Elaboré / Top / Chronometer) — i.e. general. Fill-empty only: a tier
          already typed (including a hand-set{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            brand-specific
          </code>
          ) is never touched, so re-running is a no-op. Run a dry run first.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Calibres scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Calibres to update' : 'Calibres updated'}
                value={result.calibresUpdated}
              />
              <ResultStat
                label={result.dryRun ? 'Tiers to stamp' : 'Tiers stamped'}
                value={result.tiersStamped}
              />
              <ResultStat
                label="Errors"
                value={result.errors.length}
                danger={result.errors.length > 0}
              />
            </div>

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Calibre ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors.slice(0, 100).map((e, i) => (
                            <tr key={`${e.calibreId}/${i}`}>
                              <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                {e.calibreId}
                              </td>
                              <td className="px-3 py-1.5 text-red-600 align-top">
                                {e.error}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Backfill tier types (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This stamps{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              tierType: &apos;general&apos;
            </code>{' '}
            on every un-typed calibre tier and re-indexes the touched calibres in
            Algolia. It never overwrites an existing tier type. Run a dry run
            first. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Normalize the stored calibre corpus onto the canonical British spelling.
 *
 * The code-side fold shipped separately (the shared comparison key treats
 * "Caliber" and "Calibre" as the same string, so lookups already work either
 * way). This is the data half: rewriting `name` — and, opt-in, `description` —
 * so the corpus reads canonically.
 *
 * AKAs are deliberately untouched: they still resolve through the shared fold,
 * and leaving them is what keeps the American spelling findable. Doc ids are
 * untouched too — an id is a storage key, and renaming one deletes and
 * re-creates the doc, orphaning its image storagePaths.
 *
 * Docs whose rename would collide with a name another calibre already owns are
 * skipped WHOLE and listed: merging two identically-named calibres is an
 * editorial call, not this tool's. Dry run first; re-running is a no-op.
 */
function NormalizeCalibreSpellingSection({
  onOpenCalibre,
}: {
  onOpenCalibre?: (calibreId: string) => void;
}) {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [includeDescriptions, setIncludeDescriptions] = useState(false);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [skippedExpanded, setSkippedExpanded] = useState(false);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [result, setResult] = useState<NormalizeCalibreSpellingResult | null>(null);

  const openConfirm = () => {
    setConfirmText('');
    setConfirmWrite(true);
  };

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await normalizeCalibreSpelling({ dryRun, includeDescriptions });
      setResult(r);
      setSkippedExpanded(false);
      setChangesExpanded(false);
      const summary =
        `${dryRun ? 'Dry run' : 'Normalize'} complete: ` +
        `${r.scanned} scanned, ` +
        `${dryRun ? 'would write' : 'wrote'} ${dryRun ? r.wouldWrite : r.written} doc(s) ` +
        `(${r.nameChanges} name, ${r.descriptionChanges} description)` +
        (r.skipped.length ? `, ${r.skipped.length} skipped` : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length || r.skipped.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('normalizeCalibreSpelling failed:', err);
      toast.error(
        err instanceof Error
          ? `Normalize failed: ${err.message}`
          : 'Normalize failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="normalizeCalibreSpelling"
      title="Normalize calibre spelling (Caliber → Calibre)"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Rewrites the stored calibre corpus onto the canonical British spelling.{' '}
          <strong>Names always</strong>; descriptions only when you tick the box
          below. Case is preserved per word (
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Caliber</code>{' '}
          →{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">Calibre</code>,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">CALIBER</code>{' '}
          →{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">CALIBRE</code>
          ), and the proper noun{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            Caliber Corner
          </code>{' '}
          is exempt.
        </p>
        <p className="text-sm text-gray-600">
          <strong>AKAs and document ids are never touched.</strong> AKAs still
          resolve through the shared normalizer, which is what keeps the American
          spelling findable; a doc id is a storage key and renaming one would
          orphan the calibre&apos;s images. Calibres whose new name would collide
          with an existing calibre are skipped whole and listed below for a
          manual merge. Re-running is a no-op. Dry run first.
        </p>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeDescriptions}
            onChange={(e) => setIncludeDescriptions(e.target.checked)}
            disabled={running !== null}
            className="rounded border-gray-300"
          />
          Include descriptions (opt-in)
        </label>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={openConfirm}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Normalize'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              <Badge variant={result.includeDescriptions ? 'info' : 'default'}>
                {result.includeDescriptions
                  ? 'names + descriptions'
                  : 'names only'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Calibres scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Docs to write' : 'Docs written'}
                value={result.dryRun ? result.wouldWrite : result.written}
              />
              <ResultStat label="Name changes" value={result.nameChanges} />
              <ResultStat
                label="Description changes"
                value={result.descriptionChanges}
              />
              <ResultStat
                label="Skipped (collisions)"
                value={result.skipped.length}
                danger={result.skipped.length > 0}
              />
            </div>

            {result.skipped.length > 0 && (
              <div className="mt-3 border border-amber-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setSkippedExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${skippedExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.skipped.length} skipped — duplicate name collision
                  {result.skipped.length === 1 ? '' : 's'}, needs a manual merge
                  {result.skippedTruncated ? ' (list capped)' : ''} — click to{' '}
                  {skippedExpanded ? 'collapse' : 'expand'}
                </button>

                {skippedExpanded && (
                  <div className="border-t border-amber-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Would become
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              This calibre
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Already taken by
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Brand
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.skipped.map((s) => (
                            <tr key={s.sourceId}>
                              <td className="px-3 py-1.5 text-gray-900 align-top">
                                {s.name}
                              </td>
                              <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                {onOpenCalibre ? (
                                  <button
                                    type="button"
                                    onClick={() => onOpenCalibre(s.sourceId)}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {s.sourceId} →
                                  </button>
                                ) : (
                                  s.sourceId
                                )}
                              </td>
                              <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                {onOpenCalibre ? (
                                  <button
                                    type="button"
                                    onClick={() => onOpenCalibre(s.targetId)}
                                    className="text-blue-600 hover:underline"
                                  >
                                    {s.targetId} →
                                  </button>
                                ) : (
                                  s.targetId
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-gray-500 align-top">
                                {s.brandId ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {result.changes.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setChangesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${changesExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.changes.length} change
                  {result.changes.length === 1 ? '' : 's'} shown
                  {result.changesTruncated ? ' (first 50 of more)' : ''} — click
                  to {changesExpanded ? 'collapse' : 'expand'}
                </button>

                {changesExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-96 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Calibre ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Field
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Before → after
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.changes.flatMap((c) => {
                            const rows: ReactNode[] = [];
                            if (c.name) {
                              rows.push(
                                <tr key={`${c.id}/name`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {onOpenCalibre ? (
                                      <button
                                        type="button"
                                        onClick={() => onOpenCalibre(c.id)}
                                        className="text-blue-600 hover:underline"
                                      >
                                        {c.id} →
                                      </button>
                                    ) : (
                                      c.id
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-500 align-top">
                                    name
                                  </td>
                                  <td className="px-3 py-1.5 align-top">
                                    <span className="line-through text-red-600">
                                      {c.name.before}
                                    </span>{' '}
                                    <span className="text-green-700">
                                      {c.name.after}
                                    </span>
                                  </td>
                                </tr>,
                              );
                            }
                            if (c.description) {
                              rows.push(
                                <tr key={`${c.id}/description`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {c.name ? '' : c.id}
                                  </td>
                                  <td className="px-3 py-1.5 text-gray-500 align-top">
                                    description
                                  </td>
                                  <td className="px-3 py-1.5 align-top text-gray-700">
                                    {c.description.after}
                                  </td>
                                </tr>,
                              );
                            }
                            return rows;
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white p-3">
                <div className="text-sm font-medium text-red-700 mb-1">
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'}
                </div>
                <ul className="text-xs text-red-600 space-y-0.5">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={`${e.calibreId}/${i}`}>
                      <span className="font-mono text-gray-700">
                        {e.calibreId}
                      </span>{' '}
                      — {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Normalize calibre spelling (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This rewrites calibre <strong>names</strong>
            {includeDescriptions ? (
              <>
                {' '}
                and <strong>descriptions</strong>
              </>
            ) : (
              <> (descriptions are NOT included — tick the box first if you want them)</>
            )}{' '}
            in the live catalogue, folding{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              Caliber
            </code>{' '}
            onto{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              Calibre
            </code>
            , and re-indexes every touched calibre in Algolia. AKAs and document
            ids are untouched. Run a dry run first.
          </p>
          <p className="mt-3 text-sm text-gray-700">
            Type{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              NORMALIZE
            </code>{' '}
            to confirm:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="NORMALIZE"
            autoComplete="off"
            className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={confirmText !== 'NORMALIZE'}
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Dial marker type / numerals split — lookup repair + reference migration.
 *
 * The ref editor's two dial fields both read lookup_dial_numerals, so a marker
 * CONSTRUCTION type and a marker STYLE were drawn from one vocabulary and the
 * two values landed in whichever field the curator happened to use. They are
 * separate concepts with separate lookups now:
 *
 *   Marker Type (dialAndHands.indexes)  → lookup_dial_marker_type
 *   Numerals    (dialAndHands.numerals) → lookup_dial_numerals
 *
 * This panel runs the DATA half only: reshape the stored refs to arrays and
 * move misfiled numeral slugs out of `indexes`. Dry-run by default, idempotent.
 *
 * The lookup CONFIG half (both collections multi-select, `mixed` retired via
 * isActive:false) is done by hand in the Lookup Manager, which already has UI
 * for both writes — there is deliberately no button here that would be a second
 * way to make the same edit. The migration reads those collections to decide
 * what a numeral is, so it should run after they are set.
 */
function DialMarkerNumeralsSplitSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [result, setResult] =
    useState<MigrateDialIndexesNumeralsResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await migrateDialIndexesNumerals(dryRun);
      setResult(r);
      setChangesExpanded(dryRun);
      const summary =
        `${dryRun ? 'Dry run' : 'Migration'} complete: ` +
        `${r.scanned} refs scanned, ` +
        `${dryRun ? 'would update' : 'updated'} ${dryRun ? r.wouldUpdate : r.updated}` +
        ` (${r.movedNumeralsFromIndexes} moved numerals, ` +
        `${r.mixedDroppedIndexes + r.mixedDroppedNumerals} mixed dropped)` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) toast(summary, { icon: '⚠️' });
      else toast.success(summary);
    } catch (err) {
      console.error('migrateDialIndexesNumerals failed:', err);
      toast.error(
        err instanceof Error
          ? `Migration failed: ${err.message}`
          : 'Migration failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="dialMarkerNumeralsSplit"
      title="Dial marker type / numerals split"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          The editor&apos;s two dial fields both read{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_dial_numerals
          </code>
          , so marker CONSTRUCTION and marker STYLE were drawn from one
          vocabulary. <strong>Marker Type</strong> (stored at{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            dialAndHands.indexes
          </code>
          , the name the RN app reads) now reads{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            lookup_dial_marker_type
          </code>
          ; <strong>Numerals</strong> keeps the numerals lookup.
        </p>

        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 space-y-1">
          <div className="text-sm font-semibold text-amber-900">
            Configure the lookups first — in the Lookup Manager
          </div>
          <p className="text-sm text-amber-800">
            Both{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              lookup_dial_marker_type
            </code>{' '}
            and{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              lookup_dial_numerals
            </code>{' '}
            need <strong>multi-select</strong> on (Features row), and the{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">mixed</code>{' '}
            value set to <code className="text-xs">isActive: false</code> in both
            (retired, not deleted — a multi-select field says
            &quot;mixed&quot; by picking two values, and old refs that name it
            stay readable). Multi-select also drives the AI: the enrich prompt
            asks for an array only when the collection carries the flag.
          </p>
          <p className="text-sm text-amber-800">
            The migration below reads these two lists to tell a numeral from a
            marker type, so it can only file values correctly once both
            collections hold the vocabulary you want.
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
          <div className="text-sm font-semibold text-gray-900">
            Migrate the references
          </div>
          <p className="text-sm text-gray-600">
            For every reference: wrap both fields to arrays, MOVE any value in{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              indexes
            </code>{' '}
            that belongs to the numerals vocabulary (and not the marker-type one)
            into{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              numerals
            </code>
            , and drop{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              mixed
            </code>{' '}
            from both — dropped defensively whatever the lookup says, since a ref
            can carry it from before the value was retired. Slugs in BOTH
            vocabularies are ambiguous and left alone; custom values are never
            discarded. A field left empty is deleted rather than written as{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">[]</code>
            . Re-running is a no-op. Dry run first.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              loading={running === 'dry'}
              disabled={running !== null}
              onClick={() => void run(true)}
            >
              Dry run
            </Button>
            <Button
              variant="primary"
              loading={running === 'write'}
              disabled={running !== null || !result}
              onClick={() => setConfirmWrite(true)}
            >
              Run &amp; write
            </Button>
          </div>
          {!result && (
            <p className="text-xs text-gray-500">
              Run a dry run first — the write button confirms against its count.
            </p>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Migration'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Refs scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Refs to update' : 'Refs updated'}
                value={result.dryRun ? result.wouldUpdate : result.updated}
              />
              <ResultStat
                label="Numerals moved out of indexes"
                value={result.movedNumeralsFromIndexes}
              />
              <ResultStat
                label="'mixed' dropped (indexes)"
                value={result.mixedDroppedIndexes}
              />
              <ResultStat
                label="'mixed' dropped (numerals)"
                value={result.mixedDroppedNumerals}
              />
            </div>

            {result.ambiguousSlugs.length > 0 && (
              <p className="mt-2 text-xs text-amber-700">
                Ambiguous slug{result.ambiguousSlugs.length === 1 ? '' : 's'} —
                present in both lookups, so never moved:{' '}
                <span className="font-mono">
                  {result.ambiguousSlugs.join(', ')}
                </span>
              </p>
            )}

            {result.changes.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setChangesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  {result.changes.length} change
                  {result.changes.length === 1 ? '' : 's'} shown
                  {result.changesTruncated ? ' (list capped)' : ''} — click to{' '}
                  {changesExpanded ? 'collapse' : 'expand'}
                </button>
                {changesExpanded && (
                  <div className="border-t border-gray-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 text-gray-500">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Reference
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            indexes (marker type)
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            numerals
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.changes.map((c) => (
                          <tr key={`${c.brandId ?? '?'}/${c.refId}`}>
                            <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                              {c.brandId ? `${c.brandId}/` : ''}
                              {c.refId}
                            </td>
                            <td className="px-3 py-1.5 align-top">
                              <span className="line-through text-red-600">
                                {JSON.stringify(c.indexesBefore ?? null)}
                              </span>{' '}
                              <span className="text-green-700">
                                {JSON.stringify(c.indexesAfter)}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 align-top">
                              <span className="line-through text-red-600">
                                {JSON.stringify(c.numeralsBefore ?? null)}
                              </span>{' '}
                              <span className="text-green-700">
                                {JSON.stringify(c.numeralsAfter)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white p-3">
                <div className="text-sm font-medium text-red-700 mb-1">
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'}
                </div>
                <ul className="text-xs text-red-600 space-y-0.5">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={`${e.refId}/${i}`}>
                      <span className="font-mono text-gray-700">{e.refId}</span>{' '}
                      — {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Migrate dial indexes / numerals (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            The last dry run found{' '}
            <strong>
              {result?.wouldUpdate ?? 0} reference
              {(result?.wouldUpdate ?? 0) === 1 ? '' : 's'}
            </strong>{' '}
            to update — {result?.movedNumeralsFromIndexes ?? 0} with a numeral to
            move out of{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              indexes
            </code>
            , and{' '}
            {(result?.mixedDroppedIndexes ?? 0) +
              (result?.mixedDroppedNumerals ?? 0)}{' '}
            carrying the retired{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              mixed
            </code>
            . Writing is idempotent — a second run finds nothing to do.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Crown type / crystal multi-select split — reference data migration.
 *
 * Both fields were single-value, and both are semantically several things at
 * once, so an import crammed the source's whole phrase into one unvalidated
 * custom value ("Screw-down, Twinlock double waterproofness system"). They are
 * multi-select now, over one combined vocabulary each.
 *
 * This panel runs the DATA half only: wrap both fields to arrays and resolve
 * each comma-split part to a lookup slug. The lookup CONFIG half (both
 * collections multi-select, plus the values the vocabulary is missing) is done
 * by hand in the Lookup Manager, which already has UI for both — there is
 * deliberately no button here that would be a second way to make the same edit.
 * The migration reads those collections to resolve anything at all, so it has
 * to run after they are set.
 */
function CrownCrystalSplitSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [unresolvedExpanded, setUnresolvedExpanded] = useState(false);
  const [result, setResult] = useState<MigrateCrownTypeCrystalResult | null>(
    null,
  );

  const unresolvedCount =
    (result?.unresolvedCrownRefs.length ?? 0) +
    (result?.unresolvedCrystalRefs.length ?? 0);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await migrateCrownTypeCrystal(dryRun);
      setResult(r);
      setChangesExpanded(dryRun);
      setUnresolvedExpanded(false);
      const unresolved =
        r.unresolvedCrownRefs.length + r.unresolvedCrystalRefs.length;
      const summary =
        `${dryRun ? 'Dry run' : 'Migration'} complete: ` +
        `${r.scanned} refs scanned, ` +
        `${dryRun ? 'would update' : 'updated'} ${dryRun ? r.wouldUpdate : r.updated}` +
        ` (${r.crownCanonicalized} crown + ${r.crystalCanonicalized} crystal canonicalized` +
        `, ${unresolved} need manual review)` +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) toast(summary, { icon: '⚠️' });
      else toast.success(summary);
    } catch (err) {
      console.error('migrateCrownTypeCrystal failed:', err);
      toast.error(
        err instanceof Error
          ? `Migration failed: ${err.message}`
          : 'Migration failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="crownCrystalSplit"
      title="Crown type / crystal multi-select split"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          A crown IS screw-down AND Twinlock; a crystal HAS a material AND a
          cyclops lens. As single-value fields,{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            crownType
          </code>{' '}
          and{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            crystal
          </code>{' '}
          swallowed the source&apos;s whole phrase as one custom value — a Rolex
          Datejust imported as{' '}
          <span className="font-mono text-xs">
            &quot;Screw-down, Twinlock double waterproofness system&quot;
          </span>
          . Both are multi-select now, each over ONE combined vocabulary:
          attachment + sealing system + shape for the crown, material + features
          for the crystal.
        </p>

        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 space-y-1">
          <div className="text-sm font-semibold text-amber-900">
            Configure the lookups first — in the Lookup Manager
          </div>
          <p className="text-sm text-amber-800">
            Both{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              lookup_crown_types
            </code>{' '}
            and{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              lookup_crystal_materials
            </code>{' '}
            need <strong>multi-select</strong> on (Features row), and need the
            values the vocabulary is missing —{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              twinlock
            </code>{' '}
            /{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              triplock
            </code>{' '}
            for the crown,{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              cyclops_lens
            </code>{' '}
            /{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">domed</code>{' '}
            /{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">flat</code>{' '}
            for the crystal. Multi-select also drives the AI: the enrich and
            extractor prompts ask for an array only when the collection carries
            the flag.
          </p>
          <p className="text-sm text-amber-800">
            The migration resolves stored text against these two lists, so a
            value that isn&apos;t there yet can only be reported for manual
            review. Adding it and re-running is the fix — the second pass picks
            it up.
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
          <div className="text-sm font-semibold text-gray-900">
            Migrate the references
          </div>
          <p className="text-sm text-gray-600">
            For every reference: split the stored value on commas, then resolve
            each part to a slug (exact slug → synonym → display name → the value
            found inside the surrounding prose, so{' '}
            <span className="font-mono text-xs">
              &quot;Twinlock double waterproofness system&quot;
            </span>{' '}
            becomes{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              twinlock
            </code>
            ). Canonicalization is <strong>all-or-nothing per field</strong>: if
            any part matches nothing, the field keeps its raw text (as an array)
            and the ref is listed below for manual review rather than
            half-converted. Nothing is ever discarded. A field left empty is
            deleted rather than written as{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">[]</code>
            . Re-running is a no-op. Dry run first.
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              loading={running === 'dry'}
              disabled={running !== null}
              onClick={() => void run(true)}
            >
              Dry run
            </Button>
            <Button
              variant="primary"
              loading={running === 'write'}
              disabled={running !== null || !result}
              onClick={() => setConfirmWrite(true)}
            >
              Run &amp; write
            </Button>
          </div>
          {!result && (
            <p className="text-xs text-gray-500">
              Run a dry run first — the write button confirms against its count.
            </p>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Migration'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Refs scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Refs to update' : 'Refs updated'}
                value={result.dryRun ? result.wouldUpdate : result.updated}
              />
              <ResultStat
                label="Crown canonicalized"
                value={result.crownCanonicalized}
              />
              <ResultStat
                label="Crystal canonicalized"
                value={result.crystalCanonicalized}
              />
              <ResultStat label="Need manual review" value={unresolvedCount} />
            </div>

            {unresolvedCount > 0 && (
              <div className="mt-3 border border-amber-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setUnresolvedExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-800 hover:bg-amber-50 rounded-lg"
                >
                  {unresolvedCount} ref{unresolvedCount === 1 ? '' : 's'} with a
                  value that matched no lookup entry
                  {result.unresolvedTruncated ? ' (list capped)' : ''} — click to{' '}
                  {unresolvedExpanded ? 'collapse' : 'expand'}
                </button>
                {unresolvedExpanded && (
                  <div className="border-t border-amber-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-amber-50 text-amber-800">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Field
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Reference
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Unmatched text
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {[
                          ...result.unresolvedCrownRefs.map(
                            (u) => ['crownType', u] as const,
                          ),
                          ...result.unresolvedCrystalRefs.map(
                            (u) => ['crystal', u] as const,
                          ),
                        ].map(([field, u], i) => (
                          <tr key={`${field}/${u.brandId ?? '?'}/${u.refId}/${i}`}>
                            <td className="px-3 py-1.5 font-mono text-gray-500 align-top">
                              {field}
                            </td>
                            <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                              {u.brandId ? `${u.brandId}/` : ''}
                              {u.refId}
                            </td>
                            <td className="px-3 py-1.5 align-top text-amber-900">
                              {u.parts.join('  |  ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {result.changes.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setChangesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  {result.changes.length} change
                  {result.changes.length === 1 ? '' : 's'} shown
                  {result.changesTruncated ? ' (list capped)' : ''} — click to{' '}
                  {changesExpanded ? 'collapse' : 'expand'}
                </button>
                {changesExpanded && (
                  <div className="border-t border-gray-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 text-gray-500">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Reference
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            crownType
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            crystal
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.changes.map((c) => (
                          <tr key={`${c.brandId ?? '?'}/${c.refId}`}>
                            <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                              {c.brandId ? `${c.brandId}/` : ''}
                              {c.refId}
                            </td>
                            <td className="px-3 py-1.5 align-top">
                              <span className="line-through text-red-600">
                                {JSON.stringify(c.crownBefore ?? null)}
                              </span>{' '}
                              <span
                                className={
                                  c.crownResolved
                                    ? 'text-green-700'
                                    : 'text-amber-700'
                                }
                              >
                                {JSON.stringify(c.crownAfter)}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 align-top">
                              <span className="line-through text-red-600">
                                {JSON.stringify(c.crystalBefore ?? null)}
                              </span>{' '}
                              <span
                                className={
                                  c.crystalResolved
                                    ? 'text-green-700'
                                    : 'text-amber-700'
                                }
                              >
                                {JSON.stringify(c.crystalAfter)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100">
                      Green = canonicalized to slugs. Amber = raw text kept
                      (something in it matched no lookup entry).
                    </p>
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white p-3">
                <div className="text-sm font-medium text-red-700 mb-1">
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'}
                </div>
                <ul className="text-xs text-red-600 space-y-0.5">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={`${e.refId}/${i}`}>
                      <span className="font-mono text-gray-700">{e.refId}</span>{' '}
                      — {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Migrate crown type / crystal (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            The last dry run found{' '}
            <strong>
              {result?.wouldUpdate ?? 0} reference
              {(result?.wouldUpdate ?? 0) === 1 ? '' : 's'}
            </strong>{' '}
            to update — {result?.crownCanonicalized ?? 0} crown and{' '}
            {result?.crystalCanonicalized ?? 0} crystal values fully
            canonicalized to slugs, {unresolvedCount} left as raw text for
            manual review. Writing is idempotent — a second run finds nothing to
            do, and re-running after adding the missing lookup values resolves
            the leftovers.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Calibre balanceType multi-select + micro-rotor cleanup — data migration.
 *
 * A balance wheel is several things at once: a material (Glucydur, silicon), a
 * construction pattern (variable-inertia, screw balance) and optionally a
 * free-sprung regulation architecture. One slot could never say that, so
 * `balanceType` became `string[]` in shared-types v1.91.0 — and every calibre
 * written before the flip still holds the bare scalar.
 *
 * Riding along: `micro_rotor` was seeded onto `lookup_balance_type` by mistake.
 * It says where the winding mass sits, not what the balance is made of, and
 * `lookup_winding_architecture` already carries the same slug. The sweep moves
 * it across.
 *
 * ONE pass does both, which is what makes the ordering a fact rather than an
 * instruction: the micro-rotor step always sees the array shape the wrap just
 * produced. Turn "Also move micro_rotor" off to run the wrap alone and inspect
 * the intermediate state — the micro-rotor count is reported either way.
 *
 * Retiring the `micro_rotor` VALUE itself is a hand edit in the Lookup Manager
 * (the value editor already writes isActive), matching the call the dial
 * marker-type split made. There is deliberately no button for it here.
 */
function CalibreBalanceTypeSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [includeMicroRotor, setIncludeMicroRotor] = useState(true);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [conflictsExpanded, setConflictsExpanded] = useState(false);
  const [result, setResult] = useState<MigrateCalibreBalanceTypeResult | null>(
    null,
  );

  const conflictCount = result?.windingArchitectureConflicts.length ?? 0;

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await migrateCalibreBalanceType(dryRun, includeMicroRotor);
      setResult(r);
      setChangesExpanded(dryRun);
      setConflictsExpanded(r.windingArchitectureConflicts.length > 0);
      const summary =
        `${dryRun ? 'Dry run' : 'Migration'} complete: ` +
        `${r.scanned} calibres scanned, ` +
        `${dryRun ? 'would update' : 'updated'} ${dryRun ? r.wouldUpdate : r.updated}` +
        ` (${r.wrapped} wrapped, ${r.microRotorMoved} micro-rotor moved` +
        `, ${r.tierOverridesWrapped} tier override(s))` +
        (r.windingArchitectureConflicts.length
          ? `, ${r.windingArchitectureConflicts.length} architecture conflict(s)`
          : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length || r.windingArchitectureConflicts.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('migrateCalibreBalanceType failed:', err);
      toast.error(
        err instanceof Error
          ? `Migration failed: ${err.message}`
          : 'Migration failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="calibreBalanceType"
      title="Calibre balance type multi-select + micro-rotor cleanup"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          A balance wheel is several things at once — a material (Glucydur,
          silicon), a construction pattern (variable-inertia, screw balance) and
          optionally a free-sprung regulation architecture. One slot could never
          say that, so{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            balanceType
          </code>{' '}
          became a multi-select in shared-types v1.91.0. Calibres written before
          the flip still hold the bare scalar, so a Rolex Chronometer balance
          reads{' '}
          <span className="font-mono text-xs">&quot;glucydur&quot;</span> where it
          should read{' '}
          <span className="font-mono text-xs">
            [&quot;glucydur&quot;, &quot;variable_inertia&quot;,
            &quot;free-sprung&quot;]
          </span>
          .
        </p>

        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 space-y-1">
          <div className="text-sm font-semibold text-amber-900">
            Retire the micro_rotor value by hand — in the Lookup Manager
          </div>
          <p className="text-sm text-amber-800">
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              micro_rotor
            </code>{' '}
            was seeded onto{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              lookup_balance_type
            </code>{' '}
            by mistake — it describes where the winding mass sits, not the
            balance wheel, and{' '}
            <code className="text-xs bg-white px-1.5 py-0.5 rounded">
              lookup_winding_architecture
            </code>{' '}
            already carries the same slug. This tool moves the DATA. Marking the
            value itself inactive is a hand edit in the Lookup Manager, which
            already has the Active toggle — there is deliberately no button here
            that would be a second way to make the same write.
          </p>
          <p className="text-sm text-amber-800">
            Order does not matter: the sweep strips the slug regardless of what
            the lookup says.
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
          <div className="text-sm font-semibold text-gray-900">
            Migrate the calibres
          </div>
          <p className="text-sm text-gray-600">
            For every calibre: wrap{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              data.balanceType
            </code>{' '}
            to an array (and any tier&apos;s{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              overrides.balanceType
            </code>{' '}
            with it), then move{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              micro_rotor
            </code>{' '}
            to{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              data.windingArchitecture
            </code>
            . Both in ONE pass, so the second step always sees the shape the
            first produced. A calibre that already names a{' '}
            <strong>different</strong> architecture keeps it and is listed below
            for review — the bogus balance slug is still stripped. A balance list
            left empty is deleted rather than written as{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">[]</code>
            . Re-running is a no-op. Dry run first.
          </p>

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={includeMicroRotor}
              disabled={running !== null}
              onChange={(e) => {
                setIncludeMicroRotor(e.target.checked);
                // The last result was produced under the other setting, so its
                // count no longer describes what a write would do.
                setResult(null);
              }}
            />
            <span>
              Also move <span className="font-mono text-xs">micro_rotor</span> to
              the winding architecture{' '}
              <span className="text-gray-500">
                — off runs the wrap alone; the count is still reported, so you
                can size the remaining work before committing to it
              </span>
            </span>
          </label>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              loading={running === 'dry'}
              disabled={running !== null}
              onClick={() => void run(true)}
            >
              Dry run
            </Button>
            <Button
              variant="primary"
              loading={running === 'write'}
              disabled={running !== null || !result}
              onClick={() => setConfirmWrite(true)}
            >
              Run &amp; write
            </Button>
          </div>
          {!result && (
            <p className="text-xs text-gray-500">
              Run a dry run first — the write button confirms against its count.
            </p>
          )}
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Migration'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              {!result.includeMicroRotor && (
                <Badge variant="warning">wrap only</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Calibres scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'To update' : 'Updated'}
                value={result.dryRun ? result.wouldUpdate : result.updated}
              />
              <ResultStat label="Wrapped to array" value={result.wrapped} />
              <ResultStat
                label="Tier overrides wrapped"
                value={result.tierOverridesWrapped}
              />
              <ResultStat
                label="micro_rotor found"
                value={result.microRotorFound}
              />
              <ResultStat
                label="micro_rotor moved"
                value={result.microRotorMoved}
              />
              <ResultStat
                label="balanceType removed"
                value={result.balanceTypeDeleted}
              />
              <ResultStat
                label="Architecture conflicts"
                value={conflictCount}
                danger={conflictCount > 0}
              />
            </div>

            {conflictCount > 0 && (
              <div className="mt-3 border border-amber-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setConflictsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-800 hover:bg-amber-50 rounded-lg"
                >
                  {conflictCount} calibre{conflictCount === 1 ? '' : 's'} already
                  named a different winding architecture — left untouched
                  {result.conflictsTruncated ? ' (list capped)' : ''} — click to{' '}
                  {conflictsExpanded ? 'collapse' : 'expand'}
                </button>
                {conflictsExpanded && (
                  <div className="border-t border-amber-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-amber-50 text-amber-800">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Calibre
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            windingArchitecture kept
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {result.windingArchitectureConflicts.map((c) => (
                          <tr key={c.calibreId}>
                            <td className="px-3 py-1.5 align-top">
                              <span className="text-gray-800">
                                {c.name ?? '—'}
                              </span>{' '}
                              <span className="font-mono text-gray-500">
                                {c.calibreId}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 font-mono align-top text-amber-900">
                              {c.existing}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="px-3 py-2 text-xs text-amber-800 border-t border-amber-100">
                      The bogus{' '}
                      <span className="font-mono">micro_rotor</span> was still
                      stripped off the balance list. Decide by hand which
                      architecture is right for these.
                    </p>
                  </div>
                )}
              </div>
            )}

            {result.changes.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setChangesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  {result.changes.length} change
                  {result.changes.length === 1 ? '' : 's'} shown
                  {result.changesTruncated ? ' (list capped)' : ''} — click to{' '}
                  {changesExpanded ? 'collapse' : 'expand'}
                </button>
                {changesExpanded && (
                  <div className="border-t border-gray-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 text-gray-500">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Calibre
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            balanceType
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Also
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.changes.map((c) => (
                          <tr key={c.calibreId}>
                            <td className="px-3 py-1.5 align-top">
                              <span className="text-gray-800">
                                {c.name ?? '—'}
                              </span>{' '}
                              <span className="font-mono text-gray-500">
                                {c.calibreId}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 align-top">
                              <span className="line-through text-red-600">
                                {JSON.stringify(c.before ?? null)}
                              </span>{' '}
                              <span
                                className={
                                  c.deleted ? 'text-gray-500' : 'text-green-700'
                                }
                              >
                                {c.deleted
                                  ? 'field removed'
                                  : JSON.stringify(c.after)}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 align-top text-gray-600">
                              {c.windingArchitectureSet && (
                                <span className="text-green-700">
                                  windingArchitecture ={' '}
                                  {c.windingArchitectureSet}
                                </span>
                              )}
                              {c.tierOverridesWrapped > 0 && (
                                <span className="block">
                                  {c.tierOverridesWrapped} tier override
                                  {c.tierOverridesWrapped === 1 ? '' : 's'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white p-3">
                <div className="text-sm font-medium text-red-700 mb-1">
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'}
                </div>
                <ul className="text-xs text-red-600 space-y-0.5">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={`${e.calibreId}/${i}`}>
                      <span className="font-mono text-gray-700">
                        {e.calibreId}
                      </span>{' '}
                      — {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Migrate calibre balance types (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            The last dry run found{' '}
            <strong>
              {result?.wouldUpdate ?? 0} calibre
              {(result?.wouldUpdate ?? 0) === 1 ? '' : 's'}
            </strong>{' '}
            to update — {result?.wrapped ?? 0} wrapped to an array,{' '}
            {result?.microRotorMoved ?? 0} with{' '}
            <span className="font-mono text-xs">micro_rotor</span> moved to the
            winding architecture, {result?.tierOverridesWrapped ?? 0} tier
            override{(result?.tierOverridesWrapped ?? 0) === 1 ? '' : 's'}{' '}
            reshaped, and {result?.balanceTypeDeleted ?? 0} left with no balance
            data and so removed.
            {conflictCount > 0 && (
              <>
                {' '}
                {conflictCount} calibre{conflictCount === 1 ? '' : 's'} already
                name a different winding architecture and keep it.
              </>
            )}{' '}
            {result?.includeMicroRotor === false && (
              <>
                This run wraps only —{' '}
                <span className="font-mono text-xs">micro_rotor</span> stays on
                the balance list.{' '}
              </>
            )}
            Writing is idempotent — a second run finds nothing to do.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/** Running totals across every slice of one backfill session. */
interface CalibreLinkTotals {
  scanned: number;
  alreadyLinked: number;
  placeholder: number;
  unresolvable: number;
  linked: number;
  linkedByCanonical: number;
  linkedByAka: number;
  errors: number;
  slices: number;
}

const EMPTY_CALIBRE_LINK_TOTALS: CalibreLinkTotals = {
  scanned: 0,
  alreadyLinked: 0,
  placeholder: 0,
  unresolvable: 0,
  linked: 0,
  linkedByCanonical: 0,
  linkedByAka: 0,
  errors: 0,
  slices: 0,
};

function addCalibreLinkSlice(
  totals: CalibreLinkTotals,
  slice: BackfillCalibreLinksResult,
): CalibreLinkTotals {
  return {
    scanned: totals.scanned + slice.scanned,
    alreadyLinked: totals.alreadyLinked + slice.alreadyLinked,
    placeholder: totals.placeholder + slice.placeholder,
    unresolvable: totals.unresolvable + slice.unresolvable,
    linked: totals.linked + (slice.dryRun ? slice.wouldLink : slice.linked),
    linkedByCanonical: totals.linkedByCanonical + slice.linkedByCanonical,
    linkedByAka: totals.linkedByAka + slice.linkedByAka,
    errors: totals.errors + slice.errors.length,
    slices: totals.slices + 1,
  };
}

/**
 * Phase 5d — backfill `calibreId` onto references that carry only the legacy
 * `calibre` string.
 *
 * Phases 5b and 5c put the FK on every NEW write: the reference editor, the AI
 * enrich accept path, the bulk action bar and the bulk importer all resolve the
 * calibre they were handed and write the link beside the string. Nothing went
 * back for the references written before them, so this does — under the same
 * Phase 5a resolution rules, mirrored server-side and pinned by a parity test.
 *
 * Fill-empty: a reference that already carries a `calibreId` is counted and left
 * alone whatever its string says, which is also what makes a second run write
 * nothing. It writes `calibreId` and nothing else — no legacy movement fields,
 * and no `updatedAt`, so tens of thousands of references don't jump to the top
 * of every recently-updated ordering to record a migration nobody asked to see.
 *
 * SLICED, and that is not politeness: every reference write fires watchlock's
 * Algolia sync trigger, and a stampede on that trigger is what caused the
 * Firestore read spike on 2026-08-04. One call links at most a slice, hands back
 * a cursor, and Continue runs the next one. Scoping to a single brand narrows it
 * further and is the safest way to start.
 */
function BackfillCalibreLinksSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [brandId, setBrandId] = useState('');
  const [sliceSize, setSliceSize] = useState(5000);
  const [result, setResult] = useState<BackfillCalibreLinksResult | null>(null);
  const [totals, setTotals] = useState<CalibreLinkTotals>(
    EMPTY_CALIBRE_LINK_TOTALS,
  );
  const [unresolvedExpanded, setUnresolvedExpanded] = useState(false);
  const [mismatchesExpanded, setMismatchesExpanded] = useState(false);

  const cursor = result?.nextCursor;
  const mismatchCount = result?.mismatches.length ?? 0;

  const run = async (dryRun: boolean, startAfterPath?: string) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await backfillCalibreLinks(dryRun, {
        brandId: brandId.trim() || undefined,
        startAfterPath,
        maxWrites: sliceSize,
      });
      setResult(r);
      // A fresh run restarts the tally; Continue adds to it.
      setTotals((prev) =>
        addCalibreLinkSlice(
          startAfterPath ? prev : EMPTY_CALIBRE_LINK_TOTALS,
          r,
        ),
      );
      setUnresolvedExpanded(r.unresolvedRows.length > 0 && dryRun);
      setMismatchesExpanded(r.mismatches.length > 0);
      const decided = dryRun ? r.wouldLink : r.linked;
      const summary =
        `${dryRun ? 'Dry run' : 'Backfill'} slice complete: ` +
        `${r.scanned} refs scanned, ` +
        `${dryRun ? 'would link' : 'linked'} ${decided} ` +
        `(${r.linkedByCanonical} by name, ${r.linkedByAka} by alias), ` +
        `${r.alreadyLinked} already linked, ${r.unresolvable} unresolvable` +
        (r.nextCursor ? ' — more to do' : ' — end of scan') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('backfillCalibreLinks failed:', err);
      toast.error(
        err instanceof Error
          ? `Backfill failed: ${err.message}`
          : 'Backfill failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="backfillCalibreLinks"
      title="Backfill calibre links (references with a calibre string but no calibreId)"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Every new write already sets{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            calibreId
          </code>{' '}
          beside the legacy{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            calibre
          </code>{' '}
          string. This links the references written before that, resolving each
          string under the same rules the editor and the bulk bars use — brand-
          scoped aliases, the placeholder blacklist and the catch-all flag all
          apply, and an unscoped alias on a calibre another brand owns is
          refused rather than guessed at.
        </p>

        <p className="text-sm text-gray-600">
          Fill-empty: a reference that already has a link keeps it, whatever its
          string now says. Nothing but{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            calibreId
          </code>{' '}
          is written — no legacy movement fields, and no{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            updatedAt
          </code>
          , so the sweep does not churn every recently-updated ordering.
        </p>

        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 space-y-1">
          <div className="text-sm font-semibold text-amber-900">
            Run it in slices — one write per reference wakes the Algolia sync
          </div>
          <p className="text-sm text-amber-800">
            Roughly 47,000 references are expected to link. Every one of those
            writes fires watchlock&apos;s reference trigger, and a stampede on
            that trigger is what caused the read spike on 4 August. Each call
            stops at the slice size below and hands back a cursor;{' '}
            <strong>Continue</strong> runs the next slice. Scoping to one brand
            narrows it further and is the safest way to start.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <label className="text-sm text-gray-700">
            <span className="block mb-1">
              Brand id{' '}
              <span className="text-gray-500">— blank sweeps every brand</span>
            </span>
            <input
              type="text"
              value={brandId}
              onChange={(e) => {
                setBrandId(e.target.value);
                // Drop the slice with it — a cursor belongs to the brand it was
                // produced under, and Continue must not carry one across.
                setResult(null);
                setTotals(EMPTY_CALIBRE_LINK_TOTALS);
              }}
              disabled={running !== null}
              placeholder="e.g. hublot"
              className="w-56 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </label>
          <label className="text-sm text-gray-700">
            <span className="block mb-1">
              Slice size{' '}
              <span className="text-gray-500">— links decided per call</span>
            </span>
            <input
              type="number"
              min={1}
              max={50000}
              step={500}
              value={sliceSize}
              onChange={(e) => setSliceSize(Number(e.target.value) || 1)}
              disabled={running !== null}
              className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </label>
        </div>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => void run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null || !result}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
          {cursor && (
            <Button
              variant="secondary"
              disabled={running !== null}
              onClick={() => void run(result?.dryRun !== false, cursor)}
            >
              Continue {result?.dryRun === false ? 'writing' : 'dry run'}
            </Button>
          )}
        </div>
        {!result && (
          <p className="text-xs text-gray-500">
            Run a dry run first — the write button confirms against its count.
          </p>
        )}

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Backfill'} slice{' '}
                {totals.slices > 1 ? `#${totals.slices}` : ''}
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
              {result.brandId && (
                <Badge variant="info">brand: {result.brandId}</Badge>
              )}
              {cursor && <Badge variant="warning">more to do</Badge>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <ResultStat label="Refs scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'To link' : 'Linked'}
                value={result.dryRun ? result.wouldLink : result.linked}
              />
              <ResultStat
                label="Matched by name"
                value={result.linkedByCanonical}
              />
              <ResultStat label="Matched by alias" value={result.linkedByAka} />
              <ResultStat
                label="Already linked"
                value={result.alreadyLinked}
              />
              <ResultStat label="No calibre string" value={result.withoutCalibre} />
              <ResultStat label="Placeholder string" value={result.placeholder} />
              <ResultStat
                label="Unresolvable"
                value={result.unresolvable}
                danger={result.unresolvable > 0}
              />
            </div>

            {totals.slices > 1 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white p-3">
                <div className="text-sm font-semibold text-gray-900 mb-2">
                  Session total across {totals.slices} slices
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <ResultStat label="Refs scanned" value={totals.scanned} />
                  <ResultStat
                    label={result.dryRun ? 'To link' : 'Linked'}
                    value={totals.linked}
                  />
                  <ResultStat
                    label="Already linked"
                    value={totals.alreadyLinked}
                  />
                  <ResultStat
                    label="Unresolvable"
                    value={totals.unresolvable}
                    danger={totals.unresolvable > 0}
                  />
                </div>
              </div>
            )}

            {cursor && (
              <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Stopped at the{' '}
                {result.stoppedReason === 'deadline'
                  ? 'time budget'
                  : 'slice size'}{' '}
                after {result.dryRun ? result.wouldLink : result.linked} link
                {(result.dryRun ? result.wouldLink : result.linked) === 1
                  ? ''
                  : 's'}
                . Press Continue to resume from{' '}
                <span className="font-mono text-xs break-all">{cursor}</span>.
              </p>
            )}

            {result.unresolvedRows.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setUnresolvedExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 rounded-lg"
                >
                  {result.unresolvedRows.length} calibre string
                  {result.unresolvedRows.length === 1 ? '' : 's'} the catalogue
                  could not resolve
                  {result.unresolvedTruncated ? ' (list capped)' : ''} — click to{' '}
                  {unresolvedExpanded ? 'collapse' : 'expand'}
                </button>
                {unresolvedExpanded && (
                  <div className="border-t border-gray-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Brand
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Calibre string
                          </th>
                          <th className="text-right font-medium px-3 py-1.5">
                            Refs
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Sample ref ids
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.unresolvedRows.map((row) => (
                          <tr key={`${row.brandId}|${row.calibreString}`}>
                            <td className="px-3 py-1.5 align-top text-gray-800">
                              {row.brandName}
                            </td>
                            <td className="px-3 py-1.5 align-top font-mono text-gray-900">
                              {row.calibreString}
                            </td>
                            <td className="px-3 py-1.5 align-top text-right text-gray-700">
                              {row.refCount}
                            </td>
                            <td className="px-3 py-1.5 align-top font-mono text-gray-500">
                              {row.sampleRefIds.join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="px-3 py-2 text-xs text-gray-600 border-t border-gray-100">
                      Each of these names a calibre the catalogue does not carry
                      under any name this brand may match it by. Add the calibre
                      or the alias, then run the sweep again — it will pick them
                      up.
                    </p>
                  </div>
                )}
              </div>
            )}

            {mismatchCount > 0 && (
              <div className="mt-3 border border-amber-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setMismatchesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-amber-800 hover:bg-amber-50 rounded-lg"
                >
                  {mismatchCount} reference{mismatchCount === 1 ? '' : 's'}{' '}
                  already linked to a calibre their string no longer names —
                  left untouched
                  {result.mismatchesTruncated ? ' (list capped)' : ''} — click to{' '}
                  {mismatchesExpanded ? 'collapse' : 'expand'}
                </button>
                {mismatchesExpanded && (
                  <div className="border-t border-amber-100 max-h-96 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-amber-50 text-amber-800">
                        <tr>
                          <th className="text-left font-medium px-3 py-1.5">
                            Reference
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Calibre string
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            Linked to
                          </th>
                          <th className="text-left font-medium px-3 py-1.5">
                            String resolves to
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {result.mismatches.map((m) => (
                          <tr key={`${m.brandId}|${m.refId}`}>
                            <td className="px-3 py-1.5 align-top font-mono text-gray-700">
                              {m.brandId}/{m.refId}
                            </td>
                            <td className="px-3 py-1.5 align-top font-mono text-gray-900">
                              {m.calibreString}
                            </td>
                            <td className="px-3 py-1.5 align-top font-mono text-gray-700">
                              {m.existingCalibreId}
                            </td>
                            <td className="px-3 py-1.5 align-top font-mono text-amber-900">
                              {m.resolvedCalibreId}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="px-3 py-2 text-xs text-amber-800 border-t border-amber-100">
                      Reported, never corrected. The stored link is the
                      authoritative half by construction, so a string that
                      disagrees with it is a question for a human — usually a
                      renamed alias, occasionally a bad link.
                    </p>
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-red-50 p-3">
                <div className="text-sm font-semibold text-red-800 mb-1">
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'}
                </div>
                <ul className="text-xs text-red-700 space-y-0.5 max-h-40 overflow-auto">
                  {result.errors.map((e) => (
                    <li key={e.refId}>
                      <span className="font-mono">{e.refId}</span>: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Backfill calibre links (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            The last slice found{' '}
            <strong>
              {result?.wouldLink ?? 0} reference
              {(result?.wouldLink ?? 0) === 1 ? '' : 's'}
            </strong>{' '}
            to link
            {result?.brandId ? ` under ${result.brandId}` : ' across all brands'}{' '}
            — {result?.linkedByCanonical ?? 0} matched by a calibre&apos;s own
            name and {result?.linkedByAka ?? 0} by an alias.{' '}
            {result?.alreadyLinked ?? 0} already carried a link and keep it, and{' '}
            {result?.unresolvable ?? 0} name nothing in the catalogue.
          </p>
          <p className="mt-2 text-sm text-gray-700">
            This writes at most {sliceSize} link
            {sliceSize === 1 ? '' : 's'} and then stops with a cursor. Each write
            wakes watchlock&apos;s reference trigger, so let one slice settle
            before pressing Continue. Writing is fill-empty and idempotent — a
            second pass over the same references does nothing.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Reclassify co-developed calibres → persist their brand association.
 *
 * A calibre's generic/non-generic classification is derived LIVE from its
 * manufacturer set (primary + additionalManufacturerIds), so no migration is
 * needed for the filter/AKA picker to honour co-developers. This tool persists
 * the one thing that benefits from a write: the brand. For each brand-less
 * calibre whose set implies exactly one brand via a non-generic co-developer
 * (e.g. ETA-made "OP I" → Panerai via Officine Panerai), it sets that `brandId`
 * so the calibre surfaces in brand-scoped queries / Algolia / RN. Fill-empty
 * only; multi-brand sets are reported as ambiguous and skipped. Dry run first.
 */
function ReclassifyCoDevelopedCalibresSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [samplesExpanded, setSamplesExpanded] = useState(false);
  const [result, setResult] =
    useState<ReclassifyCoDevelopedCalibresResult | null>(null);

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await reclassifyCoDevelopedCalibres({ dryRun });
      setResult(r);
      setErrorsExpanded(false);
      setSamplesExpanded(dryRun);
      const summary =
        `${dryRun ? 'Dry run' : 'Reclassify'} complete: ` +
        `${r.scanned} scanned, ` +
        `${dryRun ? 'would set brand on' : 'set brand on'} ${r.updated}` +
        (r.ambiguousMultiBrand
          ? `, ${r.ambiguousMultiBrand} ambiguous`
          : '') +
        (r.errors.length ? `, ${r.errors.length} error(s)` : '');
      if (r.errors.length || r.ambiguousMultiBrand) {
        toast(summary, { icon: '⚠️' });
      } else {
        toast.success(summary);
      }
    } catch (err) {
      console.error('reclassifyCoDevelopedCalibres failed:', err);
      toast.error(
        err instanceof Error
          ? `Reclassify failed: ${err.message}`
          : 'Reclassify failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="reclassifyCoDevelopedCalibres"
      title="Reclassify co-developed calibres (brand association)"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Scans{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            custom_calibres
          </code>{' '}
          for brand-less calibres whose manufacturer set (primary +{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            additionalManufacturerIds
          </code>
          ) ties them to a brand through a{' '}
          <span className="font-semibold">non-generic co-developer</span> — e.g.
          an ETA-made calibre co-developed with Officine Panerai belongs to
          Panerai. When exactly one brand is implied it sets{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">brandId</code>{' '}
          (never overwrites an existing one); sets implying two+ brands are
          reported as ambiguous and left for manual review. The generic/
          non-generic classification itself is derived live and needs no
          backfill. Run a dry run first, then run for real.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Reclassify'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Would set brand' : 'Brand set'}
                value={result.updated}
              />
              <ResultStat
                label="Ambiguous (multi-brand)"
                value={result.ambiguousMultiBrand}
                danger={result.ambiguousMultiBrand > 0}
              />
              <ResultStat label="Already scoped" value={result.alreadyScoped} />
              <ResultStat label="No brand link" value={result.noBrandLink} />
            </div>

            {result.samples.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setSamplesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${samplesExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.samples.length} sample
                  {result.samples.length === 1 ? '' : 's'} — click to{' '}
                  {samplesExpanded ? 'collapse' : 'expand'}
                </button>

                {samplesExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Calibre
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              {result.dryRun ? 'Would set brandId' : 'brandId set'}
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Via manufacturer(s)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.samples.map(
                            (s: ReclassifyCoDevelopedCalibresSample, i) => (
                              <tr key={`${s.calibreId}/${i}`}>
                                <td className="px-3 py-1.5 text-gray-900 align-top">
                                  {s.calibreName}
                                  <span className="block font-mono text-[10px] text-gray-400">
                                    {s.calibreId}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                  <Badge variant="info">{s.brandId}</Badge>
                                </td>
                                <td className="px-3 py-1.5 font-mono text-gray-600 align-top">
                                  {s.viaManufacturerIds.join(', ')}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                    {result.updated > result.samples.length && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first {result.samples.length} of {result.updated}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {result.errors.length > 0 && (
              <div className="mt-3 border border-red-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setErrorsExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${errorsExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.errors.length} error
                  {result.errors.length === 1 ? '' : 's'} — click to{' '}
                  {errorsExpanded ? 'collapse' : 'expand'}
                </button>

                {errorsExpanded && (
                  <div className="border-t border-red-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Calibre ID
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Error
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.errors
                            .slice(0, 100)
                            .map(
                              (e: ReclassifyCoDevelopedCalibresError, i) => (
                                <tr key={`${e.calibreId}/${i}`}>
                                  <td className="px-3 py-1.5 font-mono text-gray-700 align-top">
                                    {e.calibreId}
                                  </td>
                                  <td className="px-3 py-1.5 text-red-600 align-top">
                                    {e.error}
                                  </td>
                                </tr>
                              ),
                            )}
                        </tbody>
                      </table>
                    </div>
                    {result.errors.length > 100 && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first 100 of {result.errors.length}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Reclassify co-developed calibres (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This sets{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              brandId
            </code>{' '}
            on brand-less calibres whose manufacturer set implies exactly one
            brand. It never overwrites an existing brand, but it does modify
            documents (and re-indexes them in Algolia). Run a dry run first and
            review the samples. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Recompute `brandIdsDerivedFromRefs` on every movement manufacturer from ref
 * usage. Phase 1b of the manufacturer-first-brand model: the derived set is the
 * raw brand signal a human later promotes to `primaryBrandId` / prunes via
 * `brandExclusions`. Fully-derived cache — replaces stored values, dry-run first.
 */
function DeriveManufacturerBrandsSection() {
  const [running, setRunning] = useState<'dry' | 'write' | null>(null);
  const [confirmWrite, setConfirmWrite] = useState(false);
  const [samplesExpanded, setSamplesExpanded] = useState(false);
  const [result, setResult] = useState<DeriveManufacturerBrandsResult | null>(
    null,
  );

  const run = async (dryRun: boolean) => {
    setRunning(dryRun ? 'dry' : 'write');
    try {
      const r = await deriveManufacturerBrands({ dryRun });
      setResult(r);
      setSamplesExpanded(dryRun);
      const summary =
        `${dryRun ? 'Dry run' : 'Derive'} complete: ` +
        `${r.scanned} refs scanned, ` +
        `${dryRun ? 'would update' : 'updated'} ${r.manufacturersUpdated} manufacturer(s)`;
      toast.success(summary);
    } catch (err) {
      console.error('deriveManufacturerBrands failed:', err);
      toast.error(
        err instanceof Error
          ? `Derive failed: ${err.message}`
          : 'Derive failed — see console',
      );
    } finally {
      setRunning(null);
    }
  };

  return (
    <CollapsibleSection
      sectionId="deriveManufacturerBrands"
      title="Derive manufacturer brands from ref usage"
    >
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <p className="text-sm text-gray-600">
          Scans every{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            reference
          </code>{' '}
          and recomputes{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            brandIdsDerivedFromRefs
          </code>{' '}
          on every movement manufacturer: each ref&apos;s{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">brandId</code>{' '}
          is added to the derived set of every manufacturer in the ref&apos;s
          resolved calibre&apos;s set (primary +{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            additionalManufacturerIds
          </code>
          ). Calibres resolve by{' '}
          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
            calibre
          </code>{' '}
          string (canonical name + AKAs). This is a fully-derived{' '}
          <span className="font-semibold">cache</span>: each run recomputes from
          scratch and replaces the stored value for any manufacturer whose set
          changed (including shrinking to empty). No consumer reads this field
          yet. Run a dry run first, then run for real.
        </p>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            loading={running === 'dry'}
            disabled={running !== null}
            onClick={() => run(true)}
          >
            Dry run
          </Button>
          <Button
            variant="primary"
            loading={running === 'write'}
            disabled={running !== null}
            onClick={() => setConfirmWrite(true)}
          >
            Run &amp; write
          </Button>
        </div>

        {result && (
          <div className="mt-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {result.dryRun ? 'Dry run' : 'Derive'} result
              </span>
              <Badge variant={result.dryRun ? 'info' : 'success'}>
                {result.dryRun ? 'no writes' : 'written'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              <ResultStat label="Refs scanned" value={result.scanned} />
              <ResultStat
                label={result.dryRun ? 'Would update' : 'Updated'}
                value={result.manufacturersUpdated}
              />
              <ResultStat
                label="Unchanged"
                value={result.manufacturersUnchanged}
              />
              <ResultStat
                label="Refs w/o brand"
                value={result.refsWithoutBrand}
              />
              <ResultStat
                label="Refs w/o calibre"
                value={result.refsWithoutCalibre}
              />
            </div>

            {result.samples.length > 0 && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setSamplesExpanded((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${samplesExpanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {result.samples.length} sample
                  {result.samples.length === 1 ? '' : 's'} — click to{' '}
                  {samplesExpanded ? 'collapse' : 'expand'}
                </button>

                {samplesExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left font-medium px-3 py-1.5">
                              Manufacturer
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              Before
                            </th>
                            <th className="text-left font-medium px-3 py-1.5">
                              {result.dryRun ? 'Would become' : 'After'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.samples.map(
                            (s: DeriveManufacturerBrandsSample, i) => (
                              <tr key={`${s.mfrId}/${i}`}>
                                <td className="px-3 py-1.5 text-gray-900 align-top">
                                  {s.mfrName}
                                  <span className="block font-mono text-[10px] text-gray-400">
                                    {s.mfrId}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                  {s.before.length === 0 ? (
                                    <span className="text-gray-400">—</span>
                                  ) : (
                                    <span className="flex flex-wrap gap-1">
                                      {s.before.map((b) => (
                                        <Badge key={b} variant="default">
                                          {b}
                                        </Badge>
                                      ))}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                  {s.after.length === 0 ? (
                                    <span className="text-gray-400">—</span>
                                  ) : (
                                    <span className="flex flex-wrap gap-1">
                                      {s.after.map((b) => (
                                        <Badge key={b} variant="info">
                                          {b}
                                        </Badge>
                                      ))}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                    {result.manufacturersUpdated > result.samples.length && (
                      <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
                        Showing first {result.samples.length} of{' '}
                        {result.manufacturersUpdated}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={confirmWrite}
        onClose={() => setConfirmWrite(false)}
        title="Derive manufacturer brands (write mode)?"
      >
        <div>
          <p className="text-sm text-gray-700">
            This recomputes{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              brandIdsDerivedFromRefs
            </code>{' '}
            on every movement manufacturer from ref usage and{' '}
            <span className="font-semibold">replaces</span> the stored value on
            any manufacturer whose set changed (including clearing it to empty).
            It is a derived cache, not hand-entered data. No consumer reads this
            field yet. Run a dry run first and review the samples. Continue?
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmWrite(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmWrite(false);
                void run(false);
              }}
            >
              Run &amp; write
            </Button>
          </div>
        </div>
      </Modal>
    </CollapsibleSection>
  );
}

/**
 * Configuration → Data Maintenance.
 *
 * Brand-audit + acquisitions importers on top; the one-off backfills and
 * migrations sit in the Maintenance & Tools footer.
 */
/** USD formatter — whole-dollar precision reads fine for spend/burn figures.
 * Shared with the sibling OpenAI panel, which formats the same kind of figure. */
export function formatUsd(value: number, currency: string): string {
  try {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    });
  } catch {
    // Guard against an unexpected non-ISO currency code from the API.
    return `${currency || 'USD'} ${value.toFixed(2)}`;
  }
}

/**
 * Observability panel: recent Anthropic API spend + derived daily burn, so we can
 * see the runway BEFORE bulk enrich fails on exhausted credit. Reads the
 * admin-gated {@link getAnthropicCreditBalance} callable (Cost Report API).
 *
 * The Admin API has no remaining-balance endpoint, so `balance`/`runwayDays` come
 * back null and `balanceAvailable` is false — we show a note + a link to Plans &
 * Billing for the raw balance figure. Every field is null-guarded before render.
 */
/** Progress-bar fill colour by percent of budget used: green < 75, amber 75–90,
 * red > 90 — matching the warning thresholds James asked for. Shared with the
 * OpenAI panel so both budget bars change colour at the same points. */
export function budgetBarColor(percent: number): string {
  if (percent > 90) return 'bg-red-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-green-500';
}

function AnthropicApiSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnthropicCreditBalanceResult | null>(null);
  const [budgetInput, setBudgetInput] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);
  const [creditState, setCreditState] = useState<AnthropicCreditState | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getAnthropicCreditBalance();
      setData(r);
      // Re-sync the input with the persisted target (also reflects a just-saved
      // value). Empty when no target is set.
      setBudgetInput(
        r.monthlyBudgetTargetUsd !== null ? String(r.monthlyBudgetTargetUsd) : '',
      );
    } catch (err) {
      console.error('getAnthropicCreditBalance failed:', err);
      setData(null);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch Anthropic usage — see console.',
      );
    } finally {
      setLoading(false);
    }
  };

  const saveBudget = async () => {
    const trimmed = budgetInput.trim();
    // Empty clears the target; otherwise require a positive finite number.
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      toast.error('Enter a positive dollar amount, or clear the field to unset.');
      return;
    }
    setSavingBudget(true);
    try {
      await setAnthropicMonthlyBudget(parsed);
      toast.success(
        parsed === null ? 'Budget target cleared.' : 'Budget target saved.',
      );
      await refresh();
    } catch (err) {
      console.error('setAnthropicMonthlyBudget failed:', err);
      toast.error('Failed to save budget — see console.');
    } finally {
      setSavingBudget(false);
    }
  };

  // Auto-load once on mount so the panel isn't blank on first open.
  useEffect(() => {
    void refresh();
  }, []);

  // Live-subscribe to the reactive credit-exhausted flag for the in-tab notice.
  useEffect(() => subscribeToAnthropicCreditState(setCreditState), []);

  const currency = data?.currency ?? 'USD';
  const usage = data?.usageLast30d ?? null;
  const burn = data?.dailyBurn ?? null;
  const balance = data?.balance ?? null;
  const runway = data?.runwayDays ?? null;
  const balanceAvailable = data?.balanceAvailable === true;
  const budgetTarget = data?.monthlyBudgetTargetUsd ?? null;
  const budgetRemaining = data?.budgetRemainingUsd ?? null;
  const budgetUsedPercent = data?.budgetUsedPercent ?? null;
  const exhausted = creditState?.exhausted === true;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Anthropic API
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Recent spend on the Anthropic API (powers AI enrich / bulk enrich).
            Watch the daily burn so credit doesn&apos;t run out mid-run.
          </p>
        </div>
        <Button
          variant="secondary"
          loading={loading}
          disabled={loading}
          onClick={() => void refresh()}
        >
          Refresh
        </Button>
      </div>

      {exhausted && (
        <div className="rounded-lg border-2 border-red-500 bg-red-50 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="danger">credit exhausted</Badge>
            <span className="text-sm font-semibold text-red-800">
              Anthropic API calls are failing — credit balance too low
            </span>
          </div>
          <p className="text-sm text-red-700">
            AI enrich / bulk enrich will fail until you top up.
            {creditState?.since &&
              ` Exhausted since ${creditState.since.toLocaleString()}.`}{' '}
            <a
              href="https://console.anthropic.com/settings/billing"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium"
            >
              Top up now
            </a>
            . This clears automatically on the next successful call.
          </p>
          {creditState?.lastError && (
            <p className="mt-2 text-xs text-red-600 break-words">
              Last error: {creditState.lastError}
            </p>
          )}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            Monthly budget target
          </h4>
          <p className="mt-0.5 text-xs text-gray-500">
            Set a monthly spend target to track how close 30-day spend is to your
            ceiling. The Admin API exposes no live balance, so this is the
            proactive early-warning signal. Leave empty to disable.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1" htmlFor="anthropic-budget">
              Target ($/month)
            </label>
            <div className="flex items-center">
              <span className="text-gray-500 mr-1">$</span>
              <input
                id="anthropic-budget"
                type="number"
                min="0"
                step="10"
                inputMode="decimal"
                className="w-32 border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="e.g. 200"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                disabled={savingBudget}
              />
            </div>
          </div>
          <Button
            variant="secondary"
            loading={savingBudget}
            disabled={savingBudget}
            onClick={() => void saveBudget()}
          >
            Save
          </Button>
        </div>

        {budgetTarget !== null && (
          <div className="space-y-1">
            <div className="text-sm text-gray-700">
              {usage !== null ? formatUsd(usage, currency) : '$0'} of{' '}
              {formatUsd(budgetTarget, currency)} used this month
              {budgetUsedPercent !== null && (
                <span
                  className={`ml-1 font-semibold ${
                    budgetUsedPercent > 90
                      ? 'text-red-600'
                      : budgetUsedPercent >= 75
                        ? 'text-amber-600'
                        : 'text-green-600'
                  }`}
                >
                  ({Math.round(budgetUsedPercent)}%)
                </span>
              )}
            </div>
            {budgetUsedPercent !== null && (
              <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className={`h-full ${budgetBarColor(budgetUsedPercent)}`}
                  style={{ width: `${Math.min(100, Math.max(0, budgetUsedPercent))}%` }}
                />
              </div>
            )}
            {budgetRemaining !== null && (
              <div className="text-xs text-gray-500">
                {formatUsd(budgetRemaining, currency)} remaining
                {budgetUsedPercent !== null && budgetUsedPercent > 90 && (
                  <span className="ml-1 text-red-600 font-medium">
                    — over 90% of budget, top up soon
                  </span>
                )}
                {budgetUsedPercent !== null &&
                  budgetUsedPercent >= 75 &&
                  budgetUsedPercent <= 90 && (
                    <span className="ml-1 text-amber-600 font-medium">
                      — over 75% of budget
                    </span>
                  )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="danger">error</Badge>
            <span className="text-sm font-semibold text-red-800">
              Couldn&apos;t load usage
            </span>
          </div>
          <p className="text-sm text-red-700">{error}</p>
          <p className="mt-2 text-xs text-red-600">
            If this mentions the key: regenerate an Admin API key
            (sk-ant-admin-…) in the Anthropic console and re-set the{' '}
            <code className="bg-red-100 px-1 py-0.5 rounded">
              ANTHROPIC_ADMIN_KEY
            </code>{' '}
            secret, then redeploy the function.
          </p>
        </div>
      )}

      {loading && !data && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white text-sm text-gray-500">
          Loading usage…
        </div>
      )}

      {data && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ResultStat
              label="Current balance"
              value={
                balanceAvailable && balance !== null
                  ? formatUsd(balance, currency)
                  : 'Not exposed'
              }
            />
            <ResultStat
              label="Spend (last 30d)"
              value={usage !== null ? formatUsd(usage, currency) : 'No data'}
            />
            <ResultStat
              label="Est. daily burn"
              value={burn !== null ? formatUsd(burn, currency) : 'No data'}
            />
          </div>

          {runway !== null && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ResultStat
                label="Est. runway"
                value={`${Math.floor(runway)} days`}
                danger={runway < 7}
              />
            </div>
          )}

          {!balanceAvailable && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                The Anthropic Admin API doesn&apos;t expose a remaining
                credit balance — only usage &amp; cost reports. For the raw
                balance figure, check{' '}
                <a
                  href="https://console.anthropic.com/settings/billing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Plans &amp; Billing
                </a>{' '}
                in the console. The 30-day spend and daily burn above are the
                actionable signal for when to top up.
              </p>
            </div>
          )}

          <div className="text-xs text-gray-400 space-y-0.5">
            {data.lastUpdated && (
              <div>
                Fetched {new Date(data.lastUpdated).toLocaleString()}
              </div>
            )}
            {data.periodStart && data.periodEnd && (
              <div>
                Window {new Date(data.periodStart).toLocaleDateString()} –{' '}
                {new Date(data.periodEnd).toLocaleDateString()} (excludes
                Priority Tier)
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Reactive credit-exhausted banner (Option 2). Rendered at the top of the admin
 * dashboard so a "credit balance too low" 400 from any Anthropic call site is
 * impossible to miss. Subscribes to the live `admin_config/anthropic_state` flag
 * and renders nothing until it's actually exhausted; auto-hides when the server
 * clears the flag on the next successful call.
 */
export function AnthropicCreditBanner() {
  const [state, setState] = useState<AnthropicCreditState | null>(null);

  // subscribeToAnthropicCreditState returns its unsubscribe fn — return it so the
  // effect cleans up on unmount.
  useEffect(() => subscribeToAnthropicCreditState(setState), []);

  if (!state || !state.exhausted) return null;

  return (
    <div
      role="alert"
      className="mb-6 rounded-lg border-2 border-red-500 bg-red-50 px-4 py-3 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="danger">Anthropic credit exhausted</Badge>
        <span className="text-sm font-semibold text-red-800">
          Top up now to resume AI enrichment
        </span>
      </div>
      <p className="mt-1 text-sm text-red-700">
        Anthropic API calls are failing with &ldquo;credit balance too low&rdquo;
        {state.since && ` — exhausted since ${state.since.toLocaleString()}`}.
        Top up at{' '}
        <a
          href="https://console.anthropic.com/settings/billing"
          target="_blank"
          rel="noopener noreferrer"
          className="underline font-medium"
        >
          console.anthropic.com
        </a>
        . This banner clears automatically once a call succeeds.
      </p>
    </div>
  );
}

/**
 * Configuration → Anthropic API tab: the {@link AnthropicApiSection} spend panel.
 */
export function AnthropicApiTab() {
  return (
    <div className="space-y-8">
      <AnthropicApiSection />
    </div>
  );
}

/**
 * `onOpenCalibre` deep-links a calibre id into the Curated Calibres editor. The
 * admin is tab-based rather than routed, so DashboardPage owns the jump (it does
 * the same for CalibreMovementTypeBackfill). Optional — sections fall back to
 * plain text when it isn't supplied.
 */
export function DataMaintenanceTab({
  onOpenCalibre,
  onOpenBrand,
  onOpenManufacturer,
}: {
  onOpenCalibre?: (calibreId: string) => void;
  onOpenBrand?: (brandId: string) => void;
  onOpenManufacturer?: (manufacturerId: string) => void;
} = {}) {
  return (
    <div className="space-y-8">
      <ImportBrandAuditSection />
      <ImportAcquisitionsSection />

      <ToolsSubsection>
        <BackfillReferenceNormalizedSection />
        <BackfillSubscriptionHistorySection />
        <PurgeExtractionCacheSection />
        <BackfillSwatchMissingReferenceSection />
        <BackfillDialColorFromModelSection />
        <PruneOrphanCollectionWatchIdsSection />
        <MoveRefsBetweenBrandsSection />
        <BackfillParentOrgIdsSection
          onOpenBrand={onOpenBrand}
          onOpenManufacturer={onOpenManufacturer}
        />
        <CleanupAnalogModuleLinksSection />
        <MigrateDialFinishSection />
        <BackfillDescriptionSourceSection />
        <RepairCalibreImageNamesSection />
        <StripReferenceNameMarkupSection />
        <MigrateCustomCalibreSourceSection />
        <ReclassifyCoDevelopedCalibresSection />
        <DeriveManufacturerBrandsSection />
        <BackfillTierTypesSection />
        <NormalizeCalibreSpellingSection onOpenCalibre={onOpenCalibre} />
        <DialMarkerNumeralsSplitSection />
        <CrownCrystalSplitSection />
        <CalibreBalanceTypeSection />
        <BackfillCalibreLinksSection />
        <BackfillShopifyParentRefsSection />
        <BackfillIsVariantSection />
        <ReseedAcquisitionLookupsSection />
        <SeedMovementDecorationLookupSection />
        <SeedWindingArchitectureLookupSection />
        <SeedCalibreMaterialsLookupsSection />
        <SeedDialFeaturesLookupSection />
        <SeedStorageWinderLookupsSection />
        <SeedElectronicModuleLookupsSection />
        <SeedPocketWatchLookupsSection />
      </ToolsSubsection>
    </div>
  );
}

/**
 * Schema-driven live-config panels (all RTDB-backed `configuration/*` docs).
 *
 * Each entry in CONFIG_SCHEMAS renders one panel. Adding a new live-config
 * doc going forward = one schema entry — no new components needed here.
 *
 * `filterGroups` lets the same component back two tabs: the RN App Switches tab
 * renders only the RN-facing feature-toggle group, while the RTDB Configs tab
 * renders every other group. Using a predicate (rather than an include-list)
 * guarantees a newly-added group is never silently dropped — it falls through
 * to whichever tab's predicate accepts it.
 */
function LiveSettingsSection({
  title,
  sectionId,
  filterGroups,
}: {
  title: string;
  sectionId: string;
  filterGroups: (group: string) => boolean;
}) {
  // Group panels by `group`, rendered in GROUP_ORDER (unknown groups fall to the
  // end). Each group is its own collapsible sub-section so the collapse state +
  // localStorage persistence is reused per group.
  const grouped = new Map<string, ConfigSchema[]>();
  for (const schema of CONFIG_SCHEMAS) {
    const g = schema.group ?? 'Other';
    const list = grouped.get(g) ?? [];
    list.push(schema);
    grouped.set(g, list);
  }
  const orderedGroups = [
    ...GROUP_ORDER.filter((g) => grouped.has(g)),
    ...[...grouped.keys()].filter((g) => !GROUP_ORDER.includes(g)),
  ].filter(filterGroups);

  return (
    <CollapsibleSection sectionId={sectionId} title={title}>
      {orderedGroups.map((group) => (
        <CollapsibleSection
          key={group}
          sectionId={`liveSettings:${group}`}
          title={group}
        >
          {grouped.get(group)!.map((schema) => (
            <SchemaConfigPanel key={`${schema.database}:${schema.path}`} schema={schema} />
          ))}
        </CollapsibleSection>
      ))}
    </CollapsibleSection>
  );
}

/**
 * Configuration → Catalog Indexes & Recompute → Reference Stopwords.
 *
 * The admin-editable list of merchandising words stripped out of
 * Shopify-derived reference numbers (`admin_config/import_stopwords`). Sits
 * beside Derivation Settings because that is exactly what it is — a knob on how
 * a reference string is derived — and, like that section, it writes a plain
 * Firestore config doc, so a change is live for the next import with no deploy.
 *
 * One word or phrase per line. A live preview runs the current textarea content
 * against a sample ref so the effect of an edit is visible before saving.
 */
function ReferenceStopwordsSection() {
  const { user } = useAuth();
  const [stopwords, setStopwords] = useState<string[]>(DEFAULT_IMPORT_STOPWORDS);
  const [draft, setDraft] = useState<string>(DEFAULT_IMPORT_STOPWORDS.join('\n'));
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [docExists, setDocExists] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [sample, setSample] = useState('W-BEL-PREORDER-GYS');

  useEffect(() => {
    const unsub = subscribeToImportStopwords((next) => {
      setStopwords(next);
      setLoaded(true);
      // Never stomp an in-progress edit with a snapshot echo of the save that
      // caused it (or of another admin's write) — the textarea only re-syncs
      // while it's clean.
      setDirty((isDirty) => {
        if (!isDirty) setDraft(next.join('\n'));
        return isDirty;
      });
    });
    importStopwordsDocExists().then(setDocExists).catch(() => setDocExists(null));
    return unsub;
  }, []);

  const parsedDraft = useMemo(
    () => normalizeStopwordList(draft.split('\n')),
    [draft],
  );

  const handleSave = async () => {
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    setSaving(true);
    try {
      const written = await saveImportStopwords(parsedDraft, user.uid);
      setDraft(written.join('\n'));
      setDirty(false);
      setDocExists(true);
      toast.success(
        `Saved ${written.length} stopword${written.length === 1 ? '' : 's'}`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save stopwords');
    } finally {
      setSaving(false);
    }
  };

  const preview = stripImportStopwords(sample, parsedDraft);

  return (
    <CollapsibleSection sectionId="referenceStopwords" title="Reference Stopwords">
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-gray-900">
              Shopify reference stopwords
            </h4>
            <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              admin_config/import_stopwords
            </code>
            {!loaded && <Badge variant="default">loading…</Badge>}
            {loaded && docExists === false && (
              <Badge variant="warning">using built-in defaults</Badge>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Shopify has no reference field, so the bulk importer derives one from{' '}
            <code className="font-mono">variant.sku</code> (falling back to the product
            handle). Words listed here are stripped out of that string{' '}
            <strong>before</strong> it is normalized, so both{' '}
            <code className="font-mono">reference</code> and{' '}
            <code className="font-mono">referenceNormalized</code> land clean.
          </p>
          <p className="text-sm text-gray-600 mt-1">
            One word or phrase per line. Matching is case-insensitive and gated on word
            boundaries — <code className="font-mono">sale</code> will not strip anything
            out of <code className="font-mono">PRESALE</code>. Separator variants each
            need their own line (<code className="font-mono">preorder</code>,{' '}
            <code className="font-mono">pre-order</code>,{' '}
            <code className="font-mono">pre order</code>). Applies to the Shopify import
            path only, and never to a ref that would be left with nothing.
          </p>
          {docExists === false && (
            <p className="text-sm text-amber-700 mt-2">
              The config document doesn&apos;t exist yet — the seed list below is what
              imports are using. Save to create it and make the list editable from here.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="reference-stopwords"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Stopwords ({parsedDraft.length})
          </label>
          <textarea
            id="reference-stopwords"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDirty(true);
            }}
            rows={12}
            spellCheck={false}
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            placeholder={'preorder\npre-order\ncoming soon'}
          />
        </div>

        <div>
          <label
            htmlFor="reference-stopwords-sample"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Try it
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              id="reference-stopwords-sample"
              type="text"
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <span className="text-gray-400">→</span>
            <code
              className={`font-mono text-sm px-2 py-1 rounded ${
                preview === sample
                  ? 'bg-gray-100 text-gray-600'
                  : 'bg-green-50 text-green-800'
              }`}
            >
              {preview || '—'}
            </code>
            {preview === sample && sample.trim() !== '' && (
              <span className="text-xs text-gray-500">(unchanged)</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={handleSave} loading={saving} disabled={!loaded || !dirty}>
            Save stopwords
          </Button>
          <Button
            variant="ghost"
            disabled={saving || !dirty}
            onClick={() => {
              setDraft(stopwords.join('\n'));
              setDirty(false);
            }}
          >
            Discard changes
          </Button>
          <Button
            variant="ghost"
            disabled={saving}
            onClick={() => {
              setDraft(DEFAULT_IMPORT_STOPWORDS.join('\n'));
              setDirty(true);
            }}
            title="Load the built-in seed list into the editor. Nothing is written until you save."
          >
            Load seed list
          </Button>
          {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
        </div>
      </div>
    </CollapsibleSection>
  );
}

function DerivationSettingsSection() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<DerivationSettings>(DEFAULT_DERIVATION_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const unsub = subscribeToDerivationSettings((next) => {
      setSettings(next);
      setLoaded(true);
    });
    return unsub;
  }, []);

  const handleToggle = async (next: boolean) => {
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    setPending(true);
    try {
      await setDerivationSetting('enforceInHouseBrandConstraint', next);
      toast.success(
        next ? 'Enforcing in-house brand constraint' : 'In-house brand constraint disabled',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update setting');
    } finally {
      setPending(false);
    }
  };

  const effective = settings.enforceInHouseBrandConstraint;

  return (
    <CollapsibleSection sectionId="derivationSettings" title="Derivation Settings">
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-sm font-semibold text-gray-900">
                  Enforce in-house brand constraint
                </h4>
                <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                  enforceInHouseBrandConstraint
                </code>
                {!loaded && (
                  <Badge variant="default">loading…</Badge>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                When enabled, the brand ↔ manufacturer derivation gate suppresses any link
                between a brand and a non-generic (in-house) manufacturer unless the brand is
                present in that manufacturer&apos;s <code className="font-mono">brandIdsManualInclude</code>.
                Generic manufacturers are always allowed. Affects the on-write trigger and
                the recount callable.
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1">
              <button
                type="button"
                role="switch"
                aria-checked={effective}
                disabled={pending || !loaded}
                onClick={() => handleToggle(!effective)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  effective ? 'bg-brand-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    effective ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-xs text-gray-500">
                {effective ? 'Enforced' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
    </CollapsibleSection>
  );
}

/** Soft size cap — warn above this but still allow the upload. */
const INTRO_CTA_SOFT_SIZE_LIMIT = 2 * 1024 * 1024; // 2 MB

/**
 * Sets the background image for the RN home "identify with AI" CTA. Writes the
 * public download URL to Firestore `configuration/aiImageRecognition`
 * (field `introCtaBackgroundUrl`); RN falls back to a bundled image when empty.
 */
function IdentifyCtaImageSection() {
  const { user } = useAuth();
  // The currently-persisted URL (from Firestore) and the draft shown in the
  // preview. They diverge after an upload/paste until the admin hits Save.
  const [savedUrl, setSavedUrl] = useState<string>(
    DEFAULT_AI_IMAGE_RECOGNITION_CONFIG.introCtaBackgroundUrl,
  );
  const [draftUrl, setDraftUrl] = useState<string>('');
  // Opacity (0–1). Like the URL it has a saved value plus a live draft that the
  // slider drives; we write on release/commit, not on every drag tick.
  const [savedOpacity, setSavedOpacity] = useState<number>(
    DEFAULT_AI_IMAGE_RECOGNITION_CONFIG.introCtaBackgroundOpacity,
  );
  const [draftOpacity, setDraftOpacity] = useState<number>(
    DEFAULT_AI_IMAGE_RECOGNITION_CONFIG.introCtaBackgroundOpacity,
  );
  // Overlay text colour mode (auto/light/dark). Auto-saves on selection, like
  // opacity — a single value with no separate draft (the control commits on click).
  const [textMode, setTextMode] = useState<IntroCtaTextMode>(
    DEFAULT_AI_IMAGE_RECOGNITION_CONFIG.introCtaTextMode,
  );
  // Shared AI search result count (configuration/ai/maxResults). Drives both the
  // image-rec and text-search backends. Saved value + a string draft the input
  // edits, so we can show an inline error for invalid entries before writing.
  const [savedMaxResults, setSavedMaxResults] = useState<number>(AI_MAX_RESULTS_DEFAULT);
  const [draftMaxResults, setDraftMaxResults] = useState<string>(String(AI_MAX_RESULTS_DEFAULT));
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOpacity, setSavingOpacity] = useState(false);
  const [savingTextMode, setSavingTextMode] = useState(false);
  const [savingMaxResults, setSavingMaxResults] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Track whether the admin has edited the draft so a live RTDB update
  // (e.g. another admin) doesn't clobber an in-progress edit.
  const dirty = useRef(false);
  const opacityDirty = useRef(false);
  // True while a text-mode write is in flight, so the live subscription doesn't
  // bounce the selection back to the old value before RTDB echoes the new one.
  const textModeSaving = useRef(false);
  // True while the admin is editing the count, so a live RTDB update doesn't
  // clobber the in-progress draft.
  const maxResultsDirty = useRef(false);

  useEffect(() => {
    const unsub = subscribeToAiImageRecognitionConfig((config) => {
      setSavedUrl(config.introCtaBackgroundUrl);
      if (!dirty.current) setDraftUrl(config.introCtaBackgroundUrl);
      setSavedOpacity(config.introCtaBackgroundOpacity);
      if (!opacityDirty.current) setDraftOpacity(config.introCtaBackgroundOpacity);
      if (!textModeSaving.current) setTextMode(config.introCtaTextMode);
      setLoaded(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = subscribeToAiSearchConfig((config) => {
      setSavedMaxResults(config.maxResults);
      if (!maxResultsDirty.current) setDraftMaxResults(String(config.maxResults));
    });
    return unsub;
  }, []);

  // Persist the opacity on slider release / blur (the draft drives live preview).
  const commitOpacity = async () => {
    if (!opacityDirty.current || draftOpacity === savedOpacity) return;
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    setSavingOpacity(true);
    try {
      await setIntroCtaBackgroundOpacity(draftOpacity, user.uid);
      opacityDirty.current = false;
      setSavedOpacity(draftOpacity);
      toast.success('Opacity saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save opacity');
    } finally {
      setSavingOpacity(false);
    }
  };

  // Persist the overlay text mode the moment a new option is picked (auto-save).
  const commitTextMode = async (mode: IntroCtaTextMode) => {
    if (mode === textMode) return;
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    const prev = textMode;
    setTextMode(mode); // optimistic
    textModeSaving.current = true;
    setSavingTextMode(true);
    try {
      await setIntroCtaTextMode(mode, user.uid);
      toast.success('Text color saved');
    } catch (err) {
      setTextMode(prev); // revert on failure
      toast.error(err instanceof Error ? err.message : 'Failed to save text color');
    } finally {
      textModeSaving.current = false;
      setSavingTextMode(false);
    }
  };

  // Validate the count draft: must be an integer within the allowed bounds.
  const parsedMaxResults = Number(draftMaxResults);
  const maxResultsValid =
    draftMaxResults.trim() !== '' &&
    Number.isInteger(parsedMaxResults) &&
    parsedMaxResults >= AI_MAX_RESULTS_MIN &&
    parsedMaxResults <= AI_MAX_RESULTS_MAX;

  // Persist the count on blur / Enter (auto-saves, like opacity). Only writes a
  // valid, changed value — invalid drafts surface an inline error instead.
  const commitMaxResults = async () => {
    if (!maxResultsDirty.current) return;
    if (!maxResultsValid) return;
    if (parsedMaxResults === savedMaxResults) {
      maxResultsDirty.current = false;
      return;
    }
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    setSavingMaxResults(true);
    try {
      await setAiMaxResults(parsedMaxResults, user.uid);
      maxResultsDirty.current = false;
      setSavedMaxResults(parsedMaxResults);
      toast.success('AI search result count saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save result count');
    } finally {
      setSavingMaxResults(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    if (file.size > INTRO_CTA_SOFT_SIZE_LIMIT) {
      toast(
        `Heads up: ${(file.size / (1024 * 1024)).toFixed(1)} MB is larger than the ~2 MB recommendation, but uploading anyway.`,
        { icon: '⚠️' },
      );
    }
    setUploading(true);
    try {
      const url = await uploadIntroCtaBackground(file, file.name);
      dirty.current = true;
      setDraftUrl(url);
      toast.success('Image uploaded — hit Save to publish');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    setSaving(true);
    try {
      await setIntroCtaBackgroundUrl(draftUrl, user.uid);
      dirty.current = false;
      setSavedUrl(draftUrl);
      toast.success('Saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!user?.uid) {
      toast.error('Not signed in');
      return;
    }
    setSaving(true);
    try {
      await setIntroCtaBackgroundUrl('', user.uid);
      dirty.current = false;
      setDraftUrl('');
      setSavedUrl('');
      toast.success('Cleared — RN will use the bundled default');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const busy = uploading || saving || !loaded;
  const hasUnsavedChange = draftUrl !== savedUrl;

  return (
    <CollapsibleSection sectionId="identifyCtaImage" title="Identify CTA background image">
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h4 className="text-sm font-semibold text-gray-900">
            Home “identify with AI” CTA background
          </h4>
          <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            configuration/aiImageRecognition.introCtaBackgroundUrl
          </code>
          {!loaded && <Badge variant="default">loading…</Badge>}
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Background shown behind the “try AI identification” card on the RN home
          screen. When empty, the app uses a bundled fallback image. PNG or JPG,
          ~1200×800 recommended, will render with an overlay for text readability.
        </p>

        {/* Preview of the draft (post-upload) / current value */}
        {draftUrl ? (
          <div className="mb-3">
            <img
              src={draftUrl}
              alt="Intro CTA background preview"
              className="h-40 w-auto max-w-full rounded-lg object-cover border border-gray-200 bg-gray-50"
            />
          </div>
        ) : (
          <div className="mb-3 h-40 w-full max-w-md rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-sm text-gray-400">
            No image set — using bundled fallback
          </div>
        )}

        {/* Upload / paste */}
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="px-3 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : draftUrl ? 'Change image' : 'Upload image'}
          </button>
          <span className="text-xs text-gray-400">or</span>
          <input
            type="text"
            value={draftUrl}
            onChange={(e) => {
              dirty.current = true;
              setDraftUrl(e.target.value);
            }}
            placeholder="Paste image URL…"
            disabled={busy}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        {/* Opacity — written on release/blur (auto-saves, no Save button) */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <label
              htmlFor="introCtaOpacity"
              className="text-sm font-semibold text-gray-900"
            >
              Image opacity
            </label>
            <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              configuration/aiImageRecognition.introCtaBackgroundOpacity
            </code>
            {savingOpacity && <Badge variant="default">saving…</Badge>}
          </div>
          <p className="text-sm text-gray-600 mb-2">
            0 = invisible, 1 = full image. Lower values are more washed out /
            easier to read text over.
          </p>
          <div className="flex items-center gap-3">
            <input
              id="introCtaOpacity"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={draftOpacity}
              disabled={busy || savingOpacity}
              onChange={(e) => {
                opacityDirty.current = true;
                setDraftOpacity(Number(e.target.value));
              }}
              onMouseUp={commitOpacity}
              onTouchEnd={commitOpacity}
              onKeyUp={commitOpacity}
              onBlur={commitOpacity}
              className="flex-1 accent-brand-600 disabled:opacity-50"
            />
            <span className="text-sm font-medium text-gray-900 tabular-nums w-14 text-right">
              {Math.round(draftOpacity * 100)}%
            </span>
            <span className="text-xs text-gray-400 tabular-nums w-12 text-right">
              {draftOpacity.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Text color — segmented control, auto-saves on selection */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-gray-900">Text color</span>
            <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              configuration/aiImageRecognition.introCtaTextMode
            </code>
            {savingTextMode && <Badge variant="default">saving…</Badge>}
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Auto follows the user’s theme. Light forces white-ish text (use when the
            background image is dark). Dark forces dark text (use when the background
            image is light).
          </p>
          <div
            role="radiogroup"
            aria-label="Intro CTA text color"
            className="inline-flex rounded-lg border border-gray-300 bg-gray-50 p-0.5"
          >
            {INTRO_CTA_TEXT_MODES.map((mode) => {
              const label =
                mode === 'auto' ? 'Auto (theme)' : mode === 'light' ? 'Light' : 'Dark';
              const selected = textMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={busy || savingTextMode}
                  onClick={() => commitTextMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    selected
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* AI search result count — shared knob, auto-saves on blur/Enter */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <label
              htmlFor="aiMaxResults"
              className="text-sm font-semibold text-gray-900"
            >
              AI search results count
            </label>
            <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              configuration/ai/maxResults
            </code>
            {savingMaxResults && <Badge variant="default">saving…</Badge>}
          </div>
          <p className="text-sm text-gray-600 mb-2">
            How many top matches each AI search returns (image rec + text). Default{' '}
            {AI_MAX_RESULTS_DEFAULT}. Must be a whole number between {AI_MAX_RESULTS_MIN} and{' '}
            {AI_MAX_RESULTS_MAX}.
          </p>
          <div className="flex items-center gap-3">
            <input
              id="aiMaxResults"
              type="number"
              min={AI_MAX_RESULTS_MIN}
              max={AI_MAX_RESULTS_MAX}
              step={1}
              value={draftMaxResults}
              disabled={busy || savingMaxResults}
              onChange={(e) => {
                maxResultsDirty.current = true;
                setDraftMaxResults(e.target.value);
              }}
              onBlur={commitMaxResults}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              aria-invalid={!maxResultsValid}
              className={`w-24 border rounded-lg px-3 py-2 text-sm focus:ring-2 ${
                maxResultsValid
                  ? 'border-gray-300 focus:ring-brand-500 focus:border-brand-500'
                  : 'border-red-400 focus:ring-red-500 focus:border-red-500'
              } disabled:opacity-50`}
            />
            {!maxResultsValid && (
              <span className="text-xs text-red-600">
                Enter a whole number from {AI_MAX_RESULTS_MIN} to {AI_MAX_RESULTS_MAX}.
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button onClick={handleSave} disabled={busy || !hasUnsavedChange}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="ghost"
            onClick={handleClear}
            disabled={busy || (!savedUrl && !draftUrl)}
          >
            Clear
          </Button>
          {hasUnsavedChange && (
            <span className="text-xs text-amber-600">Unsaved change</span>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
}
